import { create } from 'zustand'
import type { HydraulicSystem, Vertex, Edge, PipeEdge, FluidZone, PipeSpecification, CalculationSettings, PathResult, CriticalPathResult, DiagramElement, HeatTransferSettings, Layer, CustomFieldDefinition, Scenarios, PortSide, EdgeDiagramProperties, Rgba } from '@shared/hydraulic'
import { HydraulicSystemSchema } from '@shared/hydraulic'
import { ensureScenarios, DEFAULT_BASE_SCENARIO_ID, resolveDensityForEdge, WATER_DENSITY_KGM3, normalizeSystemFluidZonesToSI } from '@shared/hydraulic'
import { newDocumentHeader } from '@shared/hydraulic/document/document-header'
import type { DeviceLibrarySnapshot, UsableCalcStatus } from '@shared/hydraulic'
import type { Capability } from '@shared/collaboration/access'
import type { CalculationOutput, AnalysisResponse } from '../../../services/calc-service'
import { HEARTBEAT_MS, STREAM_THRESHOLD } from '@shared/calc-stream-constants'
import { convertValue, readSI } from '@shared/hydraulic/physics/unit-converter'
import { nextVertexId, nextEdgeId, nextScenarioId } from '../utils/id-generator'
import { initialDisplayMode } from './initial-display-mode'
import { envelopePayload } from '@/lib/api-envelope'
import { branchScenario, deleteScenarioWithBake, removeOverride } from '@shared/hydraulic'
import { pruneOverridesForRemovedIds } from '@shared/hydraulic'
import type { ProposedAction } from '../components/FlowAssistActionPreview'
// The router is never called directly from this store: every call goes through
// routeEdge, which decides ortho-vs-leave-alone in one place. Adding a direct
// computeOrthogonalRoute call here would reintroduce the missed-gate failure mode.
import { routeEdge } from '../utils/route-edge'
import { getRouteBasis } from '../utils/model-basis'
import { snapCenterToGrid, vertexToRouteBox } from '../utils/grid-snap'
import { splitChainAt } from '../utils/split-chain'
import { clampSplitT } from '../utils/clamp-split-t'
import { clampSplitGeometry } from '../utils/clamp-split-geometry'
import { roundSplitLengths } from '../utils/round-split-lengths'
import { spreadOrigin, scaleAbout, spreadRatio, isSpreadNoop, clampSpread } from '../utils/spread-model'
import { normalizeLegacyPipeFlags } from '../utils/normalize-pipe-flags'
import { elevationAt, interpolateElevation } from '../utils/vertex-elevation'
import { resolveNewPipePhase } from '../utils/pipe-inheritance'
import { createVertexAtPosition } from '../vertex-type-registry'
import { computeAutoSizeSuggestions } from '../utils/auto-size-suggestions'
import { computeElementHealthMap, computeHealthSummary } from '@shared/inference'
import type { AutoSizeSuggestion } from '../utils/auto-size-suggestions'
import type { AnyEdge } from '../types/permissive-element'
import { suggestPipeSize } from '../utils/pipe-sizing'
import { getConnectedEdges, getVertexElevation } from '../utils/vertex-helpers'
import { createJunction, defaultPipeFields } from '@shared/hydraulic'
import { toast } from '@/components/ui/sonner'
import { logger } from '@/utils/logger'
import { remapElements } from '../utils/remap-elements'
import { clearCalcResults, clearCalcResultsIfSolverInputsChanged, hasPersistedResults, markScenariosStale, projectActiveScenario, staleAfterEdit } from './scenario-results'
import type { ScenarioResultsMap, ScenarioStatusMap } from './scenario-results'
import { routeFieldEdit } from './route-field-edit'
import { deriveResolved } from './resolved-system'
import { collectUsedSides } from '../utils/edge-sides'
import { deriveLockableWaypoints } from '../utils/lock-route'
import { readAiDesignReviewChoice, writeAiDesignReviewChoice, type AiDesignReviewChoice } from './ai-design-review-pref'
import { readDockOpen, readDockHeight, writeDockOpen, writeDockHeight, clampDockHeight } from '../components/scenarios/scenario-dock-prefs'

// ============================================================
// Types
// ============================================================

type HistoryEntry =
  | { kind: 'graph'; vertices: Vertex[]; edges: Edge[]; scenarios: Scenarios | undefined; diagram_elements: DiagramElement[] }
  | { kind: 'full'; vertices: Vertex[]; edges: Edge[]; fluid_zones: FluidZone[]; pipe_specifications: PipeSpecification[]; heat_transfer_settings: HeatTransferSettings[] | undefined; layers: Layer[] | undefined; custom_field_definitions: CustomFieldDefinition[] | undefined; scenarios: Scenarios | undefined; diagram_elements: DiagramElement[] }

export type ResultsDisplayMode = 'none' | 'velocity' | 'flow' | 'pressure_drop' | 'critical_path' | 'health' | 'elevation' | 'fluid_zone' | 'temperature'

/**
 * Static display modes show geometry/topology data and remain valid even
 * when calculation results go stale (they need no calc output).
 */
const STATIC_DISPLAY_MODES: ReadonlySet<ResultsDisplayMode> = new Set(['elevation', 'fluid_zone'])

/**
 * Returns the mode to use after an action that may have made results stale.
 * Static modes (elevation, fluid_zone) survive staleness; all others reset to 'none'.
 */
const nextDisplayMode = (mode: ResultsDisplayMode, stale: boolean): ResultsDisplayMode =>
  stale && !STATIC_DISPLAY_MODES.has(mode) ? 'none' : mode

export interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  timestamp: number
  elementRefs?: string[]
}

/**
 * Transient UI state for a streamed (heavy, >STREAM_THRESHOLD) calc — spec
 * §3.4. Deliberately NOT `calcStatus` (that's the persisted dashboard badge
 * derived by `deriveCalcStatus`); `streamProgress` is UI-only, cleared on
 * settle, never saved.
 */
export interface StreamProgressState {
  elapsedMs: number
  /** True when no frame (heartbeat or otherwise) has arrived for more than
   *  ~2×HEARTBEAT_MS — a "still working / connection quiet" hint, not a
   *  failure. */
  quiet: boolean
}

/** A calc result superseded by a live edit landing mid-solve (apply-guard,
 *  spec §3.4) — kept for history/debugging, never applied to the live model. */
export interface SupersededCalcResult {
  supersededAt: number
  output: CalculationOutput | null
  response: AnalysisResponse
}

interface ModelState {
  // Model data
  system: HydraulicSystem | null
  selectedIds: string[]
  interactionMode: 'select' | 'add-device' | 'add-pipe' | 'delete' | 'path-trace' | 'add-schedule' | 'add-library'
  viewMode: '2d' | '3d' | 'table' | 'compare'
  /** Latest 2D-canvas schematic as a PNG data-URL, captured (debounced) while in 2D view.
   *  Embedded in the scenario-comparison PDF. null until the 2D view has been opened. */
  schematicSnapshot: string | null
  addVertexType: string | null
  addPipeSource: string | null  // first click in add-pipe mode
  draftWaypoints: { x: number; y: number }[]  // interior points committed while drawing an iso pipe
  promptLengthEdgeId: string | null  // a just-drawn pipe whose length field should be focused

  // Device Library insertion
  pendingLibrarySnapshot: DeviceLibrarySnapshot | null

  // Clipboard / paste
  clipboard: { vertices: Vertex[], edges: Edge[], anchorOffset: { x: number, y: number } } | null
  pasteCounter: number

  // Undo/redo
  undoStack: HistoryEntry[]
  redoStack: HistoryEntry[]
  canUndo: boolean
  canRedo: boolean

  // Persistence
  isDirty: boolean
  lastSavedAt: number | null
  projectId: string | null
  modelId: string | null
  modelName: string | null
  capabilities: Capability[] | null
  importJobId: string | null
  sourceType: string | null
  serverVersion: number | null
  /** Model-history append-sequence counter (live-read, D4). Convergence target for
   *  useModelStream's dual-counter reconcile — null until a model is loaded. */
  headSeq: number | null
  getAuthToken: (() => Promise<string | null>) | null

  // Collaboration (Phase 1: per-model edit lock + presence)
  sessionId: string | null
  canEdit: boolean
  _demotionNotified: boolean

  // Calculation
  calculationResults: CalculationOutput | null
  isCalculating: boolean
  isAutoCalcEnabled: boolean
  resultsStale: boolean
  resultsDisplayMode: ResultsDisplayMode
  /** Transient "Calculating…" progress for a streamed (>STREAM_THRESHOLD)
   *  solve — see StreamProgressState. null when not mid-stream-solve. */
  streamProgress: StreamProgressState | null
  /** Results superseded by a live edit landing mid-solve (apply-guard, spec
   *  §3.4) — kept for history/debugging; never applied to the live model. */
  staleCalcHistory: SupersededCalcResult[]

  // Per-scenario calculation state
  scenarioResults: ScenarioResultsMap
  scenarioStatus: ScenarioStatusMap
  isCalculatingAll: boolean
  scenarioCalcProgress: import('./scenario-results').ScenarioCalcProgress | null

  // Analysis
  analysisResults: AnalysisResponse | null
  isAnalyzing: boolean
  // Async AI design-review job (kicked off by /analyze, polled separately)
  designReviewJobId: string | null
  isDesignReviewPending: boolean
  // AI disclosure / opt-out (per-browser; null = "ask each time")
  aiDesignReviewChoice: AiDesignReviewChoice | null
  isAiDisclosurePending: boolean

  // Health indicators (computed from analysisResults)
  elementHealthMap: Record<string, import('@shared/inference').ElementHealth>
  healthSummary: import('@shared/inference').HealthSummary | null
  hoveredId: string | null

  // Path trace
  pathTraceStartVertex: string | null
  pathTraceSource: 'manual' | 'critical_path' | null
  pathTraceResult: PathResult | null

  // Critical path
  criticalPath: CriticalPathResult | null

  // Pipe sizing
  showSizingDialog: boolean

  // Auto-sizing
  autoSizeSuggestions: AutoSizeSuggestion[]
  dismissedSuggestionIds: string[]
  isAutoSizing: boolean

  // FlowAssist
  flowAssist: {
    messages: ChatMessage[]
    isStreaming: boolean
    sessionTokens: { input: number; output: number }
    sessionId: string | null
    pendingMessage?: string
  }

  // FlowAssist Palette (Phase 2)
  showFlowAssistPalette: boolean

  // Find Device dialog
  showFindDevice: boolean

  // FlowAssist Smart Suggest (Phase 3) -- tracks accepted suggestions for correction loop
  pendingSuggestions: Record<string, { suggestedValue: unknown; timestamp: number }>

  // Convergence Doctor
  convergenceDoctorNeeded: boolean

  // Settings dialog
  settingsOpen: boolean
  settingsTab: string

  // Save-to-Library dialog (store-backed so both the toolbar and the canvas
  // right-click menu can open it)
  saveToLibraryOpen: boolean
  /** Vertex whose symbol is being chosen, or null when the picker is closed. */
  symbolPickerVertexId: string | null

  // Scenario grid dock (bottom panel under the 2D canvas). Ephemeral UI state —
  // never written into `system` (no server sync), persisted to localStorage,
  // hydrated at store init, NOT reset on model load.
  scenarioDockOpen: boolean
  scenarioDockHeight: number

  // Device Library refresh signal (transient — not persisted, not in undo history)
  libraryRefreshToken: number
}

/**
 * What an explicit Calculate reports to its caller for analytics. Size and clock are
 * captured at START — never read back from the store after the await, because the
 * Model may have changed underneath (see the apply-guard in `calculate()`).
 *
 * Declared and exported here, not in the analytics façade (`@/lib/pendo-events`):
 * this store stays analytics-free, and the `modelLoaded: true` shape below matches
 * `PendoEvents['Model Calculated']` structurally, which is all `trackEvent` needs.
 *
 * A discriminated union, not four optional fields: `calculate()`'s no-Model guard
 * clause has nothing real to report (no solve ran) and used to fabricate
 * `{outcome:'failed', durationMs:0, pipeCount:0, vertexCount:0}` — a shape a
 * genuinely failed calculation on a freshly-loaded, empty Model can also produce,
 * so callers could not tell "nothing happened" from "it happened and failed" without
 * re-deriving that distinction from field values (fragile — see EditorToolbar.tsx's
 * `isAutoSizeNoOp`, which the same class of bug already burned once). `modelLoaded`
 * makes the two cases impossible to confuse: TypeScript will not let a caller read
 * `.outcome` without first checking it, and `{modelLoaded: false}` carries no
 * `outcome` for a caller to mis-read as a real one.
 */
type CalculatedOutcome = 'success' | 'partial' | 'invalid' | 'failed'

export type CalcEventResult =
  | { modelLoaded: false }
  | { modelLoaded: true; outcome: CalculatedOutcome; durationMs: number; pipeCount: number; vertexCount: number }

interface ModelActions {
  // Lifecycle
  loadFromSystem: (system: HydraulicSystem) => void
  openModel: (projectId: string, modelId: string) => Promise<boolean>
  setAuthProvider: (fn: () => Promise<string | null>) => void
  clearModel: () => void

  // Collaboration (Phase 1: per-model edit lock + presence)
  setSessionId: (id: string | null) => void
  setCanEdit: (v: boolean) => void
  saveNow: () => Promise<void>
  /** Surgical GET refresh used on promotion to editor; bypasses openModel's idempotency guard. */
  resyncModel: () => Promise<void>
  /** Fire the "taken over" toast at most once per editor session. */
  notifyDemotedOnce: () => void

  // Calculation
  calculate: (token?: string) => Promise<CalcEventResult>
  /** Cancels the in-flight streamed calc's AbortSignal, if any (spec §3.4:
   *  "abort on navigate-away"). No-op when nothing is in flight. */
  abortCalculation: () => void
  calculateAllScenarios: (token?: string) => Promise<void>
  /** Returns the same `CalcEventResult` shape `calculate()` does — see that type.
   *  The outcome reported is what THIS invocation computed, whether or not the
   *  apply-guard committed it (PIPE-125). */
  fullAnalyze: (token?: string) => Promise<CalcEventResult>
  /** Merge an async design-review result into analysisResults + recompute health. */
  mergeDesignReview: (jobId: string, result: import('@shared/inference').AnalyzerResult) => void
  /** Set or reset (null) the AI design-review preference; persists to localStorage. */
  setAiDesignReviewChoice: (choice: AiDesignReviewChoice | null) => void
  /** Resolve the first-run disclosure dialog. 'always' persists + re-runs Analyze. */
  resolveAiDisclosure: (decision: 'always' | 'not-now' | 'never') => Promise<void>
  toggleAutoCalc: () => void
  /**
   * Wire up auto-recalculation: while `isAutoCalcEnabled` is on, re-solve
   * (debounced) whenever an edit invalidates existing results. Returns an
   * unsubscribe fn; call once from the editor and clean up on unmount.
   */
  startAutoCalc: () => () => void
  setResultsDisplayMode: (mode: ResultsDisplayMode) => void

  // Selection
  setSelectedIds: (ids: string[]) => void
  toggleSelectedId: (id: string) => void
  clearSelection: () => void
  setInteractionMode: (mode: ModelState['interactionMode']) => void
  setAddVertexType: (type: string | null) => void
  setAddPipeSource: (deviceId: string | null) => void
  setViewMode: (mode: ModelState['viewMode']) => void
  setSchematicSnapshot: (snapshot: string | null) => void

  // Device Library
  setPendingLibrarySnapshot: (snapshot: DeviceLibrarySnapshot | null) => void

  // Path trace
  setPathTraceStartVertex: (id: string | null) => void
  clearPathTrace: () => void

  // Vertex mutations
  addVertex: (vertex: Vertex) => void
  updateVertex: (id: string, updates: Partial<Vertex>) => void
  updateVertexProperty: (id: string, path: string, value: unknown, scenarioId?: string) => void
  removeVertex: (id: string) => void

  changeVertexType: (id: string, newType: string) => void

  // Edge mutations
  addEdge: (edge: Edge) => void
  addDrawnPipe: (sourceId: string, targetId: string, interior: { x: number; y: number }[]) => void
  addPipeToNewJunction: (
    sourceId: string,
    position: { x: number; y: number },
    interior?: { x: number; y: number }[],
  ) => void
  startPipeInEmptySpace: (position: { x: number; y: number }) => void
  teeOntoEdge: (
    sourceId: string,
    targetEdgeId: string,
    hit: SplitAt,
    interior?: { x: number; y: number }[],
  ) => void
  combineJunctions: (targetId: string, draggedId: string) => void
  pushDraftWaypoint: (p: { x: number; y: number }) => void
  popDraftWaypoint: () => void
  clearDraftWaypoints: () => void
  clearLengthPrompt: () => void
  updateEdge: (id: string, updates: Partial<Edge>) => void
  /** Set (Rgba) or clear (null) a pipe's presentation color override. */
  setEdgeColor: (id: string, color: Rgba | null) => void
  /** Set (Rgba) or clear (null) a device's presentation color override. */
  setVertexColor: (id: string, color: Rgba | null) => void
  updateEdgeProperty: (id: string, path: string, value: unknown) => void
  removeEdge: (id: string) => void

  // Reference data mutations
  addFluidZone: (zone: FluidZone) => void
  updateFluidZone: (id: string, updates: Partial<FluidZone>) => void
  removeFluidZone: (id: string) => void
  addPipeSpecification: (spec: PipeSpecification) => void
  updatePipeSpecification: (id: string, updates: Partial<PipeSpecification>) => void
  removePipeSpecification: (id: string) => void
  updateCalculationSettings: (partial: Partial<CalculationSettings>) => void
  updateDesignConstraints: (constraints: Record<string, unknown>) => void

  // Bulk operations
  removeSelectedItems: () => void

  // Diagram element mutations
  addDiagramElement: (element: DiagramElement) => void
  /**
   * Patch a diagram element. The patch is shallow-merged onto the element.
   * Common geometry patches (`{ position }`, `{ size }`, or both) are safe for
   * all element kinds. Patches on discriminated-union fields (e.g. `mode`,
   * `filters`) should match the current `kind` of the target element.
   */
  updateDiagramElement: (id: string, patch: Partial<DiagramElement>) => void
  removeDiagramElement: (id: string) => void

  // Bulk property editing
  bulkUpdateVertexProperty: (ids: string[], key: string, value: unknown) => void
  bulkUpdateEdgeProperty: (ids: string[], key: string, value: unknown) => void

  // Edge direction
  reverseEdge: (id: string) => void

  /**
   * Move one fitting from `fromEdgeId` to `toEdgeId` (PIPE-10). A split hands ALL fittings
   * to the upstream half; this is how the user corrects that afterwards. Both edges must be
   * pipes and the index must exist, else it is a no-op.
   */
  moveFittingToEdge: (fromEdgeId: string, index: number, toEdgeId: string) => void

  // Clipboard
  copySelection: () => void
  pasteClipboard: (cursorPos?: { x: number; y: number }) => void
  cutSelection: () => void

  // Device Library
  insertLibrarySnapshot: (snapshot: DeviceLibrarySnapshot, clickPos: { x: number; y: number }) => InsertLibraryResult

  // Split edge
  splitEdge: (edgeId: string, vertexType: string, opts?: SplitAt) => void
  /** T11/B29: delete a device but heal the network — merge compatible pipes,
   *  splice a junction for spec-change / n-way nodes, plain-delete boundaries /
   *  junctions / degree≤1. Never cascades a mid-line device's pipes. */
  dissolveDevice: (id: string) => void

  // Orthogonal routing
  updateVertexWithRouting: (vertexId: string, position: { x: number; y: number }) => void
  /** Group move: translate multiple vertices (and optionally diagram_elements) in a single history entry.
   *  Derives delta and bothMovedEdgeIds internally. Does NOT mark resultsStale (position is not a solver input). */
  moveVertices: (
    moves: Array<{ id: string; position: { x: number; y: number } }>,
    elementMoves: Array<{ id: string; position: { x: number; y: number } }>,
  ) => void
  /** Snap the given vertices (empty = all) to the grid and re-route their edges in a
   *  SINGLE history entry / store update. Never loop updateVertexWithRouting for this —
   *  that clones the whole graph per vertex (~123 s and thousands of undo entries on a
   *  5k-vertex model). Does NOT mark resultsStale (position is not a solver input). */
  relayoutToGrid: (ids: string[]) => void
  updateEdgeDiagram: (edgeId: string, diagram: Partial<{ points: { x: number; y: number }[]; sourceHandle: string; targetHandle: string }>) => void
  setEdgeManuallyRouted: (edgeId: string, manually_routed: boolean) => void
  setEdgePin: (edgeId: string, end: 'source' | 'target', side: PortSide | null) => void
  setEdgePins: (targets: Array<{ edgeId: string; end: 'source' | 'target'; side: PortSide | null }>) => void
  setEdgeWaypoints: (edgeId: string, waypoints: Array<{ x: number; y: number }> | null) => void
  lockAllPipeRoutes: () => void

  /**
   * Nudge one element's results readout clear of a crowded area, or `null` to put it back.
   *
   * Takes an element id and resolves it against vertices AND edges — in tight models both
   * kinds of readout collide, and the caller (a drag handler on a shared component) has no
   * reason to care which it is holding.
   *
   * Does NOT mark results stale: where a label sits is presentation, not a solver input,
   * same as `moveVertices`. Marking it stale would make tidying a drawing discard the
   * results being tidied.
   */
  setResultsOffset: (id: string, offset: { dx: number; dy: number } | null) => void
  /**
   * Choose which PIPE-FLO symbol a device draws with, or null to go back to the
   * default for its type. The value is a flo.shape shape NAME.
   */
  setVertexSymbol: (id: string, symbol: string | null) => void
  /** Put every moved readout back to its default anchor, in one undoable step. */
  clearAllResultsOffsets: () => void

  /**
   * Spread the drawing out (or back in) so crowded symbols and labels get room.
   *
   * `target` is ABSOLUTE — the spread relative to the layout as imported, recorded on
   * `system.diagram_spacing`. Device positions are genuinely rewritten, so every hit-test,
   * snap and router keeps working untouched; there is no view transform to invert.
   *
   * ⚠️ Scales diagram positions ONLY. `length` is a data field unrelated to where a symbol
   * sits, so the hydraulics are identical afterwards — only the picture changes.
   */
  spreadModel: (target: number) => void

  // Undo/redo
  undo: () => void
  redo: () => void

  // Pipe sizing
  toggleSizingDialog: () => void

  // Auto-sizing
  autoSizeAllPipes: (token?: string) => Promise<{
    pipesSized: number
    iterations: number
    compromises: number
    unresolvable: number
    status: 'converged' | 'max_iterations' | 'oscillation' | 'calc_failed'
  }>
  acceptAutoSizeSuggestion: (edgeId: string) => void
  dismissAutoSizeSuggestion: (edgeId: string) => void

  // FlowAssist
  addUserMessage: (content: string) => void
  appendAssistantChunk: (content: string) => void
  finalizeAssistantMessage: (elementRefs?: string[]) => void
  setFlowAssistStreaming: (isStreaming: boolean) => void
  clearFlowAssistChat: () => void
  setFlowAssistSessionId: (sessionId: string | null) => void
  addFlowAssistTokens: (input: number, output: number) => void

  // FlowAssist Palette (Phase 2)
  toggleFlowAssistPalette: () => void
  setShowFindDevice: (open: boolean) => void
  executeProposedAction: (action: ProposedAction) => void

  // FlowAssist Smart Suggest (Phase 3)
  trackAcceptedSuggestion: (deviceId: string, fieldKey: string, suggestedValue: unknown) => void
  clearPendingSuggestions: () => void

  // New system
  createBlankSystem: (
    title: string,
    fluid: { name: string; temperature: number; density: number; viscosity: number; vaporPressure: number },
    pipeSpec: { material: string; schedule: string; roughness: number; nominalSize: string; innerDiameter: number },
    basis?: 'orthogonal' | 'isometric',
  ) => void

  // Settings dialog
  openSettings: (tab?: string) => void
  closeSettings: () => void

  // Save-to-Library dialog. Passing a vertexId selects just that device first
  // (unless it's already part of the current selection), so the right-click
  // menu can "Save this device" without disturbing an existing multi-selection.
  openSaveToLibrary: (vertexId?: string) => void
  openSymbolPicker: (vertexId: string) => void
  closeSymbolPicker: () => void
  closeSaveToLibrary: () => void

  // Element focus
  selectAndFocusElement: (elementId: string, opts?: { zoom?: boolean }) => void
  selectAndFocusGroup: (groupId: string) => void
  setHoveredId: (id: string | null) => void

  // Device Library refresh signal
  bumpLibraryRefresh: () => void

  // Scenario management
  setActiveScenario: (scenarioId: string) => void
  branchScenarioAction: (parentId: string, name: string) => string
  renameScenarioAction: (scenarioId: string, name: string) => void
  resetOverrideAction: (scenarioId: string, target: 'vertex' | 'edge', id: string, field: import('@shared/hydraulic').OverrideField) => void
  deleteScenarioAction: (scenarioId: string) => void
  clearScenarioOverrides: (scenarioId: string) => void
  toggleScenarioDock: () => void
  setScenarioDockOpen: (open: boolean) => void
  setScenarioDockHeight: (px: number) => void
}

export type ModelStore = ModelState & ModelActions

// ============================================================
// Server persistence (replaces IndexedDB)
// ============================================================

const DEBOUNCE_MS = 1000

let saveTimeout: ReturnType<typeof setTimeout> | null = null

// Auto-calc debounce: coalesce rapid edits into a single re-solve once the
// user pauses. Module-level (mirrors saveTimeout) so successive edits reset
// the same timer. See startAutoCalc.
const AUTO_CALC_DEBOUNCE_MS = 600

let autoCalcTimeout: ReturnType<typeof setTimeout> | null = null

/**
 * Debounced auto-recalc of the ACTIVE scenario, shared by the edit-driven
 * subscriber (startAutoCalc) and the scenario-switch path (setActiveScenario)
 * so the two cannot drift. Single shared timer = latest action wins (see spec
 * "Concurrency"). Re-checks at fire time so a switch-away cancels a stale solve.
 */
function scheduleAutoCalc(): void {
  if (autoCalcTimeout) clearTimeout(autoCalcTimeout)
  autoCalcTimeout = setTimeout((): void => {
    autoCalcTimeout = null
    const s = useModelStore.getState()
    // Re-check at fire time — state may have changed during the debounce.
    if (!s.isAutoCalcEnabled || s.isCalculating || s.scenarioStatus[s.system?.scenarios?.active_id ?? 'base'] !== 'stale') return
    void (async (): Promise<void> => {
      const token = s.getAuthToken ? await s.getAuthToken() : null
      await s.calculate(token ?? undefined)
    })()
  }, AUTO_CALC_DEBOUNCE_MS)
}

let saveInFlight = false
// Set to true when a debounced save fires while another save is already in
// flight. The in-flight save's finally block checks this flag and drains
// one more save against the latest store state. Without this, mutations
// landing during a slow save cycle were silently dropped (Dominik QA
// Round 2, 2026-04-14, likely contributor to "AI changes don't persist").
let pendingSaveRequested = false

// ============================================================
// Streamed-calc "Calculating…" UI (Task 4, spec §3.4 / §1.2)
// ============================================================

/**
 * Records Task 0's customer-network go/no-go verdict (spec §1.2). Task 0
 * shipped SHIP-BEHIND-FALLBACK with Step 3 — the real over-the-customer's-
 * path check — deferred (see `.superpowers/sdd/task-0-report.md`); no
 * GO-DEGRADED verdict has been recorded, so this stays `false` and the live
 * elapsed-timer ticker (below) runs. Flip to `true` only if/when that check
 * records `buffering=true` (frames arrive in one buffered burst at close
 * rather than incrementally) — a live ticker would mislead in that mode, so
 * the UI instead renders a static "Calculating…" with no timer/watchdog.
 */
export const CALC_STREAM_BUFFERING_DEGRADED = false

/** Bound on `staleCalcHistory` (superseded-result apply-guard stash, spec
 *  §3.4) — each entry carries a full `AnalysisResponse` (~5MB for exactly
 *  the heavy solves this feature targets), so keep only the most recent
 *  few rather than growing unbounded across a session with repeated
 *  edits-during-heavy-solves. */
export const MAX_STALE_CALC_HISTORY = 3

/** In-flight streamed calc's AbortController, so `abortCalculation()` (called
 *  on navigate-away — loadFromSystem/clearModel/openModel — and available for
 *  a future unmount hook) can cancel it. Module-level, mirroring `saveTimeout`
 *  /`autoCalcTimeout` above: only one calc is ever in flight at a time. */
let calcAbortController: AbortController | null = null

export interface StreamTickerHandle {
  /** Call whenever calc-service's `onProgress` fires (started/heartbeat) —
   *  resets the watchdog clock. No-op in degraded mode. */
  onFrame: () => void
  /** Stops the interval(s). Always call in a `finally` once the calc settles. */
  stop: () => void
}

/**
 * Drives the streamed-calc "Calculating…" UI (spec §3.4 / task-4-brief Step
 * 3 + 3a):
 *  - `degraded: false` (the default/live mode) — a local `setInterval`
 *    advances `elapsedMs` from a start timestamp every `tickMs`, run
 *    entirely independent of frame arrival: a frame-driven counter freezes
 *    on a stalled/buffered connection, the exact "looks hung" this design
 *    exists to prevent. A separate watchdog compares time-since-last-frame
 *    (updated only by `onFrame()`) against `2 * heartbeatMs`; past that with
 *    no frame, `quiet` flips true (still solving, connection just quiet).
 *  - `degraded: true` (Task 0 recorded buffering) — no timer/watchdog is
 *    ever started; `onUpdate` fires exactly once with a fixed `{elapsedMs:
 *    0, quiet: false}` so the caller can render a static "Calculating…"
 *    string. Implemented (not dead code) so the degraded path is exercised
 *    even while Task 0's real verdict stays GO.
 */
export function createStreamTicker(opts: {
  degraded: boolean
  heartbeatMs: number
  tickMs?: number
  onUpdate: (progress: StreamProgressState) => void
}): StreamTickerHandle {
  if (opts.degraded) {
    opts.onUpdate({ elapsedMs: 0, quiet: false })
    return { onFrame: (): void => {}, stop: (): void => {} }
  }

  const startedAt = Date.now()
  let lastFrameAt = startedAt
  const tickMs = opts.tickMs ?? 1000
  const watchdogMs = opts.heartbeatMs * 2

  const tick = (): void => {
    const now = Date.now()
    opts.onUpdate({ elapsedMs: now - startedAt, quiet: now - lastFrameAt > watchdogMs })
  }

  tick() // show "0s" immediately rather than waiting out the first tickMs
  const timer = setInterval(tick, tickMs)

  return {
    onFrame: (): void => { lastFrameAt = Date.now() },
    stop: (): void => clearInterval(timer),
  }
}

/**
 * Debounced auto-save to server via PUT /api/projects/:projectId/models/:modelId
 * Called at 20+ mutation sites — name stays the same, implementation changes.
 *
 * The function reads projectId, modelId, serverVersion from the store state.
 * If any are null (model not yet persisted), the save is skipped silently.
 *
 * The `system` parameter is historical; the actual PUT body reads the LIVE
 * system from the store at fetch time. This means rapid successive
 * mutations automatically collapse into a single save of the most recent
 * state, and the queued-drain path always saves the latest state rather
 * than a stale snapshot.
 */
function debouncedSave(_system: HydraulicSystem, _calculationResults: CalculationOutput | null, _resultsStale: boolean): void {
  // Belt: a read-only session's unguarded view/calc actions still call
  // debouncedSave; never issue a network PUT for a viewer.
  if (useModelStore.getState().canEdit === false) return
  if (saveTimeout) clearTimeout(saveTimeout)
  saveTimeout = setTimeout((): void => {
    void runSave()
  }, DEBOUNCE_MS)
}

/** True when the current session may not edit. */
function guardEdit(): boolean {
  return useModelStore.getState().canEdit === false
}

/**
 * Why a library insert was refused. ⚠️ Named causes, not a bare boolean: the UI used to
 * render every failure as "This saved item isn't compatible with the current model", so a
 * read-only session (another tab holding the edit lock) was indistinguishable from corrupt
 * data — and sent us chasing a data bug that did not exist.
 */
export type InsertLibraryResult =
  | { ok: true }
  | { ok: false; reason: 'read-only' | 'no-model' | 'invalid-system' }

/**
 * Session status for a just-loaded document.
 *
 * ⚠️ Every load path used to reset this map to `{}`, so after a page reload the active
 * scenario read as 'never' — and `markScenariosStale` deliberately refuses to promote
 * 'never' to 'stale'. The result was PIPE-66: editing a reloaded model left the persisted
 * numbers on screen unmarked, and auto-calc never fired. Status is a FUNCTION of what is
 * persisted, so it is derived here rather than stored as a second field on the document.
 *
 * ⚠️ Always keyed on 'base', never on `scenarios.active_id`. `calculate()` installs
 * `calc` blocks onto the document only when Base is active, and `usePipeResult` suppresses
 * them off-Base — so the persisted snapshot is Base's by construction. Seeding whatever
 * scenario happened to be active at save time would claim freshness for numbers the UI
 * deliberately refuses to show.
 */
function seedScenarioStatus(system: HydraulicSystem | null | undefined): ScenarioStatusMap {
  return hasPersistedResults(system) ? { [DEFAULT_BASE_SCENARIO_ID]: 'fresh' } : {}
}

/**
 * The ONE normalization every server-loaded system must pass through.
 *
 * ⚠️ There are three entry points that set `system` from the server — `loadFromSystem`
 * (new/upload), `resyncModel` (opening an existing model), and the save-conflict
 * reconcile. Normalizing in only one of them is how a legacy-data fix silently misses the
 * main path: pipe flags were repaired on upload but not on open, so opening a model still
 * yielded a system that failed `HydraulicSystemSchema` and aborted library inserts.
 *
 * Legacy fixes belong HERE, not at an individual call site.
 */
/**
 * Normalise a system arriving from the server, reporting whether the fluid zones
 * actually moved.
 *
 * ⚠️ THERE IS DELIBERATELY NO FLAG-DROPPING WRAPPER. One existed and returned
 * only `.system`; two of its four callers then marked the state clean, so an
 * imported or conflict-reconciled Model was repaired in memory while the server
 * copy stayed imperial — and leaving without a further edit lost the repair
 * silently. Every caller now has to look at `fluidZonesChanged` and decide,
 * because the cost of forgetting is invisible.
 *
 * ⚠️ EVERY server-loaded path must go through here. A document saved before
 * PIPE-142 can hold a hand-created zone in lb/ft3 and cP, which the solver reads
 * as kg/m³ and Pa·s. Fixing only `openModel` was not enough: `resyncModel` and
 * the 409 conflict merge also load server state, and because the repair is not
 * persisted on open, a collaboration refresh would quietly put the imperial
 * numbers back and the next calculation would be wrong again.
 */
function normalizeLoadedSystemWithFlags(system: HydraulicSystem): { system: HydraulicSystem; fluidZonesChanged: boolean } {
  const si = normalizeSystemFluidZonesToSI(system)
  if (si.errors.length > 0) {
    // ⚠️ SURFACED, NOT THROWN. A unit we cannot convert leaves that one field as
    // it is — no worse than today — and the Model still opens. Throwing here made
    // it unopenable: import creates the Model, this throws, openModel catches and
    // returns false, and the editor shows "Not Found" for a Model that exists.
    logger.error('Fluid zone units could not be normalised; those fields are left as stored', undefined, { errors: si.errors })
  }
  const normalized = ensureScenarios(si.system)
  // `diagram_elements` is schema-defaulted but absent on systems predating Phase 1 Group A;
  // mutation actions spread/map/filter it and would crash on undefined.
  if (!normalized.diagram_elements) normalized.diagram_elements = []
  // `is_closed`/`is_oneway` are required but were omitted by every hand-built pipe until
  // the defaultPipeFields fix — see normalize-pipe-flags.
  if (normalized.edges) normalizeLegacyPipeFlags(normalized.edges)
  // ⚠️ RESULTS SOLVED BEFORE THE MIGRATION ARE DISCARDED, not merely flagged.
  // The persisted `calc` blocks in a pre-PIPE-142 document are the answer for
  // 62.3 kg/m³; presenting them beside a now-998 kg/m³ Model is a confident wrong
  // number, which is the whole category this work exists to remove.
  //
  // Dropping the blocks rather than setting `resultsStale` is what makes it hold:
  // the flag is transient and four other paths reset it to false, and
  // `seedScenarioStatus` reads the document, so Base was being seeded 'fresh'
  // from the very blocks in question. With them gone, every load path — open,
  // resync, the 409 conflict merge — inherits the right answer for free.
  //
  // Gated on `si.changed`, NOT on reference identity: this normaliser always
  // returns a fresh `fluid_zones` array, so `clearCalcResultsIfSolverInputsChanged`
  // would throw away good results on every open of every Model.
  const cleared = si.changed ? clearCalcResults(normalized) : normalized
  return { system: cleared, fluidZonesChanged: si.changed }
}

/**
 * Derive the persisted calcStatus + calcSummary from the live calculation
 * results. The dashboard card (ModelCard.tsx) reads these fields to render
 * the per-model status badge. Mapping:
 *
 *   null or invalid/pending → 'not_calculated'  (renders "Not calculated")
 *   status === 'success' with warnings → 'warnings'
 *   status === 'success' no warnings   → 'converged'
 *   status === 'failed' / 'invalid'    → 'failed'
 *
 * BUG-001 (Dominik QA Round 2, 2026-04-14): previously the auto-save body
 * omitted these fields entirely, so every model's dashboard status stayed
 * at the DB default 'not_calculated' forever, regardless of whether the
 * user had actually calculated it.
 */
/**
 * Reconcile the server's scenario tree with the local active selection after a
 * 409 conflict. Last-write-wins replaces the local system with the server's, but
 * we preserve the user's active-scenario choice when the server tree still has
 * that node (else clamp to 'base'). Returns undefined when the server system has
 * no scenarios (nothing to reconcile).
 */
export function mergeConflictScenarioSelection(
  serverScenarios: Scenarios | undefined,
  localActiveId: string,
): Scenarios | undefined {
  if (!serverScenarios) return undefined
  const keep = serverScenarios.nodes.some((n) => n.id === localActiveId)
  return { ...serverScenarios, active_id: keep ? localActiveId : 'base' }
}

/**
 * Does this result carry numbers worth installing?
 *
 * `partial` is a COMPLETED calculation: some Systems were skipped, but every
 * System that calculated produced real results. It must install like a success —
 * results applied, scenario cache retained, scenario not marked stale.
 *
 * Deliberately NOT used by auto-sizing, which needs complete edge coverage and
 * stays `success`-only.
 *
 * @internal exported for test.
 */
export function hasUsableResults<T extends { status: CalculationOutput['status'] }>(
  results: T | null | undefined,
): results is T & { status: UsableCalcStatus } {
  return results?.status === 'success' || results?.status === 'partial'
}

/** @internal exported for test — the dashboard badge mapping. */
export function deriveCalcStatus(
  results: CalculationOutput | null,
): { calcStatus: string; calcSummary: Record<string, unknown> | null } {
  if (!results) {
    return { calcStatus: 'not_calculated', calcSummary: null }
  }

  const warningsCount = results.warnings?.length ?? 0
  if (results.status === 'success') {
    return {
      calcStatus: warningsCount > 0 ? 'warnings' : 'converged',
      calcSummary: {
        iterations: results.iterations ?? 0,
        finalDeviation: results.finalDeviation ?? 0,
        warningsCount,
      },
    }
  }

  // `partial` is its own dashboard state: some Systems were skipped, but the
  // ones that ran produced real results. Reusing 'converged'/'warnings' would
  // hide the skipping; reusing 'failed' would discard results that exist.
  if (results.status === 'partial') {
    return {
      calcStatus: 'partial',
      calcSummary: {
        iterations: results.iterations ?? 0,
        finalDeviation: results.finalDeviation ?? 0,
        warningsCount,
      },
    }
  }

  // 'failed' or 'invalid' — both render as failed on the dashboard.
  return {
    calcStatus: 'failed',
    calcSummary: {
      iterations: results.iterations ?? 0,
      errorsCount: results.errors?.length ?? 0,
      warningsCount,
    },
  }
}

async function runSave(): Promise<void> {
  // If a save is currently in flight, remember that we need to save again
  // as soon as it finishes. The in-flight save's finally block will drain
  // this flag by re-invoking runSave against the then-current store state.
  if (saveInFlight) {
    pendingSaveRequested = true
    return
  }

  const state = useModelStore.getState()
  const { system, projectId, modelId, serverVersion, getAuthToken, calculationResults, resultsStale, sessionId } = state
  if (!system || !projectId || !modelId || serverVersion == null) return

  // Capture the exact system reference being saved so we can detect
  // whether any mutations landed while the PUT was in flight.
  const savedSystem = system

  // If results are stale (user edited the model since the last calc),
  // the calcStatus we'd persist would reference an older system. Safer
  // to treat stale results as 'not_calculated' for dashboard purposes.
  const { calcStatus, calcSummary } = deriveCalcStatus(
    resultsStale ? null : calculationResults,
  )

  saveInFlight = true
  try {
    const pipeCount = savedSystem.edges?.length ?? 0
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (getAuthToken) {
      const token = await getAuthToken()
      if (token) headers['Authorization'] = `Bearer ${token}`
    }
    const response = await fetch(`/api/projects/${projectId}/models/${modelId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({
        system: savedSystem,
        version: serverVersion,
        pipeCount,
        calcStatus,
        calcSummary,
        ...(sessionId ? { sessionId } : {}),
      }),
    })

    if (response.ok) {
      const json = await response.json()
      // PUT returns { version, seq } (D4 Task 3) — seq is the appended
      // model-history change_seq, omitted for empty-diff/degrade saves that
      // append nothing to the history chain.
      const result = (json.data ?? json) as { version: number; seq?: number }
      // Only clear isDirty if the live system still equals what we just
      // persisted. If mutations arrived during the request, the store's
      // `system` reference will differ from `savedSystem` and we must
      // keep isDirty=true so the UI correctly shows unsaved changes and
      // any dirty-gated behavior (e.g. unload warnings) still triggers.
      const liveSystem = useModelStore.getState().system
      const cleanAfterSave = liveSystem === savedSystem
      useModelStore.setState({
        serverVersion: result.version,
        // Leave headSeq unchanged when the save appended nothing (empty-diff
        // / calc-only degrade save returns no seq) — advancing it here would
        // make useModelStream's dual-counter reconcile think our own save
        // moved the head when it didn't.
        ...(result.seq !== undefined ? { headSeq: result.seq } : {}),
        ...(cleanAfterSave ? { isDirty: false } : {}),
        lastSavedAt: Date.now(),
      })
    } else if (response.status === 409) {
      // Version conflict — server has a newer version (e.g., another tab)
      // Per spec: last-write-wins, replace local state with server's version
      const json = await response.json()
      // A 409 is an error status, so the status-aware wrapper puts the body
      // under `error`, not `data`. Reading `data` here silently missed
      // `lock-lost` and left the editor writable after someone else took the
      // lock — see api-envelope.ts.
      // Typed loosely on purpose: `normalizeLoadedSystem` and the store setter
      // below do the real narrowing, and tightening it here only moves the cast
      // rather than removing it.
      // `currentSystem`/`currentVersion` are declared present because the one
      // conflict body that omits them — lock-lost — returns before they are
      // read. Optional here instead would only push the same assumption into a
      // cast further down.
      const conflict = (envelopePayload(json) ?? {}) as {
        reason?: string
        currentSystem: Parameters<typeof normalizeLoadedSystemWithFlags>[0]
        currentVersion: number | null
      }
      if (conflict.reason === 'lock-lost') {
        // Someone took over editing. Go read-only; do NOT touch currentSystem
        // (the lock-lost body has none) and cancel any queued drain.
        pendingSaveRequested = false
        useModelStore.getState().setCanEdit(false)
        useModelStore.getState().notifyDemotedOnce() // deduped one-shot toast
        return
      }
      // Preserve the user's active-scenario selection across the last-write-wins
      // swap when the server tree still has that node (else clamp to 'base').
      const localActiveId = useModelStore.getState().system?.scenarios?.active_id ?? 'base'
      const mergedServerLoad = normalizeLoadedSystemWithFlags(conflict.currentSystem)
      const mergedServer = mergedServerLoad.system
      const reconciledScenarios = mergeConflictScenarioSelection(mergedServer.scenarios, localActiveId)
      const reconciled = reconciledScenarios
        ? { ...mergedServer, scenarios: reconciledScenarios }
        : mergedServer
      useModelStore.setState({
        system: reconciled,
        serverVersion: conflict.currentVersion,
        // The server's copy needed repairing, so this state is ahead of it.
        isDirty: mergedServerLoad.fluidZonesChanged,
        lastSavedAt: Date.now(),
        // Server system replaces local; per-scenario caches were computed
        // against the now-replaced local system, so reset them. The plain
        // calculationResults field must agree with the emptied caches.
        scenarioResults: {},
        scenarioStatus: seedScenarioStatus(reconciled),
        isCalculatingAll: false,
        scenarioCalcProgress: null,
        calculationResults: null,
        resultsStale: false,
      })
      // After a conflict resolution the live state IS the server state,
      // so any pending drain would save server-state-back-to-server (a
      // no-op at best, another conflict at worst). Drop the pending flag.
      pendingSaveRequested = false
      logger.warn('Model version conflict resolved — loaded server version', {
        modelId,
        projectId,
        attemptedVersion: serverVersion,
        serverVersion: conflict.currentVersion,
      })
    } else {
      // Promote save failure to error — a user-facing save not landing is a
      // real incident, not a warning, and we want Sentry alerts to fire.
      logger.error('Auto-save failed', undefined, {
        status: response.status,
        modelId,
        projectId,
        attemptedVersion: serverVersion,
      })
    }
  } catch (err) {
    logger.error('Auto-save error', err instanceof Error ? err : undefined, {
      modelId,
      projectId,
      attemptedVersion: serverVersion,
    })
  } finally {
    saveInFlight = false
    // Drain a queued save request, if any. This runs on the next tick so
    // the current finally block can complete first and any state updates
    // from the fetch response are fully settled before the drain reads
    // the live store.
    if (pendingSaveRequested) {
      pendingSaveRequested = false
      setTimeout((): void => { void runSave() }, 0)
    }
  }
}

// ============================================================
// History helpers
// ============================================================

const MAX_UNDO_DEPTH = 50

function pushHistory(
  undoStack: HistoryEntry[],
  vertices: Vertex[],
  edges: Edge[],
  scenarios: Scenarios | undefined,
  // All call sites pass system.diagram_elements directly. ?? [] normalizes the field for
  // systems created before diagram_elements was introduced (e.g. createEmptySystem).
  diagram_elements: DiagramElement[] | undefined,
): HistoryEntry[] {
  const entry: HistoryEntry = {
    kind: 'graph',
    vertices: structuredClone(vertices),
    edges: structuredClone(edges),
    scenarios: scenarios !== undefined ? structuredClone(scenarios) : undefined,
    diagram_elements: structuredClone(diagram_elements ?? []),
  }
  const next = [...undoStack, entry]
  if (next.length > MAX_UNDO_DEPTH) next.shift()
  return next
}

function captureFull(system: HydraulicSystem): Extract<HistoryEntry, { kind: 'full' }> {
  return {
    kind: 'full',
    vertices: structuredClone(system.vertices),
    edges: structuredClone(system.edges),
    fluid_zones: structuredClone(system.fluid_zones),
    pipe_specifications: structuredClone(system.pipe_specifications),
    // Optional collections: pass through undefined as-is (structuredClone polyfill
    // uses JSON.parse/stringify which cannot handle undefined).
    heat_transfer_settings: system.heat_transfer_settings !== undefined
      ? structuredClone(system.heat_transfer_settings)
      : undefined,
    layers: system.layers !== undefined
      ? structuredClone(system.layers)
      : undefined,
    custom_field_definitions: system.custom_field_definitions !== undefined
      ? structuredClone(system.custom_field_definitions)
      : undefined,
    scenarios: system.scenarios !== undefined ? structuredClone(system.scenarios) : undefined,
    diagram_elements: structuredClone(system.diagram_elements ?? []),
  }
}

function applyEntry(system: HydraulicSystem, entry: HistoryEntry): HydraulicSystem {
  // Restore diagram_elements from the history snapshot.
  // Fixes a latent pre-existing bug: add/remove-schedule undo was a no-op because
  // applyEntry never restored diagram_elements (it spread the live system forward unchanged).
  // Also required for group move to stay in sync on undo.
  // Every HistoryEntry constructor guarantees diagram_elements is a DiagramElement[] (never
  // undefined), so this always restores the correct pre-action state.
  if (entry.kind === 'graph') {
    return {
      ...system,
      vertices: entry.vertices,
      edges: entry.edges,
      scenarios: entry.scenarios,
      diagram_elements: entry.diagram_elements,
    }
  }
  return {
    ...system,
    vertices: entry.vertices,
    edges: entry.edges,
    fluid_zones: entry.fluid_zones,
    pipe_specifications: entry.pipe_specifications,
    heat_transfer_settings: entry.heat_transfer_settings,
    layers: entry.layers,
    custom_field_definitions: entry.custom_field_definitions,
    scenarios: entry.scenarios,
    diagram_elements: entry.diagram_elements,
  }
}

// T11/B29: boundary-condition vertex types that must NOT be dissolved into a
// passive junction (doing so would silently drop the pressure/grade/flow
// reference and break solvability). They plain-delete instead.
export const DISSOLVE_BOUNDARY_TYPES = new Set<string>(['pressure_boundary', 'tank', 'flow_demand'])

/** The endpoint of `e` that is not `id`. */
function otherEndpoint(e: Edge, id: string): string {
  return e.connection.source_id === id ? e.connection.target_id : e.connection.source_id
}

/**
 * Re-key every edge-scoped scenario override from `fromId` onto each of `toIds`.
 *
 * ⚠️ `Scenarios` is `{ active_id, nodes: [{ overrides: [...] }] }` — NOT an array of
 * scenarios. An earlier version of this helper assumed the array shape, so
 * `Array.isArray` was false and it returned the input untouched: a **silent no-op** that
 * left every split's overrides dangling exactly as before. Shape-guessing here fails
 * quietly, which is why this is typed against the real schema.
 *
 * Both split halves inherit the same base spec/size, so both are legitimate heirs;
 * migrating to only one silently half-reverts the override on the other segment. A
 * dangling override id fails `validateScenariosClosure` and the HydraulicSystemSchema
 * refine rejects the whole system.
 *
 * ⚠️ Deduped by (id, field): when a merge migrates an override onto an edge that ALREADY
 * overrides that field, the survivor's own value wins — it is the one already attached.
 */
function migrateEdgeOverrides(scenarios: Scenarios, fromId: string, toIds: string[]): Scenarios {
  return {
    ...scenarios,
    nodes: scenarios.nodes.map(n => {
      const expanded = n.overrides.flatMap(o =>
        o.target === 'edge' && o.id === fromId
          ? toIds.map(id => ({ ...o, id }))
          : [o],
      )
      const seen = new Set<string>()
      const overrides = expanded.filter(o => {
        const key = `${o.target}::${o.id}::${o.field}`
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
      return { ...n, overrides }
    }),
  }
}

/** Which stored-chain index `p` falls nearest, in the indexing `splitChainAt` expects
 *  (0 = source seam, chain.length = target seam). Used only by the legacy no-opts path,
 *  which has no segment index of its own. */
function nearestStoredSegment(chain: Array<{ x: number; y: number }>, p: { x: number; y: number }): number {
  if (chain.length === 0) return 0
  let bestIdx = 0
  let bestDist = Infinity
  for (let i = 0; i < chain.length; i++) {
    const d = Math.hypot(chain[i]!.x - p.x, chain[i]!.y - p.y)
    if (d < bestDist) { bestDist = d; bestIdx = i }
  }
  return bestIdx
}

interface SplitResult {
  vertices: unknown[]
  edges: unknown[]
  scenarios: unknown
  newVertexId: string
  edgeAId: string
  edgeBId: string
}

/**
 * Where a positional split lands, plus the unit the user reads lengths in.
 *
 * `lengthUnit` is what lets the split tidy its two halves (PIPE-10): rounding has to happen
 * in the DISPLAYED unit, and only a React caller can resolve that from the unit-system
 * context. Omit it and the halves stay an exact proportional cut.
 */
export interface SplitAt {
  position: { x: number; y: number }
  t: number
  segmentIndex: number
  lengthUnit?: string
}

/**
 * Everything a positional split changes, as plain data — NO history, NO `set`.
 *
 * Pure so the tee (`teeOntoEdge`) can compose a split with a new pipe inside a SINGLE
 * history frame. An action that pushed history itself could not be composed.
 *
 * `opts` omitted reproduces the legacy right-click GEOMETRY (endpoint midpoint, 50/50)
 * while still applying the reference-integrity, undefined-length and route-preservation
 * fixes — those are correctness fixes, not behaviour changes.
 */
function buildSplit(
  system: HydraulicSystem,
  edgeId: string,
  vertexType: string,
  opts?: SplitAt,
): SplitResult | null {
  const edge = system.edges.find(e => e.id === edgeId)
  if (!edge) return null
  if (edge.type !== 'pipe' && edge.type !== 'compressible_pipe') return null

  const sourceVertex = system.vertices.find(v => v.id === edge.connection.source_id)
  const targetVertex = system.vertices.find(v => v.id === edge.connection.target_id)
  if (!sourceVertex || !targetVertex) return null

  const claimedIds = new Set<string>()
  const newVertexId = nextVertexId(system.vertices, claimedIds)
  claimedIds.add(newVertexId)
  const edgeAId = nextEdgeId(system.edges, claimedIds)
  claimedIds.add(edgeAId)
  const edgeBId = nextEdgeId(system.edges, claimedIds)

  const chain = ((edge.diagram as { points?: Array<{ x: number; y: number }> } | undefined)?.points ?? [])

  let position: { x: number; y: number }
  let t: number
  let segmentIndex: number
  if (opts) {
    // ⚠️ Positional path (tee / drop-on-pipe): run BOTH guards through ONE clamped
    // fraction so length and geometry agree. `clampSplitGeometry` pulls the inserted
    // vertex off an endpoint when the drop was within ε — the geometry-side backstop that
    // the length-only `clampSplitT` does NOT provide (its comment says the vertex "still
    // lands exactly where the user clicked"). Without this, a near-endpoint tee gets a
    // non-zero length yet a junction sitting ON the endpoint: a zero-length sliver in the
    // drawing. §3.2/§3.3. When the drop is safely mid-pipe, geometry stays exactly on the
    // resolver's projected point.
    const polyline = [sourceVertex.diagram.position, ...chain, targetVertex.diagram.position]
    const g = clampSplitGeometry(polyline, opts.t)
    t = g.t
    position = g.clamped ? g.position : opts.position
    segmentIndex = g.clamped ? nearestStoredSegment(chain, position) : opts.segmentIndex
  } else {
    // Legacy right-click "Insert Device": endpoint-midpoint, 50/50 length. Geometry
    // unchanged by design (§3.2) — only reference handling is fixed (below).
    t = clampSplitT(0.5).t
    position = {
      x: (sourceVertex.diagram.position.x + targetVertex.diagram.position.x) / 2,
      y: (sourceVertex.diagram.position.y + targetVertex.diagram.position.y) / 2,
    }
    segmentIndex = nearestStoredSegment(chain, position)
  }
  const { before, after } = splitChainAt(chain, segmentIndex, position)

  // ⚠️ Partition-and-re-route, never `points: []`. There is NO render-time router: an
  // empty chain renders a STRAIGHT DIAGONAL, so clearing it flattens every split. This
  // is a live bug in the pre-existing implementation, not just a shaped-route concern.
  // routeEdge regenerates for 'orthogonal' and preserves for 'isometric'.
  const basis = getRouteBasis(system)
  const srcBox = vertexToRouteBox(sourceVertex.diagram.position, sourceVertex.type)
  const tgtBox = vertexToRouteBox(targetVertex.diagram.position, targetVertex.type)
  const newBox = vertexToRouteBox(position, vertexType)
  const routeA = routeEdge(srcBox, newBox, { waypoints: before }, { points: before }, basis)
  const routeB = routeEdge(newBox, tgtBox, { waypoints: after }, { points: after }, basis)

  // ⚠️ Interpolate the inserted vertex's elevation. createVertexAtPosition →
  // createJunction hard-defaults {value:0,unit:'m'}, which silently injects a static-head
  // error on any model not at datum — invisible, because canvas Y is not elevation.
  const created = createVertexAtPosition(newVertexId, vertexType, position) as unknown as Record<string, unknown>
  const elev = interpolateElevation(elevationAt(sourceVertex, 'source'), elevationAt(targetVertex, 'target'), t)
  const newVertex = elev ? { ...created, elevation: elev } : created

  // ⚠️ An UNDEFINED length stays undefined on both halves. The old `?? 0` produced a
  // DEFINED zero — physically meaningless, and invisible to the length prompt, which
  // keys on `length === undefined`.
  const srcLen = (edge as AnyEdge).length
  const half = (frac: number): { value: number; unit: string } | undefined =>
    srcLen === undefined ? undefined : { value: srcLen.value * frac, unit: srcLen.unit }

  // PIPE-10: an exact proportional cut inherits every pixel of pointer imprecision, so a
  // 10 ft pipe teed near the middle reports 4.995 / 5.005. Snap to a magnitude-appropriate
  // step, keeping the total. Returns null (→ exact cut) when rounding can't help.
  //
  // ⚠️ POSITIONAL PATH ONLY. The legacy right-click insert is a deliberate 50/50 midpoint
  // with no pointer noise in it; rounding that would DISTORT a clean value (a 7 ft pipe's
  // honest 3.5/3.5 would become 4/3). `lengthUnit` is only ever supplied by the drop paths.
  const tidied = opts?.lengthUnit ? roundSplitLengths(srcLen, t, opts.lengthUnit) : null

  const base = structuredClone(edge) as unknown as Record<string, unknown>
  const makeHalf = (
    id: string, source_id: string, target_id: string,
    len: { value: number; unit: string } | undefined,
    fittings: unknown[],
    route: { points: Array<{ x: number; y: number }>; sourceHandle: string; targetHandle: string },
  ): Record<string, unknown> => {
    const out: Record<string, unknown> = {
      ...base, id, name: `Pipe ${id}`,
      connection: { source_id, target_id },
      fittings,
      diagram: { points: route.points, sourceHandle: route.sourceHandle, targetHandle: route.targetHandle },
    }
    if (len === undefined) delete out['length']
    else out['length'] = len
    return out
  }

  // ⚠️ Fittings go UPSTREAM only. structuredClone put them on BOTH halves, double-counting
  // every K.
  const edgeA = makeHalf(edgeAId, edge.connection.source_id, newVertexId, tidied?.a ?? half(t),
    structuredClone((edge as unknown as { fittings?: unknown[] }).fittings ?? []), routeA)
  const edgeB = makeHalf(edgeBId, newVertexId, edge.connection.target_id, tidied?.b ?? half(1 - t), [], routeB)

  // ⚠️ Reference integrity: `edgeId` is DROPPED, so every reference to it must be re-keyed
  // or a schema refine rejects the whole system.
  const vertices = system.vertices.map(v => {
    const pens = (v as unknown as { penetrations?: Array<{ edge_id: string }> }).penetrations
    if (!pens) return v as unknown
    // The penetration follows the half that stays incident to this tank.
    const heir = v.id === edge.connection.source_id ? edgeAId : edgeBId
    return { ...v, penetrations: pens.map(p => (p.edge_id === edgeId ? { ...p, edge_id: heir } : p)) }
  }) as unknown[]
  vertices.push(newVertex)

  return {
    vertices,
    edges: [...system.edges.filter(e => e.id !== edgeId), edgeA, edgeB] as unknown[],
    scenarios: system.scenarios ? migrateEdgeOverrides(system.scenarios, edgeId, [edgeAId, edgeBId]) : system.scenarios,
    newVertexId, edgeAId, edgeBId,
  }
}

/**
 * The zero-loss connection points `convertJunction` folds into a single pressure node.
 *
 * ⚠️ NOT the literal 'junction' string. A tee inserts a `tee` vertex, so gating on
 * 'junction' alone would make the weld refuse the very nodes teeing creates.
 */
const WELDABLE_VERTEX_TYPES = new Set(['junction', 'tee', 'bend', 'branch'])

/** T11/B29: can the two pipes incident on a deleted device be merged losslessly
 * into one continuous pipe? Requires plain pipes with identical spec/size/zone,
 * neither one-way (avoids direction ambiguity), same closed-state, and distinct
 * far endpoints (else A—A self-loop). Anything else splices a junction instead. */
function mergeableForDissolve(e1: Edge, e2: Edge, deviceId: string): boolean {
  if (e1.type !== 'pipe' || e2.type !== 'pipe') return false
  const a = e1 as PipeEdge
  const b = e2 as PipeEdge
  return (
    a.pipe_specification_id === b.pipe_specification_id &&
    a.pipe_size_id === b.pipe_size_id &&
    a.fluid_zone_id === b.fluid_zone_id &&
    a.is_oneway === false && b.is_oneway === false &&
    a.is_closed === b.is_closed &&
    otherEndpoint(e1, deviceId) !== otherEndpoint(e2, deviceId)
  )
}

/**
 * Is this vertex a plain weld between two pipes that can be spliced back into one?
 *
 * A junction inserted by a split is exactly this shape, and deleting it should undo the
 * split rather than take both pipes with it (PIPE-10 follow-up). Unlike a pump or a valve,
 * a junction carries no equipment — it IS the break — so there is nothing to remove
 * independently of the pipes and "delete + pipes" is never the useful reading.
 *
 * ⚠️ Every delete path now funnels through `dissolveDevice`, which is the sole caller — the
 * Delete key hands a lone junction to it and the context menu's single "Delete" calls it
 * directly. That is deliberate: the three used to decide independently (the menu hid the
 * option for junctions, `dissolveDevice` short-circuited them to a plain delete, and the
 * key cascaded), so no route rejoined the pipe. Route new delete affordances through
 * `dissolveDevice` rather than re-testing this predicate at the call site.
 */
export function canHealOnDissolve(system: HydraulicSystem, vertexId: string): boolean {
  const incident = getConnectedEdges(vertexId, system.edges)
  if (incident.length !== 2) return false
  return mergeableForDissolve(incident[0]!, incident[1]!, vertexId)
}

/** Apply a single property write to an edge for the Base path. `pipe_spec` is a
 * coupled field carrying both spec + size ids; all other fields wrap directly. */
function applyEdgeField(e: Edge, prop: string, val: unknown): Edge {
  if (prop === 'pipe_spec') {
    const v = val as { pipe_specification_id: string; pipe_size_id: string }
    return { ...e, pipe_specification_id: v.pipe_specification_id, pipe_size_id: v.pipe_size_id } as Edge
  }
  return { ...e, [prop]: val } as Edge
}

// ============================================================
// Store
// ============================================================

export const useModelStore = create<ModelStore>()((set, get) => ({
  // Initial state
  system: null,
  selectedIds: [],
  interactionMode: 'select',
  viewMode: '2d',
  schematicSnapshot: null,
  addVertexType: null,
  addPipeSource: null,
  draftWaypoints: [],
  promptLengthEdgeId: null,
  pendingLibrarySnapshot: null,
  clipboard: null,
  pasteCounter: 0,
  undoStack: [],
  redoStack: [],
  canUndo: false,
  canRedo: false,
  isDirty: false,
  lastSavedAt: null,
  projectId: null,
  modelId: null,
    modelName: null,
    capabilities: null,
    importJobId: null,
    sourceType: null,
  serverVersion: null,
  headSeq: null,
  getAuthToken: null,
  sessionId: null,
  canEdit: true,
  _demotionNotified: false,
  calculationResults: null,
  isCalculating: false,
  isAutoCalcEnabled: false,
  resultsStale: false,
  resultsDisplayMode: 'none' as ResultsDisplayMode,
  streamProgress: null,
  staleCalcHistory: [],
  scenarioResults: {},
  scenarioStatus: {},
  isCalculatingAll: false,
  scenarioCalcProgress: null,
  analysisResults: null,
  isAnalyzing: false,
  designReviewJobId: null,
  isDesignReviewPending: false,
  aiDesignReviewChoice: readAiDesignReviewChoice(),
  isAiDisclosurePending: false,
  elementHealthMap: {},
  healthSummary: null,
  hoveredId: null,
  pathTraceStartVertex: null,
  pathTraceSource: null,
  pathTraceResult: null,
  criticalPath: null,
  showSizingDialog: false,
  autoSizeSuggestions: [],
  dismissedSuggestionIds: [],
  isAutoSizing: false,
  flowAssist: {
    messages: [],
    isStreaming: false,
    sessionTokens: { input: 0, output: 0 },
    sessionId: null,
  },
  showFlowAssistPalette: false,
  showFindDevice: false,
  pendingSuggestions: {},

  // Convergence Doctor
  convergenceDoctorNeeded: false,

  // Settings dialog
  settingsOpen: false,
  settingsTab: 'general',

  // Save-to-Library dialog
  saveToLibraryOpen: false,
  symbolPickerVertexId: null,


  // Scenario grid dock — hydrated from localStorage; NOT reset on model load.
  scenarioDockOpen: readDockOpen(),
  scenarioDockHeight: readDockHeight(),

  // Device Library refresh signal (transient — not persisted, not in undo history)
  libraryRefreshToken: 0,

  // -- Lifecycle ------------------------------------------------

  loadFromSystem: (system): void => {
    if (saveTimeout) clearTimeout(saveTimeout)
    // Navigate-away (spec §3.4): cancel any in-flight streamed calc rather
    // than let it keep running/holding a worker slot for a model we're
    // leaving.
    calcAbortController?.abort()
    calcAbortController = null
    const { getAuthToken } = get()
    // Normalize payload: `diagram_elements` is defaulted in the zod schema but
    // arrives undefined on legacy persisted systems that pre-date Phase 1 Group A.
    // Without this, mutation actions crash on spread/map/filter of `undefined`.
    const loaded = normalizeLoadedSystemWithFlags(structuredClone(system))
    const normalized = loaded.system
    set({
      system: normalized,
      selectedIds: [],
      undoStack: [],
      redoStack: [],
      canUndo: false,
      canRedo: false,
      // An imported system whose zones we repaired is ahead of whatever was
      // persisted from it, so the repair rides out on the next save.
      isDirty: loaded.fluidZonesChanged,
      lastSavedAt: null,
      projectId: null,
      modelId: null,
    modelName: null,
    capabilities: null,
    importJobId: null,
    sourceType: null,
      serverVersion: null,
      headSeq: null,
      getAuthToken, // preserve auth provider across resets
      interactionMode: 'select',
      // The picker names a vertex in the model being cleared. Left set, it
      // survives into the next model, where the same generated id ('v0', 'J1')
      // is likely to name an entirely different device.
      symbolPickerVertexId: null,
      pendingLibrarySnapshot: null,
      schematicSnapshot: null,
      viewMode: '2d',
      addVertexType: null,
      addPipeSource: null,
      calculationResults: null,
      isCalculating: false,
      isAutoCalcEnabled: false,
      resultsStale: false,
      resultsDisplayMode: initialDisplayMode(system),
      streamProgress: null,
      staleCalcHistory: [],
      scenarioResults: {},
      scenarioStatus: seedScenarioStatus(normalized),
      isCalculatingAll: false,
      scenarioCalcProgress: null,
      analysisResults: null,
      isAnalyzing: false,
      elementHealthMap: {},
      healthSummary: null,
      pathTraceStartVertex: null,
      pathTraceSource: null,
      pathTraceResult: null,
      criticalPath: null,
      showSizingDialog: false,
      autoSizeSuggestions: [],
      dismissedSuggestionIds: [],
      isAutoSizing: false,
    })
    // NOTE: No debouncedSave here — model is not on the server yet.
    // The import flow will call POST to create the model and then set projectId/modelId.
  },

  setAuthProvider: (fn: () => Promise<string | null>): void => {
    set({ getAuthToken: fn })
  },

  openModel: async (projectId: string, modelId: string): Promise<boolean> => {
    try {
      // Idempotency guard: if the caller asks for the model that's already
      // loaded, skip everything. Otherwise an unrelated re-render of
      // EditorPage that causes its load-model useEffect to fire again
      // would clobber the store and reset the FlowAssist chat history
      // mid-session. This was BUG-003 (Dominik QA Round 2, 2026-04-14):
      // "chat history spontaneously reset/disappeared" — the reset
      // happened because openModel unconditionally wrote flowAssist: {}
      // even when re-opening the same model as before.
      const { projectId: currentProjectId, modelId: currentModelId, system: currentSystem } = get()
      if (
        currentSystem !== null &&
        currentProjectId === projectId &&
        currentModelId === modelId
      ) {
        return true
      }

      // Navigate-away (spec §3.4): cancel any in-flight streamed calc for
      // whatever model we're switching away from.
      calcAbortController?.abort()
      calcAbortController = null

      const { getAuthToken } = get()
      const headers: Record<string, string> = {}
      if (getAuthToken) {
        const token = await getAuthToken()
        if (token) headers['Authorization'] = `Bearer ${token}`
      }
      const response = await fetch(`/api/projects/${projectId}/models/${modelId}`, { headers })
      if (!response.ok) {
        if (response.status === 404) return false
        throw new Error(`Failed to fetch model: ${response.status}`)
      }

      const json = await response.json()
      // Unwrap apiResponseWrapper envelope: { success, data: { system, version, ... } }
      const model = json.data ?? json
      // Shared with resyncModel and the conflict merge — see
      // normalizeLoadedSystemWithFlags. openModel used to call ensureScenarios
      // directly, which is how it drifted from the other load paths.
      const loaded = normalizeLoadedSystemWithFlags(model.system)

      set({
        system: loaded.system,
        projectId,
        modelId,
        modelName: model.name ?? null,
        capabilities: model.capabilities ?? null,
        importJobId: model.importJobId ?? null,
        sourceType: model.sourceType ?? null,
        serverVersion: model.version,
        headSeq: model.headSeq ?? null,
        selectedIds: [],
        undoStack: [],
        redoStack: [],
        canUndo: false,
        canRedo: false,
        // ⚠️ DIRTY WHEN THE UNITS WERE REPAIRED, so the migration and the discarded
        // results reach the server on the next save the user makes.
        //
        // Without this the repair lives only in memory: `isDirty: false` means no
        // PUT, so the stored document keeps its imperial zone AND its now-deleted
        // results, and `calcStatus` keeps the dashboard card advertising
        // "Converged" for a calculation the editor has thrown away.
        //
        // NOT an autosave. Saves are issued by explicit `debouncedSave(...)` calls
        // inside the edit actions; nothing in this store watches `isDirty`. So
        // opening a model marks it changed and waits, rather than silently
        // rewriting a document because somebody looked at it.
        isDirty: loaded.fluidZonesChanged,
        lastSavedAt: Date.now(),
        interactionMode: 'select',
        pendingLibrarySnapshot: null,
        schematicSnapshot: null,
        viewMode: '2d',
        addVertexType: null,
        addPipeSource: null,
        calculationResults: null,
        isCalculating: false,
        isAutoCalcEnabled: false,
        // Not stale — GONE. normalizeLoadedSystemWithFlags discards results that
        // were solved before the zones were normalised, so there is nothing left
        // to be out of date.
        resultsStale: false,
        resultsDisplayMode: initialDisplayMode(loaded.system),
        streamProgress: null,
        staleCalcHistory: [],
        scenarioResults: {},
        scenarioStatus: seedScenarioStatus(loaded.system),
        isCalculatingAll: false,
        scenarioCalcProgress: null,
        analysisResults: null,
        isAnalyzing: false,
        elementHealthMap: {},
        healthSummary: null,
        pathTraceStartVertex: null,
        pathTraceSource: null,
        pathTraceResult: null,
        criticalPath: null,
        showSizingDialog: false,
        autoSizeSuggestions: [],
        dismissedSuggestionIds: [],
        isAutoSizing: false,
        // UX-010: reset the FlowAssist chat state when opening a new model.
        // Otherwise the AI carries forward messages and session context from
        // whatever model the user had open before, which is both confusing
        // (the chat references devices that don't exist in the new model)
        // and potentially unsafe (stale session tokens could be applied to
        // the wrong system).
        flowAssist: {
          messages: [],
          isStreaming: false,
          sessionTokens: { input: 0, output: 0 },
          sessionId: null,
        },
      })
      return true
    } catch (err) {
      logger.error('Failed to open model', err instanceof Error ? err : undefined, { projectId, modelId })
      return false
    }
  },

  clearModel: (): void => {
    if (saveTimeout) clearTimeout(saveTimeout)
    // Navigate-away (spec §3.4): cancel any in-flight streamed calc.
    calcAbortController?.abort()
    calcAbortController = null
    const { getAuthToken } = get()
    set({
      system: null,
      selectedIds: [],
      undoStack: [],
      redoStack: [],
      canUndo: false,
      canRedo: false,
      isDirty: false,
      lastSavedAt: null,
      projectId: null,
      modelId: null,
    modelName: null,
    capabilities: null,
    importJobId: null,
    sourceType: null,
      serverVersion: null,
      headSeq: null,
      getAuthToken, // preserve auth provider across resets
      interactionMode: 'select',
      // Same reason as loadFromSystem: the picker names a vertex in the model
      // being torn down, and ids are generated per model.
      symbolPickerVertexId: null,
      pendingLibrarySnapshot: null,
      schematicSnapshot: null,
      viewMode: '2d',
      addVertexType: null,
      addPipeSource: null,
      calculationResults: null,
      isCalculating: false,
      isAutoCalcEnabled: false,
      resultsStale: false,
      resultsDisplayMode: 'none' as const,
      streamProgress: null,
      staleCalcHistory: [],
      scenarioResults: {},
      scenarioStatus: {},
      isCalculatingAll: false,
      scenarioCalcProgress: null,
      analysisResults: null,
      isAnalyzing: false,
      elementHealthMap: {},
      healthSummary: null,
      pathTraceStartVertex: null,
      pathTraceSource: null,
      pathTraceResult: null,
      criticalPath: null,
      showSizingDialog: false,
      autoSizeSuggestions: [],
      dismissedSuggestionIds: [],
      isAutoSizing: false,
    })
  },

  abortCalculation: (): void => {
    calcAbortController?.abort()
  },

  // -- Collaboration (Phase 1: per-model edit lock + presence) --

  setSessionId: (id: string | null): void => set({ sessionId: id }),
  // Becoming editor clears the one-shot demotion flag so a future takeover can notify again.
  setCanEdit: (v: boolean): void => set(v ? { canEdit: true, _demotionNotified: false } : { canEdit: false }),
  saveNow: (): Promise<void> => runSave(),
  // Fire the "taken over" toast at most once per editor session — deduped across
  // the heartbeat-demotion path (Task 6) and the save-rejection path (runSave's
  // lock-lost branch).
  notifyDemotedOnce: (): void => {
    if (get()._demotionNotified) return
    set({ _demotionNotified: true })
    toast.warning('Editing taken over', {
      description: 'Someone took over editing this model. Your recent unsaved changes may not be saved.',
    })
  },
  // Surgical re-fetch used on promotion to editor: refresh ONLY the server
  // document + version + result caches; does NOT reset viewMode/selection/undo
  // like openModel, and bypasses openModel's already-loaded guard.
  resyncModel: async (): Promise<void> => {
    const { projectId, modelId, getAuthToken } = get()
    if (!projectId || !modelId) return
    const headers: Record<string, string> = {}
    if (getAuthToken) { const t = await getAuthToken(); if (t) headers['Authorization'] = `Bearer ${t}` }
    const res = await fetch(`/api/projects/${projectId}/models/${modelId}`, { headers })
    if (!res.ok) return
    const json = await res.json()
    const model = json.data ?? json
    const resynced = normalizeLoadedSystemWithFlags(model.system)
    set({
      // Same normalised system for both, or Base is seeded 'fresh' from result
      // blocks the normalisation just discarded — which suppresses auto-calc.
      system: resynced.system,
      serverVersion: model.version,
      headSeq: model.headSeq ?? null,
      // Same rule as openModel: a repair the server has not seen is a change.
      isDirty: resynced.fluidZonesChanged,
      scenarioResults: {}, scenarioStatus: seedScenarioStatus(resynced.system), isCalculatingAll: false,
      scenarioCalcProgress: null, calculationResults: null, resultsStale: false,
    })
  },

  // -- Selection ------------------------------------------------

  setSelectedIds: (ids): void => set({ selectedIds: ids }),

  toggleSelectedId: (id): void => {
    const { selectedIds } = get()
    if (selectedIds.includes(id)) {
      set({ selectedIds: selectedIds.filter(sid => sid !== id) })
    } else {
      set({ selectedIds: [...selectedIds, id] })
    }
  },

  clearSelection: (): void => set({ selectedIds: [] }),

  setInteractionMode: (mode): void => set({
    interactionMode: mode,
    addPipeSource: null,
    draftWaypoints: [],
    pathTraceStartVertex: null,
    pathTraceResult: null,
    ...(mode !== 'add-device' ? { addVertexType: null } : {}),
    ...(mode !== 'add-library' ? { pendingLibrarySnapshot: null } : {}),
  }),

  setAddVertexType: (type): void => set({ addVertexType: type }),
  setAddPipeSource: (deviceId): void => set({ addPipeSource: deviceId }),

  pushDraftWaypoint: (p): void => set({ draftWaypoints: [...get().draftWaypoints, p] }),
  popDraftWaypoint: (): void => set({ draftWaypoints: get().draftWaypoints.slice(0, -1) }),
  clearDraftWaypoints: (): void => set({ draftWaypoints: [] }),
  clearLengthPrompt: (): void => set({ promptLengthEdgeId: null }),

  /**
   * Start a pipe with no device armed: place a junction in empty space and arm it as the
   * add-pipe source, so junction→junction pipes need no pre-placed device (spec §3.1,
   * "a pipe may also start empty").
   *
   * This commits the start junction in its OWN history frame and the finish
   * (`addPipeToNewJunction` / `teeOntoEdge` / `addDrawnPipe`) commits the pipe in a
   * second — each frame independently sensible (undo once → terminal gone; undo twice →
   * start junction gone). Only the FINISH is required to be atomic (§3.1 undo note); the
   * start is a plain vertex placement, same as an add-device drop into empty space.
   *
   * ⚠️ No source means no elevation to inherit — the junction takes createJunction's 0 m
   * default. That is the honest value here (canvas Y carries no elevation), and the
   * eventual pipe still prompts for length. Contrast `addPipeToNewJunction`, which DOES
   * inherit, because there it has a real source vertex to read.
   *
   * `position` is expected pre-snapped by the caller (grid on ortho, lattice on iso),
   * exactly as the armed-source finish snaps its terminal — the store does not re-snap.
   */
  startPipeInEmptySpace: (position): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return
    if (!Number.isFinite(position.x) || !Number.isFinite(position.y)) {
      logger.warn('startPipeInEmptySpace: rejected non-finite position')
      return
    }

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const vertexId = nextVertexId(system.vertices)
    // Through the factory so the junction arrives CALCULABLE (schema-required elevation
    // supplied) — a bare {id,type,name} makes calculate throw.
    const newVertex = createVertexAtPosition(vertexId, 'junction', position)

    let updatedSystem: HydraulicSystem = {
      ...system,
      vertices: [...system.vertices, newVertex],
    }
    updatedSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)

    const stale = staleAfterEdit({ updatedSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults })
    set({
      system: updatedSystem,
      undoStack,
      redoStack: [],
      canUndo: true,
      canRedo: false,
      isDirty: true,
      selectedIds: [vertexId],
      // Arm the just-committed junction and start the draw from a clean slate.
      addPipeSource: vertexId,
      draftWaypoints: [],
      ...stale,
      resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale),
      criticalPath: stale.resultsStale ? null : get().criticalPath,
    })
    debouncedSave(updatedSystem, stale.calculationResults, stale.resultsStale)
  },

  /**
   * Finish a pipe in empty space: insert the terminal junction AND the connecting edge
   * in ONE `set()` / one `pushHistory`.
   *
   * ⚠️ Atomic deliberately. `addVertex` and `addEdge` each push their own history frame,
   * so composing them makes one gesture take two Ctrl-Z presses — and the first undo
   * leaves a dangling degree-0 junction on the canvas.
   *
   * ⚠️ The junction INHERITS the source's elevation. `createVertexAtPosition` →
   * `createJunction` hard-defaults `{value: 0, unit: 'm'}`, so on a model whose run sits
   * at 30 m every empty-space endpoint would silently inject a 30 m static-head error —
   * invisible, because canvas Y carries no elevation information.
   */
  addPipeToNewJunction: (sourceId, position, interior): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return
    const source = system.vertices.find(v => v.id === sourceId)
    if (!source) return

    const chain = interior ?? []
    if (chain.some(p => !Number.isFinite(p.x) || !Number.isFinite(p.y))) {
      logger.warn('addPipeToNewJunction: rejected non-finite chain', { sourceId })
      return
    }

    // ⚠️ Inherit the run's phase + zone rather than emitting the model default. The far
    // end here is ALWAYS a bare junction, so a source on a compressible run is refused
    // rather than silently producing a mixed-phase node.
    const phase = resolveNewPipePhase(system, { sourceId, farEndIsBareJunction: true })
    if (!phase.ok) {
      logger.warn('addPipeToNewJunction: refused a mixed-phase connection', { sourceId })
      return
    }

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const claimedIds = new Set<string>()
    const vertexId = nextVertexId(system.vertices, claimedIds)
    claimedIds.add(vertexId)
    const edgeId = nextEdgeId(system.edges, claimedIds)

    const created = createVertexAtPosition(vertexId, 'junction', position) as unknown as Record<string, unknown>
    const inherited = elevationAt(source, 'source')
    const newVertex = inherited ? { ...created, elevation: inherited } : created

    const basis = getRouteBasis(system)
    const route = routeEdge(
      vertexToRouteBox(source.diagram.position, source.type),
      vertexToRouteBox(position, 'junction'),
      { waypoints: chain },
      { points: chain },
      basis,
    )

    // Zone + size default to the model's own so the pipe is calculable; length is left
    // for the user (no safe default — the panel prompts for it).
    const newEdge = {
      id: edgeId,
      name: '',
      connection: { source_id: sourceId, target_id: vertexId },
      ...defaultPipeFields(system),
      type: phase.type,
      ...(phase.fluidZoneId === undefined ? {} : { fluid_zone_id: phase.fluidZoneId }),
      diagram: { points: route.points, sourceHandle: route.sourceHandle, targetHandle: route.targetHandle },
    } as unknown as Record<string, unknown>
    delete newEdge['length']

    let updatedSystem: HydraulicSystem = {
      ...system,
      vertices: [...system.vertices, newVertex] as never,
      edges: [...system.edges, newEdge] as never,
    }
    updatedSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)

    const stale = staleAfterEdit({ updatedSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults })
    set({ system: updatedSystem, undoStack, redoStack: [], canUndo: true, canRedo: false, isDirty: true, selectedIds: [edgeId], promptLengthEdgeId: edgeId, ...stale, resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale), criticalPath: stale.resultsStale ? null : get().criticalPath })
    debouncedSave(updatedSystem, stale.calculationResults, stale.resultsStale)
  },

  /**
   * Tee a drawn pipe onto an existing pipe: split the target at `hit` AND connect the
   * new pipe to the inserted junction.
   *
   * ⚠️ ONE history frame for what is two topological operations. This is why the split
   * lives in the pure `buildSplit` builder rather than inside `splitEdge` — calling
   * `splitEdge` then `addPipeToNewJunction` would push two frames, so a single Ctrl-Z
   * would undo the connection and leave the target severed.
   */
  teeOntoEdge: (sourceId, targetEdgeId, hit, interior): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return
    const source = system.vertices.find(v => v.id === sourceId)
    if (!source) return

    const chain = interior ?? []
    if (chain.some(p => !Number.isFinite(p.x) || !Number.isFinite(p.y))) {
      logger.warn('teeOntoEdge: rejected non-finite chain', { sourceId, targetEdgeId })
      return
    }

    // ⚠️ The tee pipe inherits the TARGET run's phase + zone. Both its ends are real
    // (the inserted junction is degree-3, not a dead-leg), so a gas run propagates
    // properly; a source and target that disagree on phase are refused.
    const phase = resolveNewPipePhase(system, {
      sourceId, targetEdgeId, farEndIsBareJunction: false,
    })
    if (!phase.ok) {
      logger.warn('teeOntoEdge: refused a mixed-phase connection', { sourceId, targetEdgeId })
      return
    }

    // buildSplit enforces the pipe/compressible_pipe target guard and returns null on a
    // duct, so a duct tee is a no-op here rather than a half-applied mutation.
    const split = buildSplit(system, targetEdgeId, 'junction', hit)
    if (!split) return

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)

    // ⚠️ Allocate against the POST-split edge list, or this can hand back an id
    // buildSplit just assigned to one of the halves.
    const claimedIds = new Set<string>([split.edgeAId, split.edgeBId])
    const teeEdgeId = nextEdgeId(split.edges as never, claimedIds)

    const basis = getRouteBasis(system)
    const route = routeEdge(
      vertexToRouteBox(source.diagram.position, source.type),
      vertexToRouteBox(hit.position, 'junction'),
      { waypoints: chain },
      { points: chain },
      basis,
    )
    const teeEdge = {
      id: teeEdgeId,
      name: '',
      connection: { source_id: sourceId, target_id: split.newVertexId },
      ...defaultPipeFields(system),
      type: phase.type,
      ...(phase.fluidZoneId === undefined ? {} : { fluid_zone_id: phase.fluidZoneId }),
      diagram: { points: route.points, sourceHandle: route.sourceHandle, targetHandle: route.targetHandle },
    } as unknown as Record<string, unknown>
    delete teeEdge['length']

    let updatedSystem: HydraulicSystem = {
      ...system,
      vertices: split.vertices as never,
      edges: [...split.edges, teeEdge] as never,
      ...(split.scenarios ? { scenarios: split.scenarios as never } : {}),
    }
    updatedSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)

    const stale = staleAfterEdit({ updatedSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults })
    set({ system: updatedSystem, undoStack, redoStack: [], canUndo: true, canRedo: false, isDirty: true, selectedIds: [teeEdgeId], promptLengthEdgeId: teeEdgeId, ...stale, resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale), criticalPath: stale.resultsStale ? null : get().criticalPath })
    debouncedSave(updatedSystem, stale.calculationResults, stale.resultsStale)
  },

  /**
   * Weld the dragged junction into the drop target: the target survives (id, position,
   * elevation), every edge incident on the dragged one is repointed to it, and the
   * dragged junction is deleted.
   *
   * ⚠️ NOT pure topology. A repointed edge still carries geometry drawn to the OLD
   * position, which would render as a dogleg to nowhere — so every repointed edge is
   * re-routed through the routeEdge facade.
   *
   * ⚠️ A pipe joining the two collapses to target→target. It is dropped, and its id must
   * go into `removedIds` alongside the vertex — pruning only the vertex id (which the
   * repoint branch of dissolveDevice does) orphans that edge's scenario overrides.
   */
  combineJunctions: (targetId, draggedId): void => {
    if (guardEdit()) return
    if (targetId === draggedId) return
    const { system } = get()
    if (!system) return
    const target = system.vertices.find(v => v.id === targetId)
    const dragged = system.vertices.find(v => v.id === draggedId)
    if (!target || !dragged) return
    if (!WELDABLE_VERTEX_TYPES.has(target.type) || !WELDABLE_VERTEX_TYPES.has(dragged.type)) return

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)

    const droppedEdgeIds = system.edges
      .filter(e => {
        const s2 = e.connection.source_id
        const t2 = e.connection.target_id
        return (s2 === draggedId && t2 === targetId) || (s2 === targetId && t2 === draggedId)
      })
      .map(e => e.id)
    const dropped = new Set(droppedEdgeIds)

    const basis = getRouteBasis(system)
    const vertices = system.vertices.filter(v => v.id !== draggedId)
    const byId = new Map(vertices.map(v => [v.id, v]))

    const edges = system.edges.filter(e => !dropped.has(e.id)).map(e => {
      const srcId = e.connection.source_id === draggedId ? targetId : e.connection.source_id
      const tgtId = e.connection.target_id === draggedId ? targetId : e.connection.target_id
      if (srcId === e.connection.source_id && tgtId === e.connection.target_id) return e

      const sv = byId.get(srcId)
      const tv = byId.get(tgtId)
      const repointed = { ...e, connection: { ...e.connection, source_id: srcId, target_id: tgtId } }
      if (!sv || !tv) return repointed

      const chain = ((e.diagram as { points?: Array<{ x: number; y: number }> } | undefined)?.points ?? [])
      const route = routeEdge(
        vertexToRouteBox(sv.diagram.position, sv.type),
        vertexToRouteBox(tv.diagram.position, tv.type),
        { waypoints: chain },
        { points: chain },
        basis,
      )
      return {
        ...repointed,
        diagram: { ...e.diagram, points: route.points, sourceHandle: route.sourceHandle, targetHandle: route.targetHandle },
      }
    })

    const removedIds = new Set<string>([draggedId, ...droppedEdgeIds])
    let updatedSystem: HydraulicSystem = {
      ...system,
      vertices,
      edges: edges as never,
      ...(system.scenarios ? { scenarios: pruneOverridesForRemovedIds(system.scenarios, removedIds) } : {}),
    }
    updatedSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)

    const stale = staleAfterEdit({ updatedSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults })
    set({ system: updatedSystem, undoStack, redoStack: [], canUndo: true, canRedo: false, isDirty: true, selectedIds: [targetId], ...stale, resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale), criticalPath: stale.resultsStale ? null : get().criticalPath })
    debouncedSave(updatedSystem, stale.calculationResults, stale.resultsStale)
  },

  // Persist a drawn isometric pipe as ONE edge carrying the full interior chain.
  // Delegates to addEdge so the whole draw is a single undo entry (no bare-edge
  // intermediate, no second setEdgeWaypoints pass). The chain is already
  // on-bearing (routeIsoChain); the finite check is the whole-action backstop.
  // Read-only is enforced transitively by addEdge's guardEdit.
  addDrawnPipe: (sourceId, targetId, interior): void => {
    const { system } = get()
    if (!system) return
    if (interior.some(p => !Number.isFinite(p.x) || !Number.isFinite(p.y))) {
      logger.warn('addDrawnPipe: rejected non-finite chain', { sourceId, targetId })
      return
    }
    get().addEdge({
      id: nextEdgeId(system.edges),
      type: 'pipe',
      name: '',
      connection: { source_id: sourceId, target_id: targetId },
      // Zone + size default to the model's own so the pipe is calculable; length
      // is left for the user (no safe default — prompted on draw).
      ...defaultPipeFields(system),
      diagram: { sourceHandle: 'right-src', targetHandle: 'left-tgt', points: interior },
    } as unknown as Edge)
  },
  setPendingLibrarySnapshot: (snapshot): void => set({ pendingLibrarySnapshot: snapshot }),
  setViewMode: (mode): void => set({
    viewMode: mode,
    interactionMode: 'select',
    addVertexType: null,
    addPipeSource: null,
  }),
  setSchematicSnapshot: (snapshot): void => set({ schematicSnapshot: snapshot }),
  setScenarioDockOpen: (open): void => {
    writeDockOpen(open)
    set(open
      ? { scenarioDockOpen: true, viewMode: '2d', interactionMode: 'select', addVertexType: null, addPipeSource: null }
      : { scenarioDockOpen: false })
  },
  toggleScenarioDock: (): void => { get().setScenarioDockOpen(!get().scenarioDockOpen) },
  setScenarioDockHeight: (px): void => {
    const h = clampDockHeight(px)
    writeDockHeight(h)
    set({ scenarioDockHeight: h })
  },

  setPathTraceStartVertex: (id): void => set({ pathTraceStartVertex: id }),

  clearPathTrace: (): void => set({
    pathTraceStartVertex: null,
    pathTraceResult: null,
    pathTraceSource: null,
    interactionMode: 'select',
  }),

  // -- Diagram Element Mutations ---------------------------------

  addDiagramElement: (element): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) {
      logger.warn('results-schedule addDiagramElement called with no system loaded — no-op. Load a model first.')
      return
    }
    const existing = system.diagram_elements ?? []
    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    set({
      system: { ...system, diagram_elements: [...existing, element] },
      undoStack,
      redoStack: [],
      canUndo: true,
      canRedo: false,
      isDirty: true,
      selectedIds: [element.id],
    })
    debouncedSave(get().system!, get().calculationResults, get().resultsStale)
  },

  updateDiagramElement: (id, patch): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return
    const existing = system.diagram_elements ?? []
    const next = existing.map(el =>
      el.id === id ? ({ ...el, ...patch } as DiagramElement) : el
    )
    // If nothing changed (rare — e.g. patching to identical values), bail to avoid
    // spamming the save queue with no-op updates during drag.
    if (next === existing) return
    set({
      system: { ...system, diagram_elements: next },
      isDirty: true,
    })
    debouncedSave(get().system!, get().calculationResults, get().resultsStale)
  },

  removeDiagramElement: (id): void => {
    if (guardEdit()) return
    const { system, selectedIds } = get()
    if (!system) return
    const existing = system.diagram_elements ?? []
    const next = existing.filter(el => el.id !== id)
    if (next.length === existing.length) return // nothing to remove
    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    set({
      system: { ...system, diagram_elements: next },
      undoStack,
      redoStack: [],
      canUndo: true,
      canRedo: false,
      isDirty: true,
      selectedIds: selectedIds.filter(sid => sid !== id),
    })
    debouncedSave(get().system!, get().calculationResults, get().resultsStale)
  },

  // -- Vertex Mutations -----------------------------------------

  addVertex: (vertex): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const updatedVertices = [...system.vertices, vertex]
    let updatedSystem: HydraulicSystem = {
      ...system,
      vertices: updatedVertices,
    }

    updatedSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)

    const stale = staleAfterEdit({ updatedSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults })
    set({
      system: updatedSystem,
      undoStack,
      redoStack: [],
      canUndo: true,
      canRedo: false,
      isDirty: true,
      selectedIds: [vertex.id],
      ...stale,
      resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale),
      criticalPath: stale.resultsStale ? null : get().criticalPath,
    })
    debouncedSave(updatedSystem, stale.calculationResults, stale.resultsStale)
  },

  updateVertex: (id, updates): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const updatedVertices = system.vertices.map((v) =>
      v.id === id ? { ...v, ...updates } as Vertex : v
    )
    let updatedSystem: HydraulicSystem = { ...system, vertices: updatedVertices }

    updatedSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)

    const stale = staleAfterEdit({ updatedSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults })
    set({
      system: updatedSystem,
      undoStack,
      redoStack: [],
      canUndo: true,
      canRedo: false,
      isDirty: true,
      ...stale,
      resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale),
      criticalPath: stale.resultsStale ? null : get().criticalPath,
    })
    debouncedSave(updatedSystem, stale.calculationResults, stale.resultsStale)
  },

  changeVertexType: (id, newType): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return
    const existing = system.vertices.find(v => v.id === id)
    if (!existing || existing.type === newType) return

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    // Create a fresh vertex of the new type, preserving id/position/name/note.
    // The vertex union has many member types; we treat the fresh instance as a
    // string-keyed record while we copy fields across — the cast back to Vertex
    // happens once the field-level mutation is done.
    const fresh = createVertexAtPosition(id, newType, existing.diagram.position) as Record<string, unknown>
    fresh['name'] = existing.name
    if (existing.note) fresh['note'] = existing.note

    // Carry over elevation if both types have it
    const oldElev = (existing as Partial<{ elevation: { value: number; unit: string } }>).elevation
    if (oldElev && 'elevation' in fresh) {
      fresh['elevation'] = oldElev
    }

    // Match units on all ValueWithUnit fields to the existing vertex's unit system
    // (e.g., if elevation was in feet, liquid_level/surface_pressure should use compatible units)
    if (oldElev) {
      const lengthUnit = oldElev.unit // e.g., 'ft' or 'm'
      const pressureUnit = lengthUnit === 'm' ? 'Pa' : 'psi'
      for (const key of Object.keys(fresh)) {
        const val = fresh[key] as { value: number; unit: string } | undefined
        if (val && typeof val === 'object' && 'value' in val && 'unit' in val && key !== 'elevation') {
          // Remap length-like units
          if (val.unit === 'm' && lengthUnit !== 'm') fresh[key] = { ...val, unit: lengthUnit }
          else if (val.unit === 'ft' && lengthUnit !== 'ft') fresh[key] = { ...val, unit: lengthUnit }
          // Remap pressure units
          else if (val.unit === 'Pa' && pressureUnit !== 'Pa') fresh[key] = { value: convertValue(val.value, 'Pa', pressureUnit), unit: pressureUnit }
          else if (val.unit === 'psi' && pressureUnit !== 'psi') fresh[key] = { value: convertValue(val.value, 'psi', pressureUnit), unit: pressureUnit }
        }
      }
    }

    const updatedVertices = system.vertices.map(v => v.id === id ? fresh as unknown as Vertex : v)
    let updatedSystem: HydraulicSystem = { ...system, vertices: updatedVertices }

    updatedSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)

    const stale = staleAfterEdit({ updatedSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults })
    set({
      system: updatedSystem,
      undoStack,
      redoStack: [],
      canUndo: true,
      canRedo: false,
      isDirty: true,
      ...stale,
      resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale),
      criticalPath: stale.resultsStale ? null : get().criticalPath,
    })
    debouncedSave(updatedSystem, stale.calculationResults, stale.resultsStale)
  },

  updateVertexProperty: (id, property, value, scenarioId): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const route = routeFieldEdit(system, 'vertex', id, property, value, scenarioId)
    let updatedSystem: HydraulicSystem
    if (route.kind === 'override') {
      updatedSystem = { ...system, scenarios: route.scenarios }
    } else {
      const updatedVertices = system.vertices.map((v) => {
        if (v.id !== id) return v
        const updated = { ...v, [property]: value } as Vertex
        if (property === 'name') {
          (updated as Vertex & { name: string }).name = String(value)
        }
        return updated
      })
      updatedSystem = { ...system, vertices: updatedVertices }
    }

    updatedSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)

    // Explicit-column (scenarios grid) write: key staleness off the EDITED node, not active_id.
    // Every existing caller passes no scenarioId and falls through to the unchanged active path,
    // so form/canvas behavior is byte-identical.
    if (scenarioId !== undefined && updatedSystem.scenarios) {
      const editedScenarioId = route.kind === 'override' ? scenarioId : DEFAULT_BASE_SCENARIO_ID
      const { status: scenarioStatus, invalidatedIds } = markScenariosStale(updatedSystem.scenarios, get().scenarioStatus, editedScenarioId)
      const scenarioResults = { ...get().scenarioResults }
      for (const invId of invalidatedIds) delete scenarioResults[invId]
      const projection = projectActiveScenario({ scenarios: updatedSystem.scenarios, scenarioResults, scenarioStatus, current: get().calculationResults })
      set({
        system: updatedSystem, undoStack, redoStack: [], canUndo: true, canRedo: false, isDirty: true,
        scenarioResults, scenarioStatus, ...projection,
        resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, projection.resultsStale),
        criticalPath: projection.resultsStale ? null : get().criticalPath,
      })
      debouncedSave(updatedSystem, projection.calculationResults, projection.resultsStale)
      return
    }

    const stale = staleAfterEdit({ updatedSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults })
    set({
      system: updatedSystem,
      undoStack,
      redoStack: [],
      canUndo: true,
      canRedo: false,
      isDirty: true,
      ...stale,
      resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale),
      criticalPath: stale.resultsStale ? null : get().criticalPath,
    })
    debouncedSave(updatedSystem, stale.calculationResults, stale.resultsStale)
  },

  bulkUpdateVertexProperty: (ids, property, value): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system || ids.length === 0) return

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    let scenarios = system.scenarios
    let vertices = system.vertices
    let wroteBase = false
    for (const id of ids) {
      const route = routeFieldEdit({ ...system, scenarios }, 'vertex', id, property, value)
      if (route.kind === 'override') { scenarios = route.scenarios }
      else { wroteBase = true; vertices = vertices.map((v) => (v.id === id ? ({ ...v, [property]: value } as Vertex) : v)) }
    }
    let updatedSystem: HydraulicSystem = { ...system, ...(wroteBase ? { vertices } : {}), ...(scenarios ? { scenarios } : {}) }

    updatedSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)

    const stale = staleAfterEdit({ updatedSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults })
    set({
      system: updatedSystem,
      undoStack,
      redoStack: [],
      canUndo: true,
      canRedo: false,
      isDirty: true,
      ...stale,
      resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale),
      criticalPath: stale.resultsStale ? null : get().criticalPath,
    })
    debouncedSave(updatedSystem, stale.calculationResults, stale.resultsStale)
  },

  bulkUpdateEdgeProperty: (ids, property, value): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system || ids.length === 0) return

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    let scenarios = system.scenarios
    let edges = system.edges
    let wroteBase = false
    for (const id of ids) {
      const route = routeFieldEdit({ ...system, scenarios }, 'edge', id, property, value)
      if (route.kind === 'override') { scenarios = route.scenarios }
      else { wroteBase = true; edges = edges.map((e) => (e.id === id ? applyEdgeField(e, property, value) : e)) }
    }
    let updatedSystem: HydraulicSystem = { ...system, ...(wroteBase ? { edges } : {}), ...(scenarios ? { scenarios } : {}) }

    updatedSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)

    const stale = staleAfterEdit({ updatedSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults })
    set({
      system: updatedSystem,
      undoStack,
      redoStack: [],
      canUndo: true,
      canRedo: false,
      isDirty: true,
      ...stale,
      resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale),
      criticalPath: stale.resultsStale ? null : get().criticalPath,
    })
    debouncedSave(updatedSystem, stale.calculationResults, stale.resultsStale)
  },

  removeVertex: (id): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    // Remove vertex and any connected edges
    const updatedVertices = system.vertices.filter((v): boolean => v.id !== id)
    const updatedEdges = system.edges.filter(
      (e): boolean => e.connection.source_id !== id && e.connection.target_id !== id
    )
    const removedEdgeIds = system.edges
      .filter((e) => e.connection.source_id === id || e.connection.target_id === id)
      .map((e) => e.id)
    const removedIds = new Set<string>([id, ...removedEdgeIds])
    let updatedSystem: HydraulicSystem = {
      ...system,
      vertices: updatedVertices,
      edges: updatedEdges,
      ...(system.scenarios ? { scenarios: pruneOverridesForRemovedIds(system.scenarios, removedIds) } : {}),
    }

    updatedSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)

    const stale = staleAfterEdit({ updatedSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults })
    set({
      system: updatedSystem,
      undoStack,
      redoStack: [],
      canUndo: true,
      canRedo: false,
      isDirty: true,
      selectedIds: get().selectedIds.filter(sid => sid !== id),
      ...stale,
      resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale),
      criticalPath: stale.resultsStale ? null : get().criticalPath,
    })
    debouncedSave(updatedSystem, stale.calculationResults, stale.resultsStale)
  },

  // -- Edge Mutations -------------------------------------------

  addEdge: (edge): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return

    // A Pressure Boundary is a fixed-grade terminal: a second connection makes
    // it mathematically ambiguous (the solver would silently treat it as an
    // inline pressure node). Reject a new user/AI edge that would give a
    // pressure_boundary more than one connection. Import/paste bypass addEdge
    // and are handled by a TopologyAnalyzer warning instead (existing models).
    for (const endpointId of [edge.connection.source_id, edge.connection.target_id]) {
      const vertex = system.vertices.find((v): boolean => v.id === endpointId)
      if (vertex?.type === 'pressure_boundary' && getConnectedEdges(endpointId, system.edges).length >= 1) {
        toast.error('Pressure Boundary allows only one connection')
        return
      }
    }

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const updatedEdges = [...system.edges, edge]
    let updatedSystem: HydraulicSystem = {
      ...system,
      edges: updatedEdges,
    }

    updatedSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)

    const stale = staleAfterEdit({ updatedSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults })
    set({
      system: updatedSystem,
      undoStack,
      redoStack: [],
      canUndo: true,
      canRedo: false,
      isDirty: true,
      selectedIds: [edge.id],
      // A freshly-drawn pipe has no length (no safe default). Flag it so the
      // property panel opens focused on the length field — a prompt, not a
      // silent zero. Non-pipe or already-lengthed edges (paste/import) don't prompt.
      promptLengthEdgeId: edge.type === 'pipe' && (edge as Partial<{ length: unknown }>).length === undefined
        ? edge.id
        : null,
      addPipeSource: null,
      ...stale,
      resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale),
      criticalPath: stale.resultsStale ? null : get().criticalPath,
    })
    debouncedSave(updatedSystem, stale.calculationResults, stale.resultsStale)
  },

  updateEdge: (id, updates): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const updatedEdges = system.edges.map((e): { id: string; diagram: { points: Array<{ x: number; y: number; }>; sourceHandle?: string | undefined; targetHandle?: string | undefined; }; name: string; connection: { source_id: string; target_id: string; }; type: "pipe"; fluid_zone_id: string; pipe_specification_id: string; pipe_size_id: string; length: { value: number; unit: string; }; is_closed: boolean; is_oneway: boolean; note?: string | undefined; design_status?: "undesigned" | "sizing" | "designed" | undefined; custom_field_values?: Record<string, string | number | boolean> | undefined; layer_id?: string | undefined; manually_routed?: boolean | undefined; calc?: { flow?: { value: number; unit: string; } | undefined; pressure_drop?: { value: number; unit: string; } | undefined; } | undefined; fittings?: Array<{ fittingId: string; count: number; customK?: number | undefined; reducedDiameter?: number | undefined; angle?: number | undefined; }> | undefined; heat_transfer_settings_id?: string | undefined; design_factor?: number | undefined; valve_closure_time?: { value: number; unit: string; } | undefined; wall_thickness?: { value: number; unit: string; } | undefined; outer_diameter?: { value: number; unit: string; } | undefined; flow_area?: { value: number; unit: string; } | undefined; } | { id: string; diagram: { points: Array<{ x: number; y: number; }>; sourceHandle?: string | undefined; targetHandle?: string | undefined; }; name: string; connection: { source_id: string; target_id: string; }; type: "compressible_pipe"; fluid_zone_id: string; pipe_specification_id: string; pipe_size_id: string; length: { value: number; unit: string; }; is_closed: boolean; is_oneway: boolean; note?: string | undefined; design_status?: "undesigned" | "sizing" | "designed" | undefined; custom_field_values?: Record<string, string | number | boolean> | undefined; layer_id?: string | undefined; manually_routed?: boolean | undefined; calc?: { flow?: { value: number; unit: string; } | undefined; pressure_drop?: { value: number; unit: string; } | undefined; } | undefined; fittings?: Array<{ fittingId: string; count: number; customK?: number | undefined; reducedDiameter?: number | undefined; angle?: number | undefined; }> | undefined; heat_transfer_settings_id?: string | undefined; design_factor?: number | undefined; valve_closure_time?: { value: number; unit: string; } | undefined; wall_thickness?: { value: number; unit: string; } | undefined; outer_diameter?: { value: number; unit: string; } | undefined; flow_area?: { value: number; unit: string; } | undefined; } | { id: string; diagram: { points: Array<{ x: number; y: number; }>; sourceHandle?: string | undefined; targetHandle?: string | undefined; }; name: string; connection: { source_id: string; target_id: string; }; type: "duct"; geometry: { shape: "circular"; diameter: { value: number; unit: string; }; } | { shape: "rectangular"; width: { value: number; unit: string; }; height: { value: number; unit: string; }; }; is_closed: boolean; note?: string | undefined; design_status?: "undesigned" | "sizing" | "designed" | undefined; custom_field_values?: Record<string, string | number | boolean> | undefined; layer_id?: string | undefined; manually_routed?: boolean | undefined; calc?: { flow?: { value: number; unit: string; } | undefined; pressure_drop?: { value: number; unit: string; } | undefined; } | undefined; fluid_zone_id?: string | undefined; length?: { value: number; unit: string; } | undefined; roughness?: { value: number; unit: string; } | undefined; loss_coefficient?: number | undefined; design_factor?: number | undefined; flow_area?: { value: number; unit: string; } | undefined; hydraulic_diameter?: { value: number; unit: string; } | undefined; } =>
      e.id === id ? { ...e, ...updates } as Edge : e
    )
    let updatedSystem: HydraulicSystem = { ...system, edges: updatedEdges }

    updatedSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)

    const stale = staleAfterEdit({ updatedSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults })
    set({
      system: updatedSystem,
      undoStack,
      redoStack: [],
      canUndo: true,
      canRedo: false,
      isDirty: true,
      ...stale,
      resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale),
      criticalPath: stale.resultsStale ? null : get().criticalPath,
    })
    debouncedSave(updatedSystem, stale.calculationResults, stale.resultsStale)
  },

  setEdgeColor: (id, color): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return
    if (!system.edges.some((e): boolean => e.id === id)) return

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const updatedEdges = system.edges.map((e): Edge => {
      if (e.id !== id) return e
      if (color === null) {
        // Clear the override — shallow merge can't drop a key, so copy-and-delete.
        const rest: Record<string, unknown> = { ...e }
        delete rest['color']
        return rest as unknown as Edge
      }
      return { ...e, color } as Edge
    })
    const updatedSystem: HydraulicSystem = { ...system, edges: updatedEdges }
    // Color is presentation-only (stripped from the version diff, #625) and not a
    // solver input — recoloring must NOT invalidate a valid calculation.
    set({ system: updatedSystem, undoStack, redoStack: [], canUndo: true, canRedo: false, isDirty: true })
    debouncedSave(updatedSystem, get().calculationResults, get().resultsStale)
  },

  setVertexColor: (id, color): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return
    if (!system.vertices.some((v): boolean => v.id === id)) return

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const updatedVertices = system.vertices.map((v): Vertex => {
      if (v.id !== id) return v
      if (color === null) {
        // Clear the override — shallow merge can't drop a key, so copy-and-delete.
        const rest: Record<string, unknown> = { ...v }
        delete rest['color']
        return rest as unknown as Vertex
      }
      return { ...v, color } as Vertex
    })
    const updatedSystem: HydraulicSystem = { ...system, vertices: updatedVertices }
    // Presentation-only (stripped from the version diff, #625) and not a solver
    // input — recoloring must NOT invalidate a valid calculation.
    set({ system: updatedSystem, undoStack, redoStack: [], canUndo: true, canRedo: false, isDirty: true })
    debouncedSave(updatedSystem, get().calculationResults, get().resultsStale)
  },

  updateEdgeProperty: (id, property, value): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const route = routeFieldEdit(system, 'edge', id, property, value)
    let updatedSystem: HydraulicSystem
    if (route.kind === 'override') {
      updatedSystem = { ...system, scenarios: route.scenarios }
    } else {
      const updatedEdges = system.edges.map((e) => (e.id === id ? applyEdgeField(e, property, value) : e))
      updatedSystem = { ...system, edges: updatedEdges }
    }

    updatedSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)

    const stale = staleAfterEdit({ updatedSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults })
    set({
      system: updatedSystem,
      undoStack,
      redoStack: [],
      canUndo: true,
      canRedo: false,
      isDirty: true,
      ...stale,
      resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale),
      criticalPath: stale.resultsStale ? null : get().criticalPath,
    })
    debouncedSave(updatedSystem, stale.calculationResults, stale.resultsStale)
  },

  removeEdge: (id): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const updatedEdges = system.edges.filter((e): boolean => e.id !== id)
    let updatedSystem: HydraulicSystem = {
      ...system,
      edges: updatedEdges,
      ...(system.scenarios ? { scenarios: pruneOverridesForRemovedIds(system.scenarios, new Set([id])) } : {}),
    }

    updatedSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)

    const stale = staleAfterEdit({ updatedSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults })
    set({
      system: updatedSystem,
      undoStack,
      redoStack: [],
      canUndo: true,
      canRedo: false,
      isDirty: true,
      selectedIds: get().selectedIds.filter(sid => sid !== id),
      ...stale,
      resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale),
      criticalPath: stale.resultsStale ? null : get().criticalPath,
    })
    debouncedSave(updatedSystem, stale.calculationResults, stale.resultsStale)
  },

  // -- Reference Data Mutations ------------------------------------

  addFluidZone: (zone): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    // ⚠️ NORMALISED HERE, at the store boundary, not in the editor that calls it.
    // FlowAssist dispatches these actions directly (write-tools.ts:712 ->
    // model-store.ts:5475) and its tool schema does not require a nested `unit`,
    // so `{ density: { value: 62.3 } }` would reach the solver and be read as
    // 62.3 kg/m³. Normalising in FluidZoneForm covered the human writer and left
    // the machine one open. PIPE-142.
    const zoneAdd = normalizeSystemFluidZonesToSI({ fluid_zones: [zone] })
    if (zoneAdd.errors.length > 0) {
      logger.error('Refusing to add a fluid zone whose units cannot be normalised', undefined, { errors: zoneAdd.errors })
      return
    }
    const updatedSystem: HydraulicSystem = {
      ...system,
      fluid_zones: [...system.fluid_zones, (zoneAdd.system.fluid_zones[0] ?? zone) as FluidZone],
    }

    // ⚠️ Reference data IS solver input. These seven actions used to skip both the clearing
    // and `staleAfterEdit` entirely, so halving `max_iterations` or editing a referenced
    // fluid's density left the previous run's numbers displayed and re-saved them as
    // current — PIPE-66 for the inputs that do not live on an element (PIPE-77).
    //
    // ⚠️ Staleness is attributed to BASE, not to `active_id`. A fluid, a pipe specification
    // and the calculation settings are model-wide: they invalidate every scenario. Letting
    // `staleAfterEdit` default to the active scenario cleared the document blocks globally
    // but marked only the active subtree, leaving a non-active Base 'fresh' over a stale
    // cached output. Base cascades to every node, and this matches how `routeFieldEdit`
    // already routes every other non-overridable field.
    const nextSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)
    const stale = staleAfterEdit({ updatedSystem: nextSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults, editedScenarioId: DEFAULT_BASE_SCENARIO_ID })
    set({
      system: nextSystem,
      undoStack,
      redoStack: [],
      canUndo: true,
      canRedo: false,
      isDirty: true,
      ...stale,
      resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale),
      criticalPath: stale.resultsStale ? null : get().criticalPath,
    })
    debouncedSave(nextSystem, stale.calculationResults, stale.resultsStale)
  },

  updateFluidZone: (id, updates): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return

    // ⚠️ Same boundary as addFluidZone, for the same reason: FlowAssist writes
    // here directly and may supply a value with no unit at all.
    const existing = system.fluid_zones.find((z): boolean => z.id === id)
    const merged = existing ? { ...existing, ...updates } : undefined
    const zoneUpdate = merged ? normalizeSystemFluidZonesToSI({ fluid_zones: [merged] }) : undefined
    if (zoneUpdate && zoneUpdate.errors.length > 0) {
      logger.error('Refusing to update a fluid zone with units that cannot be normalised', undefined, { errors: zoneUpdate.errors })
      return
    }
    const mergedZone = zoneUpdate?.system.fluid_zones[0] as FluidZone | undefined

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const updatedSystem: HydraulicSystem = {
      ...system,
      fluid_zones: system.fluid_zones.map((z): { id: string; name: string; fluid_name: string; temperature: { value: number; unit: string; }; density: { value: number; unit: string; }; viscosity: { value: number; unit: string; }; note?: string | undefined; specific_heat?: { value: number; unit: string; } | undefined; vapor_pressure?: { value: number; unit: string; } | undefined; bulk_modulus?: { value: number; unit: string; } | undefined; surface_tension?: { value: number; unit: string; } | undefined; specific_heat_ratio?: number | undefined; molecular_weight?: number | undefined; compressibility_factor?: number | undefined; critical_temperature?: { value: number; unit: string; } | undefined; critical_pressure?: { value: number; unit: string; } | undefined; acentric_factor?: number | undefined; components?: Array<{ fluid_id: string; mole_fraction: number; }> | undefined; pressure?: { value: number; unit: string; } | undefined; fluid_id?: string | undefined; fluid_source?: "custom" | "database" | "mixture" | undefined; mixture_id?: string | undefined; mixture_ratio?: number | undefined; } =>
        z.id === id ? (mergedZone ?? z) : z
      ),
    }

    // ⚠️ Reference data IS solver input. These seven actions used to skip both the clearing
    // and `staleAfterEdit` entirely, so halving `max_iterations` or editing a referenced
    // fluid's density left the previous run's numbers displayed and re-saved them as
    // current — PIPE-66 for the inputs that do not live on an element (PIPE-77).
    //
    // ⚠️ Staleness is attributed to BASE, not to `active_id`. A fluid, a pipe specification
    // and the calculation settings are model-wide: they invalidate every scenario. Letting
    // `staleAfterEdit` default to the active scenario cleared the document blocks globally
    // but marked only the active subtree, leaving a non-active Base 'fresh' over a stale
    // cached output. Base cascades to every node, and this matches how `routeFieldEdit`
    // already routes every other non-overridable field.
    const nextSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)
    const stale = staleAfterEdit({ updatedSystem: nextSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults, editedScenarioId: DEFAULT_BASE_SCENARIO_ID })
    set({
      system: nextSystem,
      undoStack,
      redoStack: [],
      canUndo: true,
      canRedo: false,
      isDirty: true,
      ...stale,
      resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale),
      criticalPath: stale.resultsStale ? null : get().criticalPath,
    })
    debouncedSave(nextSystem, stale.calculationResults, stale.resultsStale)
  },

  removeFluidZone: (id): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const updatedSystem: HydraulicSystem = {
      ...system,
      fluid_zones: system.fluid_zones.filter((z): boolean => z.id !== id),
    }

    // ⚠️ Reference data IS solver input. These seven actions used to skip both the clearing
    // and `staleAfterEdit` entirely, so halving `max_iterations` or editing a referenced
    // fluid's density left the previous run's numbers displayed and re-saved them as
    // current — PIPE-66 for the inputs that do not live on an element (PIPE-77).
    //
    // ⚠️ Staleness is attributed to BASE, not to `active_id`. A fluid, a pipe specification
    // and the calculation settings are model-wide: they invalidate every scenario. Letting
    // `staleAfterEdit` default to the active scenario cleared the document blocks globally
    // but marked only the active subtree, leaving a non-active Base 'fresh' over a stale
    // cached output. Base cascades to every node, and this matches how `routeFieldEdit`
    // already routes every other non-overridable field.
    const nextSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)
    const stale = staleAfterEdit({ updatedSystem: nextSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults, editedScenarioId: DEFAULT_BASE_SCENARIO_ID })
    set({
      system: nextSystem,
      undoStack,
      redoStack: [],
      canUndo: true,
      canRedo: false,
      isDirty: true,
      ...stale,
      resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale),
      criticalPath: stale.resultsStale ? null : get().criticalPath,
    })
    debouncedSave(nextSystem, stale.calculationResults, stale.resultsStale)
  },

  addPipeSpecification: (spec): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const updatedSystem: HydraulicSystem = {
      ...system,
      pipe_specifications: [...system.pipe_specifications, spec],
    }

    // ⚠️ Reference data IS solver input. These seven actions used to skip both the clearing
    // and `staleAfterEdit` entirely, so halving `max_iterations` or editing a referenced
    // fluid's density left the previous run's numbers displayed and re-saved them as
    // current — PIPE-66 for the inputs that do not live on an element (PIPE-77).
    //
    // ⚠️ Staleness is attributed to BASE, not to `active_id`. A fluid, a pipe specification
    // and the calculation settings are model-wide: they invalidate every scenario. Letting
    // `staleAfterEdit` default to the active scenario cleared the document blocks globally
    // but marked only the active subtree, leaving a non-active Base 'fresh' over a stale
    // cached output. Base cascades to every node, and this matches how `routeFieldEdit`
    // already routes every other non-overridable field.
    const nextSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)
    const stale = staleAfterEdit({ updatedSystem: nextSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults, editedScenarioId: DEFAULT_BASE_SCENARIO_ID })
    set({
      system: nextSystem,
      undoStack,
      redoStack: [],
      canUndo: true,
      canRedo: false,
      isDirty: true,
      ...stale,
      resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale),
      criticalPath: stale.resultsStale ? null : get().criticalPath,
    })
    debouncedSave(nextSystem, stale.calculationResults, stale.resultsStale)
  },

  updatePipeSpecification: (id, updates): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const updatedSystem: HydraulicSystem = {
      ...system,
      pipe_specifications: system.pipe_specifications.map((s): { id: string; name: string; material: string; metric: boolean; roughness: { value: number; unit: string; }; schedule: string; standard: string; standard_version: string; sizes: Array<{ id: string; name: string; inner_diameter: { value: number; unit: string; }; }>; note?: string | undefined; modulus_of_elasticity?: { value: number; unit: string; } | undefined; thermal_conductivity?: { value: number; unit: string; } | undefined; hazen_williams_C?: number | undefined; percent_degradation?: number | undefined; } =>
        s.id === id ? { ...s, ...updates } : s
      ),
    }

    // ⚠️ Reference data IS solver input. These seven actions used to skip both the clearing
    // and `staleAfterEdit` entirely, so halving `max_iterations` or editing a referenced
    // fluid's density left the previous run's numbers displayed and re-saved them as
    // current — PIPE-66 for the inputs that do not live on an element (PIPE-77).
    //
    // ⚠️ Staleness is attributed to BASE, not to `active_id`. A fluid, a pipe specification
    // and the calculation settings are model-wide: they invalidate every scenario. Letting
    // `staleAfterEdit` default to the active scenario cleared the document blocks globally
    // but marked only the active subtree, leaving a non-active Base 'fresh' over a stale
    // cached output. Base cascades to every node, and this matches how `routeFieldEdit`
    // already routes every other non-overridable field.
    const nextSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)
    const stale = staleAfterEdit({ updatedSystem: nextSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults, editedScenarioId: DEFAULT_BASE_SCENARIO_ID })
    set({
      system: nextSystem,
      undoStack,
      redoStack: [],
      canUndo: true,
      canRedo: false,
      isDirty: true,
      ...stale,
      resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale),
      criticalPath: stale.resultsStale ? null : get().criticalPath,
    })
    debouncedSave(nextSystem, stale.calculationResults, stale.resultsStale)
  },

  removePipeSpecification: (id): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const updatedSystem: HydraulicSystem = {
      ...system,
      pipe_specifications: system.pipe_specifications.filter((s): boolean => s.id !== id),
    }

    // ⚠️ Reference data IS solver input. These seven actions used to skip both the clearing
    // and `staleAfterEdit` entirely, so halving `max_iterations` or editing a referenced
    // fluid's density left the previous run's numbers displayed and re-saved them as
    // current — PIPE-66 for the inputs that do not live on an element (PIPE-77).
    //
    // ⚠️ Staleness is attributed to BASE, not to `active_id`. A fluid, a pipe specification
    // and the calculation settings are model-wide: they invalidate every scenario. Letting
    // `staleAfterEdit` default to the active scenario cleared the document blocks globally
    // but marked only the active subtree, leaving a non-active Base 'fresh' over a stale
    // cached output. Base cascades to every node, and this matches how `routeFieldEdit`
    // already routes every other non-overridable field.
    const nextSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)
    const stale = staleAfterEdit({ updatedSystem: nextSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults, editedScenarioId: DEFAULT_BASE_SCENARIO_ID })
    set({
      system: nextSystem,
      undoStack,
      redoStack: [],
      canUndo: true,
      canRedo: false,
      isDirty: true,
      ...stale,
      resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale),
      criticalPath: stale.resultsStale ? null : get().criticalPath,
    })
    debouncedSave(nextSystem, stale.calculationResults, stale.resultsStale)
  },

  updateCalculationSettings: (partial): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const updatedSystem: HydraulicSystem = {
      ...system,
      calculation_settings: { ...system.calculation_settings, ...partial },
    }

    // ⚠️ Reference data IS solver input. These seven actions used to skip both the clearing
    // and `staleAfterEdit` entirely, so halving `max_iterations` or editing a referenced
    // fluid's density left the previous run's numbers displayed and re-saved them as
    // current — PIPE-66 for the inputs that do not live on an element (PIPE-77).
    //
    // ⚠️ Staleness is attributed to BASE, not to `active_id`. A fluid, a pipe specification
    // and the calculation settings are model-wide: they invalidate every scenario. Letting
    // `staleAfterEdit` default to the active scenario cleared the document blocks globally
    // but marked only the active subtree, leaving a non-active Base 'fresh' over a stale
    // cached output. Base cascades to every node, and this matches how `routeFieldEdit`
    // already routes every other non-overridable field.
    const nextSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)
    const stale = staleAfterEdit({ updatedSystem: nextSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults, editedScenarioId: DEFAULT_BASE_SCENARIO_ID })
    set({
      system: nextSystem,
      undoStack,
      redoStack: [],
      canUndo: true,
      canRedo: false,
      isDirty: true,
      ...stale,
      resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale),
      criticalPath: stale.resultsStale ? null : get().criticalPath,
    })
    debouncedSave(nextSystem, stale.calculationResults, stale.resultsStale)
  },

  updateDesignConstraints: (constraints): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const existing = system.design_constraints ?? {}
    const updatedSystem: HydraulicSystem = {
      ...system,
      design_constraints: { ...existing, ...constraints },
    } as HydraulicSystem

    // Recompute auto-size suggestions with the new constraints
    const calcResults = get().calculationResults
    const autoSizeSuggestions = calcResults?.status === 'success'
      ? computeAutoSizeSuggestions(updatedSystem, calcResults)
      : []

    set({
      system: updatedSystem,
      undoStack,
      redoStack: [],
      canUndo: true,
      canRedo: false,
      isDirty: true,
      autoSizeSuggestions,
      dismissedSuggestionIds: [],
    })
    debouncedSave(updatedSystem, get().calculationResults, get().resultsStale)
  },

  // -- Bulk Operations ------------------------------------------

  removeSelectedItems: (): void => {
    if (guardEdit()) return
    const { system, selectedIds } = get()
    if (!system || selectedIds.length === 0) return

    // ⚠️ Deleting a lone junction is the INVERSE OF THE SPLIT that created it, so it hands
    // off to `dissolveDevice`: rejoin the pipes when they can merge, plain-delete when they
    // can't. A junction carries no equipment — it IS the break — so "delete it and its
    // pipes" is never what the user means, unlike a pump or valve where the context menu
    // deliberately offers both readings.
    //
    // No mergeability test here: `dissolveDevice` already falls back to exactly the cascade
    // this function would have performed, so checking first would only duplicate it.
    // Restricted to a single-vertex selection — a multi-select that includes the pipes
    // plainly does mean "remove these too".
    const lone = selectedIds.length === 1 ? selectedIds[0]! : null
    if (lone !== null && system.vertices.find(v => v.id === lone)?.type === 'junction') {
      get().dissolveDevice(lone)
      return
    }

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const selectedSet = new Set(selectedIds)

    const updatedVertices = system.vertices.filter(v => !selectedSet.has(v.id))

    const removedVertexIds = new Set(
      system.vertices.filter(v => selectedSet.has(v.id)).map(v => v.id)
    )
    const updatedEdges = system.edges.filter(e =>
      !selectedSet.has(e.id) &&
      !removedVertexIds.has(e.connection.source_id) &&
      !removedVertexIds.has(e.connection.target_id)
    )

    const updatedDiagramElements = (system.diagram_elements ?? []).filter(el => !selectedSet.has(el.id))

    // Removed element ids = removed vertices + every edge dropped (directly
    // selected or cascade-deleted with its vertex), derived as the complement
    // of the surviving edges.
    const survivingEdgeIds = new Set(updatedEdges.map(e => e.id))
    const removedEdgeIds = system.edges.filter(e => !survivingEdgeIds.has(e.id)).map(e => e.id)
    const removedIds = new Set<string>([...removedVertexIds, ...removedEdgeIds])

    let updatedSystem: HydraulicSystem = { ...system, vertices: updatedVertices, edges: updatedEdges, diagram_elements: updatedDiagramElements,
      ...(system.scenarios ? { scenarios: pruneOverridesForRemovedIds(system.scenarios, removedIds) } : {}) }

    updatedSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)

    const stale = staleAfterEdit({ updatedSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults })
    set({
      system: updatedSystem,
      undoStack,
      redoStack: [],
      canUndo: true,
      canRedo: false,
      isDirty: true,
      selectedIds: [],
      ...stale,
      resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale),
      criticalPath: stale.resultsStale ? null : get().criticalPath,
    })
    debouncedSave(updatedSystem, stale.calculationResults, stale.resultsStale)
  },

  // -- Edge Direction -------------------------------------------

  reverseEdge: (id): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return
    const basis = getRouteBasis(system)
    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const updatedEdges = system.edges.map(e => {
      if (e.id !== id) return e
      const conn = { source_id: e.connection.target_id, target_id: e.connection.source_id }
      // Rebuild from scratch so absent pins do not linger; swap the two ends.
      const next: EdgeDiagramProperties = { points: e.diagram.points }
      if (e.diagram.targetPin !== undefined) next.sourcePin = e.diagram.targetPin
      if (e.diagram.sourcePin !== undefined) next.targetPin = e.diagram.sourcePin
      if (e.diagram.waypoints !== undefined) next.waypoints = [...e.diagram.waypoints].reverse()
      const manual = (e as Partial<{ manually_routed: boolean }>).manually_routed
      const src = system.vertices.find(v => v.id === conn.source_id)
      const tgt = system.vertices.find(v => v.id === conn.target_id)
      if (manual) {
        // Preserve hand-authored geometry: reverse the polyline + swap handle strings.
        next.points = [...e.diagram.points].reverse()
        if (e.diagram.targetHandle !== undefined) next.sourceHandle = e.diagram.targetHandle
        if (e.diagram.sourceHandle !== undefined) next.targetHandle = e.diagram.sourceHandle
      // else (orphaned edge, vertex missing): pins + connection are swapped but geometry
      // is not recomputed — accepted degraded state, same as setEdgePin.
      } else if (src && tgt) {
        const route = routeEdge(
          vertexToRouteBox(src.diagram.position, src.type),
          vertexToRouteBox(tgt.diagram.position, tgt.type),
          {
            ...(next.sourcePin !== undefined ? { forcedSrcSide: next.sourcePin } : {}),
            ...(next.targetPin !== undefined ? { forcedTgtSide: next.targetPin } : {}),
            ...(next.waypoints !== undefined ? { waypoints: next.waypoints } : {}),
            takenSrcSides: collectUsedSides(system.edges, conn.source_id, e.id),
            takenTgtSides: collectUsedSides(system.edges, conn.target_id, e.id),
          },
          {
            // The connection ends were just swapped above, so the chain and handles
            // must swap with them — the same transform the `manual` branch applies.
            // Passing them un-reversed would draw the pipe from the new source across
            // the drawing to the far end and back.
            points: [...e.diagram.points].reverse(),
            ...(e.diagram.targetHandle !== undefined ? { sourceHandle: e.diagram.targetHandle } : {}),
            ...(e.diagram.sourceHandle !== undefined ? { targetHandle: e.diagram.sourceHandle } : {}),
          },
          basis,
        )
        next.points = route.points
        next.sourceHandle = route.sourceHandle
        next.targetHandle = route.targetHandle
      }
      return { ...e, connection: conn, diagram: next }
    })
    let updatedSystem: HydraulicSystem = { ...system, edges: updatedEdges }
    updatedSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)
    const stale = staleAfterEdit({ updatedSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults })
    set({ system: updatedSystem, undoStack, redoStack: [], canUndo: true, canRedo: false, isDirty: true, ...stale, resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale), criticalPath: stale.resultsStale ? null : get().criticalPath })
    debouncedSave(updatedSystem, stale.calculationResults, stale.resultsStale)
  },

  moveFittingToEdge: (fromEdgeId: string, index: number, toEdgeId: string): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return
    if (fromEdgeId === toEdgeId) return

    const from = system.edges.find(e => e.id === fromEdgeId)
    const to = system.edges.find(e => e.id === toEdgeId)
    if (!from || !to) return
    // ⚠️ Pipes only, both ends. `fittings` is a pipe field; writing one onto a pump edge
    // would produce a schema-invalid edge that nothing validates until save or export.
    const isPipe = (e: Edge): boolean => e.type === 'pipe' || e.type === 'compressible_pipe'
    if (!isPipe(from) || !isPipe(to)) return

    const fromFittings = (from as unknown as { fittings?: unknown[] }).fittings ?? []
    const moving = fromFittings[index]
    if (moving === undefined) return

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const updatedEdges = system.edges.map(e => {
      if (e.id === fromEdgeId) {
        return { ...e, fittings: fromFittings.filter((_, i) => i !== index) }
      }
      if (e.id === toEdgeId) {
        const toFittings = (e as unknown as { fittings?: unknown[] }).fittings ?? []
        return { ...e, fittings: [...toFittings, structuredClone(moving)] }
      }
      return e
    }) as never

    let updatedSystem: HydraulicSystem = { ...system, edges: updatedEdges }
    // Moving a fitting moves its K between two pipes, so any existing results are stale.
    updatedSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)
    const stale = staleAfterEdit({ updatedSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults })
    set({ system: updatedSystem, undoStack, redoStack: [], canUndo: true, canRedo: false, isDirty: true, ...stale, resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale), criticalPath: stale.resultsStale ? null : get().criticalPath })
    debouncedSave(updatedSystem, stale.calculationResults, stale.resultsStale)
  },

  // -- Clipboard ------------------------------------------------

  copySelection: (): void => {
    const { system, selectedIds } = get()
    if (!system || selectedIds.length === 0) return
    const selectedSet = new Set(selectedIds)
    const vertices = system.vertices.filter(v => selectedSet.has(v.id)).map(v => structuredClone(v))
    const edges = system.edges.filter(e =>
      selectedSet.has(e.connection.source_id) && selectedSet.has(e.connection.target_id)
    ).map(e => structuredClone(e))
    const anchor = vertices[0]?.diagram.position ?? { x: 0, y: 0 }
    set({ clipboard: { vertices, edges, anchorOffset: anchor }, pasteCounter: 0 })
  },

  pasteClipboard: (cursorPos?: { x: number; y: number }): void => {
    if (guardEdit()) return
    // Paste reads/writes canonical Base: clipboard vertices come from copySelection
    // (which captures system.vertices, i.e. Base) and pasted clones land back in
    // Base with NO scenario override — they carry Base values regardless of the
    // active scenario, and the scenario tree structure is left untouched.
    const { system, clipboard, pasteCounter } = get()
    if (!system || !clipboard || clipboard.vertices.length === 0) return

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)

    // Compute paste offset: cursor-relative when cursorPos given, else stair-step.
    const pasteOffset = cursorPos
      ? { x: cursorPos.x - clipboard.anchorOffset.x, y: cursorPos.y - clipboard.anchorOffset.y }
      : { x: 40 * (pasteCounter + 1), y: 40 * (pasteCounter + 1) }

    // Delegate id-remap (incl. tank penetrations, three-way ports, etc.) to
    // remapElements.  Source has no reference-data arrays → passthrough-on-miss
    // preserves every fluid_zone_id / pipe_specification_id / layer_id unchanged.
    const remapped = remapElements(
      { vertices: clipboard.vertices, edges: clipboard.edges },
      {
        vertices: system.vertices,
        edges: system.edges,
        ...(system.layers !== undefined ? { layers: system.layers } : {}),
      },
      pasteOffset,
    )

    let updatedSystem: HydraulicSystem = {
      ...system,
      vertices: [...system.vertices, ...remapped.vertices],
      edges: [...system.edges, ...remapped.edges],
    }
    const newSelectedIds = [...remapped.vertices.map(v => v.id), ...remapped.edges.map(e => e.id)]

    updatedSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)

    const stale = staleAfterEdit({ updatedSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults })
    set({
      system: updatedSystem, undoStack, redoStack: [], canUndo: true, canRedo: false,
      isDirty: true, selectedIds: newSelectedIds, pasteCounter: pasteCounter + 1,
      ...stale,
      resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale),
      criticalPath: stale.resultsStale ? null : get().criticalPath,
    })
    debouncedSave(updatedSystem, stale.calculationResults, stale.resultsStale)
  },

  cutSelection: (): void => {
    get().copySelection()
    get().removeSelectedItems()
  },

  // -- Device Library -------------------------------------------

  insertLibrarySnapshot: (snapshot: DeviceLibrarySnapshot, clickPos: { x: number; y: number }): InsertLibraryResult => {
    // ⚠️ Each refusal names ITSELF. This used to return a bare `false` for three unrelated
    // causes and the UI rendered every one as "This saved item isn't compatible with the
    // current model" — so a read-only session (someone else holds the edit lock) looked
    // like corrupt data, and cost a long debugging detour chasing a data bug that did not
    // exist. A refusal must say which refusal it is.
    if (guardEdit()) return { ok: false, reason: 'read-only' }
    const { system } = get()
    if (!system) return { ok: false, reason: 'no-model' }
    // Anchor on the snapshot's bounding-box TOP-LEFT (not an arbitrary first
    // vertex) so the whole group is placed down-and-right from the cursor and
    // can never land off the top-left edge of the canvas. A multi-device save
    // whose vertices[0] happened to be the bottom-right element previously
    // pushed the other devices to a negative offset from the cursor, so they
    // appeared off-screen / "missing". Relative layout is preserved either way;
    // remapElements adds this offset to every vertex position.
    const positions = snapshot.vertices.map(v => v.diagram?.position ?? { x: 0, y: 0 })
    const anchor = {
      x: Math.min(...positions.map(p => p.x)),
      y: Math.min(...positions.map(p => p.y)),
    }
    const insertOffset = { x: clickPos.x - anchor.x, y: clickPos.y - anchor.y }
    const remapped = remapElements(
      snapshot,
      {
        vertices: system.vertices,
        edges: system.edges,
        ...(system.layers !== undefined ? { layers: system.layers } : {}),
      },
      insertOffset,
    )
    const candidate: HydraulicSystem = {
      ...system,
      vertices: [...system.vertices, ...remapped.vertices],
      edges: [...system.edges, ...remapped.edges],
      fluid_zones: [...system.fluid_zones, ...remapped.fluid_zones],
      pipe_specifications: [...system.pipe_specifications, ...remapped.pipe_specifications],
      heat_transfer_settings: [...(system.heat_transfer_settings ?? []), ...remapped.heat_transfer_settings],
      layers: [...(system.layers ?? []), ...remapped.layers],
      custom_field_definitions: [...(system.custom_field_definitions ?? []), ...remapped.custom_field_definitions],
    }
    // A library item carries its own fluid zones, and one saved from a model
    // built before PIPE-142 can be in lb/ft3 and cP. Normalise the merged system
    // rather than just the incoming zones — idempotent, so the zones already in
    // the model are untouched.
    // ⚠️ A snapshot carrying a unit we cannot convert is refused, not inserted.
    // This is a WRITE: the user is placing something new, so declining is honest
    // and recoverable, where a load must never refuse (it would strand the Model).
    // KonvaCanvas.tsx:1123 shows a toast on `{ ok: false }` — an exception here
    // would instead leave placement armed and reach the error boundary.
    // ⚠️ REFUSE ON THE INCOMING ZONES ONLY. The current model may legitimately
    // carry a unit the load path tolerated and left as stored — that is the whole
    // point of reading never refusing. Judging the MERGED candidate would let one
    // pre-existing zone block every clean insert, which is a defect in this check
    // rather than a problem with the snapshot the user is placing.
    const incoming = normalizeSystemFluidZonesToSI({ fluid_zones: remapped.fluid_zones })
    if (incoming.errors.length > 0) {
      logger.error('Library insert carries a fluid zone we cannot normalise; aborting', undefined, { errors: incoming.errors })
      return { ok: false, reason: 'invalid-system' }
    }
    const normalizedInsert = normalizeSystemFluidZonesToSI(candidate)
    const parsed = HydraulicSystemSchema.safeParse(normalizedInsert.system)
    if (!parsed.success) {
      logger.error('Library insert produced an invalid system; aborting', undefined, { issues: parsed.error.issues })
      return { ok: false, reason: 'invalid-system' }
    }
    const undoStack = [...get().undoStack, captureFull(system)]
    if (undoStack.length > MAX_UNDO_DEPTH) undoStack.shift()
    // ⚠️ `normalizedInsert.system`, NOT `parsed.data`, and NOT `candidate`.
    //
    // `candidate` was the original bug: the normalisation was used to validate and
    // then thrown away. But `parsed.data` is a STRIPPED clone — Zod drops keys the
    // schema does not declare, across the whole existing model, not just the
    // insert. `pump-curve-resolvers.ts:68` documents relying on exactly that:
    // `pump_flo_selection.diameter_mm` is deliberately undeclared and read only on
    // "code paths that bypass strict validation". Assigning `parsed.data` here made
    // this a validating path, so a library insert silently erased that diameter and
    // moved the pump affinity ratio to 1.
    //
    // So: normalise for correctness, validate as a CHECK, store the unstripped one.
    let updatedSystem = normalizedInsert.system
    updatedSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)
    const stale = staleAfterEdit({ updatedSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults })
    set({
      system: updatedSystem,
      undoStack,
      redoStack: [],
      canUndo: true,
      canRedo: false,
      isDirty: true,
      selectedIds: [...remapped.vertices.map(v => v.id), ...remapped.edges.map(e => e.id)],
      ...stale,
      resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale),
      criticalPath: stale.resultsStale ? null : get().criticalPath,
    })
    debouncedSave(updatedSystem, stale.calculationResults, stale.resultsStale)
    return { ok: true }
  },

  // -- Split Edge -----------------------------------------------

  splitEdge: (edgeId: string, vertexType: string, opts?: SplitAt): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return

    const split = buildSplit(system, edgeId, vertexType, opts)
    if (!split) return

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    let updatedSystem: HydraulicSystem = {
      ...system,
      vertices: split.vertices as never,
      edges: split.edges as never,
      ...(split.scenarios ? { scenarios: split.scenarios as never } : {}),
    }

    updatedSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)

    const stale = staleAfterEdit({ updatedSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults })
    set({ system: updatedSystem, undoStack, redoStack: [], canUndo: true, canRedo: false, isDirty: true, selectedIds: [split.newVertexId], ...stale, resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale), criticalPath: stale.resultsStale ? null : get().criticalPath })
    debouncedSave(updatedSystem, stale.calculationResults, stale.resultsStale)
  },

  dissolveDevice: (id: string): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return
    const vertex = system.vertices.find(v => v.id === id)
    if (!vertex) return

    const incident = getConnectedEdges(id, system.edges)

    // ⚠️ A junction welding two mergeable pipes DOES have a through-line to keep — it is
    // the node a split inserted, and deleting it must undo that split. This case used to
    // fall into the plain-delete below, which took both pipes with it and left the run
    // severed. Everything else about the short-circuit stands.
    const healableJunction = vertex.type === 'junction' && canHealOnDissolve(system, id)

    // Nothing to preserve (junction / boundary reference / degree ≤ 1): a passive
    // junction can't stand in for a boundary condition, a lone spur is clutter, and
    // there's no through-line to keep. Plain-delete (single history entry — this runs
    // before any pushHistory below).
    //
    // ⚠️ A non-healable junction MUST still land here. The splice branch below replaces the
    // device with a NEW junction, so sending a junction there would swap J3 for J4 at the
    // same spot — churn that reads to the user as "delete did nothing".
    if (!healableJunction &&
        (vertex.type === 'junction' || DISSOLVE_BOUNDARY_TYPES.has(vertex.type) || incident.length <= 1)) {
      get().removeVertex(id)
      return
    }

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const claimedIds = new Set<string>()

    let updatedVertices: Vertex[]
    let updatedEdges: Edge[]
    let removedIds: Set<string>
    /** Scenarios after any override migration the heal performs, before pruning. */
    let healedScenarios = system.scenarios

    const e1 = incident[0]!
    const e2 = incident[1]!
    if (incident.length === 2 && mergeableForDissolve(e1, e2, id)) {
      // Heal into one pipe A—B: e1 absorbs e2 (keeps e1's id/name/props so e1's
      // scenario overrides survive on the merged pipe). e2 is dropped + pruned.
      const a = otherEndpoint(e1, id)
      const b = otherEndpoint(e2, id)
      const p1 = e1 as PipeEdge
      const p2 = e2 as PipeEdge
      // ⚠️ Either half may legitimately carry NO length, and dereferencing it threw —
      // aborting the whole action before any `set`, so the delete looked simply DEAD.
      // `PipeEdgeSchema` declares `length` required, which is why TypeScript never flagged
      // this, but the authoring flow contradicts that: `defaultPipeFields` omits it (a
      // length has no physically-safe default, so the UI prompts) and `buildSplit`
      // propagates the absence to both halves. Same schema-vs-reality gap as the
      // is_closed/is_oneway one.
      //
      // Summing a known length with an unknown one is not meaningful, so the total is
      // unknown too — and dropping the key is what lets the length prompt, which keys on
      // `length === undefined`, ask for it.
      const l1 = (p1 as Partial<PipeEdge>).length
      const l2 = (p2 as Partial<PipeEdge>).length
      const summedLength = l1 === undefined || l2 === undefined
        ? undefined
        : { value: l1.value + convertValue(l2.value, l2.unit, l1.unit), unit: l1.unit }

      const fittings = [...(p1.fittings ?? []), ...(p2.fittings ?? [])]
      const mergedFields = {
        ...structuredClone(e1),
        connection: { source_id: a, target_id: b },
        ...(fittings.length > 0 ? { fittings } : {}),
        diagram: { points: [] },
      } as unknown as Record<string, unknown>
      if (summedLength === undefined) delete mergedFields['length']
      else mergedFields['length'] = summedLength
      const merged = mergedFields as unknown as Edge
      updatedVertices = system.vertices.filter(v => v.id !== id)

      // ⚠️ Re-route rather than leaving `diagram: { points: [] }`. There is no
      // render-time router, so an empty chain draws a straight line between the new
      // endpoints — the healed pipe would visibly cut across the drawing.
      const av = updatedVertices.find(v => v.id === a)
      const bv = updatedVertices.find(v => v.id === b)
      if (av && bv) {
        const healRoute = routeEdge(
          vertexToRouteBox(av.diagram.position, av.type),
          vertexToRouteBox(bv.diagram.position, bv.type),
          undefined, undefined, getRouteBasis(system),
        )
        ;(merged as unknown as Record<string, unknown>)['diagram'] = {
          points: healRoute.points,
          sourceHandle: healRoute.sourceHandle,
          targetHandle: healRoute.targetHandle,
        }
      }

      updatedEdges = system.edges.filter(e => e.id !== e1.id && e.id !== e2.id).concat(merged)

      // ⚠️ Migrate the ABSORBED edge's scenario overrides onto the survivor before
      // pruning. e1 keeps its id so its own overrides were always safe; e2's were
      // silently dropped, quietly reverting a scenario the user had configured.
      if (healedScenarios) {
        healedScenarios = migrateEdgeOverrides(healedScenarios, e2.id, [merged.id])
      }
      removedIds = new Set<string>([id, e2.id])
    } else {
      // Spec-change (different diameter/spec) or n-way node: splice a junction at
      // the device's spot and re-point every incident edge (all types incl. duct).
      const newId = nextVertexId(system.vertices, claimedIds)
      const junction = createJunction(newId, {
        diagram: { position: { ...vertex.diagram.position } },
        elevation: { value: getVertexElevation(vertex), unit: 'm' },
      })
      const repointed = new Map<string, Edge>(
        incident.map(e => [e.id, {
          ...e,
          connection: {
            source_id: e.connection.source_id === id ? newId : e.connection.source_id,
            target_id: e.connection.target_id === id ? newId : e.connection.target_id,
          },
        } as Edge]),
      )
      updatedVertices = system.vertices.filter(v => v.id !== id).concat(junction)
      updatedEdges = system.edges.map(e => repointed.get(e.id) ?? e)
      removedIds = new Set<string>([id])
    }

    let updatedSystem: HydraulicSystem = {
      ...system,
      vertices: updatedVertices,
      edges: updatedEdges,
      ...(healedScenarios ? { scenarios: pruneOverridesForRemovedIds(healedScenarios, removedIds) } : {}),
    }
    updatedSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)

    const stale = staleAfterEdit({ updatedSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults })
    set({ system: updatedSystem, undoStack, redoStack: [], canUndo: true, canRedo: false, isDirty: true, selectedIds: [], ...stale, resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale), criticalPath: stale.resultsStale ? null : get().criticalPath })
    debouncedSave(updatedSystem, stale.calculationResults, stale.resultsStale)
  },

  // -- Orthogonal Routing ----------------------------------------

  updateVertexWithRouting: (vertexId, position): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return
    const basis = getRouteBasis(system)
    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const updatedVertices = system.vertices.map(v =>
      v.id === vertexId ? { ...v, diagram: { ...v.diagram, position } } : v
    )
    const updatedEdges = system.edges.map(e => {
      const isConnected = e.connection.source_id === vertexId || e.connection.target_id === vertexId
      // manually_routed is an opt-in flag the importer adds to skip auto-routing;
      // not on every Edge member, so narrow through a partial view.
      if (!isConnected || (e as Partial<{ manually_routed: boolean }>).manually_routed) return e
      const src = updatedVertices.find(v => v.id === e.connection.source_id)
      const tgt = updatedVertices.find(v => v.id === e.connection.target_id)
      if (!src || !tgt) return e
      // Convert each endpoint to a NodeBox (center + type-specific half
      // dimensions) so the router can compute handle positions that
      // actually match the rendered node sizes. Prior to the 2026-04-09
      // fix the router assumed every node was 60×60, producing visible
      // pipe jogs whenever mixed-size devices were connected.
      const srcBox = vertexToRouteBox(src.diagram.position, src.type)
      const tgtBox = vertexToRouteBox(tgt.diagram.position, tgt.type)
      // NB: use the inline `e.diagram.waypoints !== undefined` narrowing form
      // (same as sibling sites 4b/4d) directly in each spread. A boolean alias
      // (`const hasWaypoints = ...`) does NOT narrow `Point[] | undefined`, so
      // under exactOptionalPropertyTypes spreading `{ waypoints: e.diagram.waypoints }`
      // through the alias fails to compile (`Point[] | undefined` not assignable
      // to optional `waypoints?: Point[]`).
      const route = routeEdge(srcBox, tgtBox, {
        ...(e.diagram.sourcePin !== undefined ? { forcedSrcSide: e.diagram.sourcePin } : {}),
        ...(e.diagram.targetPin !== undefined ? { forcedTgtSide: e.diagram.targetPin } : {}),
        ...(e.diagram.waypoints !== undefined ? { waypoints: e.diagram.waypoints } : {}),
        // Waypointed edges use the cost-based side pick whose taken-sides
        // tie-break must see the same inputs a slide sees — read the
        // PRE-update edges (deterministic, matches other sites). Gated on the
        // same inline narrowing so the taken-sides hints are only added for
        // waypointed edges (auto/pinned edges keep their existing behavior).
        ...(e.diagram.waypoints !== undefined ? {
          takenSrcSides: collectUsedSides(system.edges, e.connection.source_id, e.id),
          takenTgtSides: collectUsedSides(system.edges, e.connection.target_id, e.id),
        } : {}),
      }, {
        // One endpoint moves, so the chain is unchanged and the terminal rubber-bands
        // off the live vertex at render (KonvaCanvas.tsx:306-314).
        points: e.diagram.points,
        ...(e.diagram.sourceHandle !== undefined ? { sourceHandle: e.diagram.sourceHandle } : {}),
        ...(e.diagram.targetHandle !== undefined ? { targetHandle: e.diagram.targetHandle } : {}),
      }, basis)
      return { ...e, diagram: { ...e.diagram, points: route.points, sourceHandle: route.sourceHandle, targetHandle: route.targetHandle } }
    })
    // A device move changes only diagram position + auto-routed pipe geometry —
    // both purely visual. Position is NOT a solver input, so results stay valid:
    // do NOT clearCalcResults / mark resultsStale (that blanked the V/Q/dP
    // overlay and forced a needless recalc). Mirror updateEdgeDiagram: persist
    // the move (isDirty) and keep it undoable, nothing more.
    const updatedSystem: HydraulicSystem = { ...system, vertices: updatedVertices, edges: updatedEdges }
    set({ system: updatedSystem, undoStack, redoStack: [], canUndo: true, canRedo: false, isDirty: true })
    debouncedSave(updatedSystem, get().calculationResults, get().resultsStale)
  },

  relayoutToGrid: (ids): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return
    const basis = getRouteBasis(system)

    // Empty ids = relayout the whole model (mirrors the Cmd/Ctrl+Shift+G default).
    const targetIds = ids.length > 0 ? ids : system.vertices.map((v): string => v.id)
    const targetSet = new Set(targetIds)

    // Snap every target vertex once, then index the result by id. This Map is
    // what makes the whole action O(V+E): the old path did `vertices.find` twice
    // per edge INSIDE a per-vertex loop, i.e. O(V²) finds on top of O(V) clones.
    const updatedVertices = system.vertices.map(v => {
      if (!targetSet.has(v.id) || !v.diagram?.position) return v
      return { ...v, diagram: { ...v.diagram, position: snapCenterToGrid(v.diagram.position, v.type) } }
    })
    const vertexById = new Map(updatedVertices.map(v => [v.id, v]))

    // Re-route every edge touching a relayouted vertex, against the FULLY-snapped
    // vertex map. Same per-edge routing as updateVertexWithRouting, but each edge
    // is routed exactly once here rather than once per snapped endpoint. Re-route
    // even for no-op snaps so legacy-routed edges pick up the current router math
    // (the reason the old path "always called").
    const updatedEdges = system.edges.map(e => {
      const connected = targetSet.has(e.connection.source_id) || targetSet.has(e.connection.target_id)
      if (!connected || (e as Partial<{ manually_routed: boolean }>).manually_routed) return e
      const src = vertexById.get(e.connection.source_id)
      const tgt = vertexById.get(e.connection.target_id)
      if (!src || !tgt) return e
      const srcBox = vertexToRouteBox(src.diagram.position, src.type)
      const tgtBox = vertexToRouteBox(tgt.diagram.position, tgt.type)
      const route = routeEdge(srcBox, tgtBox, {
        ...(e.diagram.sourcePin !== undefined ? { forcedSrcSide: e.diagram.sourcePin } : {}),
        ...(e.diagram.targetPin !== undefined ? { forcedTgtSide: e.diagram.targetPin } : {}),
        ...(e.diagram.waypoints !== undefined ? { waypoints: e.diagram.waypoints } : {}),
        ...(e.diagram.waypoints !== undefined ? {
          takenSrcSides: collectUsedSides(system.edges, e.connection.source_id, e.id),
          takenTgtSides: collectUsedSides(system.edges, e.connection.target_id, e.id),
        } : {}),
      }, {
        points: e.diagram.points,
        ...(e.diagram.sourceHandle !== undefined ? { sourceHandle: e.diagram.sourceHandle } : {}),
        ...(e.diagram.targetHandle !== undefined ? { targetHandle: e.diagram.targetHandle } : {}),
      }, basis)
      return { ...e, diagram: { ...e.diagram, points: route.points, sourceHandle: route.sourceHandle, targetHandle: route.targetHandle } }
    })

    // ONE history entry, ONE store update — see the interface note. Position is not
    // a solver input, so results stay valid (mirror updateVertexWithRouting).
    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const updatedSystem: HydraulicSystem = { ...system, vertices: updatedVertices, edges: updatedEdges }
    set({ system: updatedSystem, undoStack, redoStack: [], canUndo: true, canRedo: false, isDirty: true })
    debouncedSave(updatedSystem, get().calculationResults, get().resultsStale)
  },

  moveVertices: (moves, elementMoves): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return
    if (moves.length === 0) return
    const basis = getRouteBasis(system)

    // Derive delta from the first moved vertex (identical for all members by construction)
    const firstMove = moves[0]!
    const firstVertex = system.vertices.find(v => v.id === firstMove.id)
    if (!firstVertex) return
    const delta = {
      x: firstMove.position.x - firstVertex.diagram.position.x,
      y: firstMove.position.y - firstVertex.diagram.position.y,
    }

    // Derive bothMovedEdgeIds: edges whose BOTH endpoints are in the moved set
    const movedVertexIds = new Set(moves.map(m => m.id))
    const bothMovedEdgeIds = new Set(
      system.edges
        .filter(e => movedVertexIds.has(e.connection.source_id) && movedVertexIds.has(e.connection.target_id))
        .map(e => e.id)
    )

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)

    // Update all vertex positions in one pass
    const updatedVertices = system.vertices.map(v => {
      const move = moves.find(m => m.id === v.id)
      if (!move) return v
      return { ...v, diagram: { ...v.diagram, position: move.position } }
    })

    // Reroute affected edges exactly once against the post-move vertex map.
    // For both-endpoints-moved edges: translate waypoints+points by delta BEFORE routing.
    // For one-endpoint-moved edges: leave the chain in place; router self-heals.
    const affectedEdgeIds = new Set(
      system.edges
        .filter(e => movedVertexIds.has(e.connection.source_id) || movedVertexIds.has(e.connection.target_id))
        .map(e => e.id)
    )

    const updatedEdges = system.edges.map(e => {
      if (!affectedEdgeIds.has(e.id)) return e

      // Waypoint/points translation for both-endpoints-moved edges.
      // (same rationale as paste translation in remap-elements.ts:231-236)
      // NB: e.diagram.waypoints narrowing must be inline, not through a boolean alias,
      // for exactOptionalPropertyTypes (see the same comment in updateVertexWithRouting).
      let translatedWaypoints: { x: number; y: number }[] | undefined
      let translatedPoints: { x: number; y: number }[]
      if (bothMovedEdgeIds.has(e.id)) {
        translatedWaypoints = e.diagram.waypoints !== undefined
          ? e.diagram.waypoints.map(p => ({ x: p.x + delta.x, y: p.y + delta.y }))
          : undefined
        translatedPoints = Array.isArray(e.diagram.points)
          ? e.diagram.points.map(p => ({ x: p.x + delta.x, y: p.y + delta.y }))
          : []
      } else {
        translatedWaypoints = e.diagram.waypoints
        translatedPoints = e.diagram.points ?? []
      }

      const diagramWithTranslatedChain: typeof e.diagram = {
        ...e.diagram,
        ...(translatedWaypoints !== undefined ? { waypoints: translatedWaypoints } : {}),
        points: translatedPoints,
      }

      // Skip manually_routed edges (same semantics as updateVertexWithRouting)
      if ((e as Partial<{ manually_routed: boolean }>).manually_routed) {
        return { ...e, diagram: diagramWithTranslatedChain }
      }
      const src = updatedVertices.find(v => v.id === e.connection.source_id)
      const tgt = updatedVertices.find(v => v.id === e.connection.target_id)
      if (!src || !tgt) return { ...e, diagram: diagramWithTranslatedChain }
      const srcBox = vertexToRouteBox(src.diagram.position, src.type)
      const tgtBox = vertexToRouteBox(tgt.diagram.position, tgt.type)
      // Access sourcePin/targetPin through e.diagram (EdgeDiagramProperties — correctly typed as PortSide)
      // and waypoints through diagramWithTranslatedChain (already translated above).
      // Use inline narrowing per exactOptionalPropertyTypes requirement (see updateVertexWithRouting).
      const routeWaypoints = diagramWithTranslatedChain.waypoints
      const route = routeEdge(srcBox, tgtBox, {
        ...(e.diagram.sourcePin !== undefined ? { forcedSrcSide: e.diagram.sourcePin } : {}),
        ...(e.diagram.targetPin !== undefined ? { forcedTgtSide: e.diagram.targetPin } : {}),
        ...(routeWaypoints !== undefined ? { waypoints: routeWaypoints } : {}),
        ...(routeWaypoints !== undefined ? {
          takenSrcSides: collectUsedSides(system.edges, e.connection.source_id, e.id),
          takenTgtSides: collectUsedSides(system.edges, e.connection.target_id, e.id),
        } : {}),
      }, {
        // The TRANSLATED chain, not e.diagram.points: when both endpoints move the
        // chain must travel with them (:2375-2377), or an iso edge would be left
        // behind by its own devices. For a partial selection translatedPoints is the
        // original chain unchanged (:2380), which is also what iso wants — one
        // terminal rubber-bands and the geometry stays put.
        points: diagramWithTranslatedChain.points,
        ...(e.diagram.sourceHandle !== undefined ? { sourceHandle: e.diagram.sourceHandle } : {}),
        ...(e.diagram.targetHandle !== undefined ? { targetHandle: e.diagram.targetHandle } : {}),
      }, basis)
      return { ...e, diagram: { ...diagramWithTranslatedChain, points: route.points, sourceHandle: route.sourceHandle, targetHandle: route.targetHandle } }
    })

    // Apply element position moves
    // ?? [] — createEmptySystem + non-parsed in-memory systems may omit this field
    const existingElements = system.diagram_elements ?? []
    const updatedElements = elementMoves.length === 0
      ? existingElements
      : existingElements.map(el => {
          const move = elementMoves.find(m => m.id === el.id)
          if (!move) return el
          return { ...el, position: move.position } as DiagramElement
        })

    // Position is NOT a solver input, so results stay valid — mirror updateVertexWithRouting:
    // do NOT clearCalcResults / mark resultsStale. Just persist the move and push one undo entry.
    const updatedSystem: HydraulicSystem = {
      ...system,
      vertices: updatedVertices,
      edges: updatedEdges,
      ...(updatedElements !== existingElements
        ? { diagram_elements: updatedElements }
        : {}),
    }
    set({ system: updatedSystem, undoStack, redoStack: [], canUndo: true, canRedo: false, isDirty: true })
    debouncedSave(updatedSystem, get().calculationResults, get().resultsStale)
  },

  updateEdgeDiagram: (edgeId, diagram): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return
    const updatedEdges = system.edges.map(e => e.id === edgeId ? { ...e, diagram: { ...e.diagram, ...diagram } } : e)
    set({ system: { ...system, edges: updatedEdges }, isDirty: true })
    debouncedSave({ ...system, edges: updatedEdges }, get().calculationResults, get().resultsStale)
  },

  setResultsOffset: (id, offset): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return

    const vertex = system.vertices.find(v => v.id === id)
    const edge = vertex ? undefined : system.edges.find(e => e.id === id)
    if (!vertex && !edge) return

    // No-op when nothing changes, so a click-without-drag doesn't push an undo frame the
    // user then has to press Ctrl-Z through.
    const current = (vertex ?? edge)!.diagram as { resultsOffset?: { dx: number; dy: number } } | undefined
    const before = current?.resultsOffset
    if (offset === null ? before === undefined
      : before?.dx === offset.dx && before?.dy === offset.dy) return

    const applied = <T extends { diagram?: unknown }>(el: T): T => {
      const diagram = { ...(el.diagram as Record<string, unknown>) }
      if (offset === null) delete diagram['resultsOffset']
      else diagram['resultsOffset'] = { dx: offset.dx, dy: offset.dy }
      return { ...el, diagram }
    }

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const updatedSystem: HydraulicSystem = vertex
      ? { ...system, vertices: system.vertices.map(v => v.id === id ? applied(v) : v) as never }
      : { ...system, edges: system.edges.map(e => e.id === id ? applied(e) : e) as never }

    // ⚠️ No `staleAfterEdit` / `clearCalcResults` here. Label placement is presentation, not
    // a solver input — invalidating results would mean tidying the drawing throws away the
    // very numbers being tidied. Mirrors `moveVertices`.
    set({ system: updatedSystem, undoStack, redoStack: [], canUndo: true, canRedo: false, isDirty: true })
    debouncedSave(updatedSystem, get().calculationResults, get().resultsStale)
  },

  setVertexSymbol: (id, symbol): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return

    const vertex = system.vertices.find(v => v.id === id)
    if (!vertex) return

    // No-op when nothing changes, so re-picking the symbol already in use does
    // not push an undo frame the user then has to press Ctrl-Z through.
    const current = vertex.diagram as { symbol?: string } | undefined
    const before = current?.symbol
    if (symbol === null ? before === undefined : before === symbol) return

    const applied = <T extends { diagram?: unknown }>(el: T): T => {
      const diagram = { ...(el.diagram as Record<string, unknown>) }
      // Absent, never empty-string: the renderer treats absent as "use the
      // default", and an empty string would be a second way to say the same
      // thing that only one of the two code paths recognises.
      if (symbol === null) delete diagram['symbol']
      else diagram['symbol'] = symbol
      return { ...el, diagram }
    }

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const updatedSystem: HydraulicSystem = {
      ...system,
      vertices: system.vertices.map(v => v.id === id ? applied(v) : v) as never,
    }

    // ⚠️ No `staleAfterEdit` / `clearCalcResults`. Which glyph a device is drawn
    // with is presentation, not a solver input — invalidating results would mean
    // changing a symbol throws away the numbers on screen. Mirrors
    // setResultsOffset.
    set({ system: updatedSystem, undoStack, redoStack: [], canUndo: true, canRedo: false, isDirty: true })
    debouncedSave(updatedSystem, get().calculationResults, get().resultsStale)
  },

  clearAllResultsOffsets: (): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return

    const hasOffset = (el: { diagram?: unknown }): boolean =>
      (el.diagram as { resultsOffset?: unknown } | undefined)?.resultsOffset !== undefined
    if (!system.vertices.some(hasOffset) && !system.edges.some(hasOffset)) return

    const stripped = <T extends { diagram?: unknown }>(el: T): T => {
      if (!hasOffset(el)) return el
      const diagram = { ...(el.diagram as Record<string, unknown>) }
      delete diagram['resultsOffset']
      return { ...el, diagram }
    }

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const updatedSystem: HydraulicSystem = {
      ...system,
      vertices: system.vertices.map(stripped) as never,
      edges: system.edges.map(stripped) as never,
    }
    set({ system: updatedSystem, undoStack, redoStack: [], canUndo: true, canRedo: false, isDirty: true })
    debouncedSave(updatedSystem, get().calculationResults, get().resultsStale)
  },

  spreadModel: (target): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return

    const current = system.diagram_spacing ?? 1
    const next = clampSpread(target)
    if (isSpreadNoop(current, next)) return
    const ratio = spreadRatio(current, next)

    const origin = spreadOrigin(system.vertices.map(v => v.diagram.position))
    const basis = getRouteBasis(system)

    const updatedVertices = system.vertices.map(v => {
      if (!v.diagram?.position) return v
      return { ...v, diagram: { ...v.diagram, position: scaleAbout(v.diagram.position, origin, ratio) } }
    })
    const vertexById = new Map(updatedVertices.map(v => [v.id, v]))

    const updatedEdges = system.edges.map(e => {
      // ⚠️ A hand-shaped route must have its WAYPOINTS scaled, not be skipped the way
      // relayoutToGrid skips it. Its endpoints move with their devices regardless, so
      // leaving the chain at the old scale detaches the shaped route from both ends.
      const waypoints = e.diagram.waypoints?.map(p => scaleAbout(p, origin, ratio))
      if ((e as Partial<{ manually_routed: boolean }>).manually_routed) {
        return {
          ...e,
          diagram: {
            ...e.diagram,
            points: e.diagram.points.map(p => scaleAbout(p, origin, ratio)),
            ...(waypoints ? { waypoints } : {}),
          },
        }
      }

      const src = vertexById.get(e.connection.source_id)
      const tgt = vertexById.get(e.connection.target_id)
      if (!src || !tgt) return e
      const route = routeEdge(
        vertexToRouteBox(src.diagram.position, src.type),
        vertexToRouteBox(tgt.diagram.position, tgt.type),
        {
          ...(e.diagram.sourcePin !== undefined ? { forcedSrcSide: e.diagram.sourcePin } : {}),
          ...(e.diagram.targetPin !== undefined ? { forcedTgtSide: e.diagram.targetPin } : {}),
          ...(waypoints ? { waypoints } : {}),
        },
        {
          points: e.diagram.points,
          ...(e.diagram.sourceHandle !== undefined ? { sourceHandle: e.diagram.sourceHandle } : {}),
          ...(e.diagram.targetHandle !== undefined ? { targetHandle: e.diagram.targetHandle } : {}),
        },
        basis,
      )
      return {
        ...e,
        diagram: {
          ...e.diagram,
          points: route.points,
          sourceHandle: route.sourceHandle,
          targetHandle: route.targetHandle,
          ...(waypoints ? { waypoints } : {}),
        },
      }
    })

    // ⚠️ Annotations move WITH the drawing or they detach from whatever they label. Their
    // size is deliberately left alone — text scaled with the layout would become unreadable
    // at 0.5x and absurd at 4x, and legibility is the reason to spread in the first place.
    const updatedElements = (system.diagram_elements ?? []).map(el =>
      el.position ? { ...el, position: scaleAbout(el.position, origin, ratio) } : el)

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const updatedSystem: HydraulicSystem = {
      ...system,
      vertices: updatedVertices as never,
      edges: updatedEdges as never,
      diagram_elements: updatedElements as never,
      diagram_spacing: next,
    }

    // ⚠️ No staleAfterEdit. Position is not a solver input — same as moveVertices and
    // relayoutToGrid. Spreading a drawing to read it must not discard the results.
    set({ system: updatedSystem, undoStack, redoStack: [], canUndo: true, canRedo: false, isDirty: true })
    debouncedSave(updatedSystem, get().calculationResults, get().resultsStale)
  },

  setEdgeManuallyRouted: (edgeId, manually_routed): void => {
    const { system } = get()
    if (!system) return
    const updatedEdges = system.edges.map(e => e.id === edgeId ? { ...e, manually_routed } : e)
    set({ system: { ...system, edges: updatedEdges }, isDirty: true })
  },

  setEdgePin: (edgeId, end, side): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return
    const basis = getRouteBasis(system)
    const edge = system.edges.find(e => e.id === edgeId)
    if (!edge) return
    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)

    // Merge the pin change in-memory, then derive both forced sides from the merged state.
    const merged: EdgeDiagramProperties = { ...edge.diagram }
    if (side === null) {
      if (end === 'source') delete merged.sourcePin
      else delete merged.targetPin
    } else {
      if (end === 'source') merged.sourcePin = side
      else merged.targetPin = side
    }

    let nextDiagram = merged
    const src = system.vertices.find(v => v.id === edge.connection.source_id)
    const tgt = system.vertices.find(v => v.id === edge.connection.target_id)
    // Reroute only when both endpoints exist; for an orphaned edge (vertex deleted)
    // store the pin without recomputing geometry.
    if (src && tgt) {
      const srcBox = vertexToRouteBox(src.diagram.position, src.type)
      const tgtBox = vertexToRouteBox(tgt.diagram.position, tgt.type)
      // exactOptionalPropertyTypes is on — only set forced* when defined (conditional spread).
      const route = routeEdge(srcBox, tgtBox, {
        ...(merged.sourcePin !== undefined ? { forcedSrcSide: merged.sourcePin } : {}),
        ...(merged.targetPin !== undefined ? { forcedTgtSide: merged.targetPin } : {}),
        ...(merged.waypoints !== undefined ? { waypoints: merged.waypoints } : {}),
        takenSrcSides: collectUsedSides(system.edges, edge.connection.source_id, edge.id),
        takenTgtSides: collectUsedSides(system.edges, edge.connection.target_id, edge.id),
      }, {
        // On an iso model the pin is recorded but the geometry is left alone: the
        // router is what would act on a pin, and it is off. Accepted for now — the
        // pin persists and is inert rather than destroying the drawing.
        points: merged.points,
        ...(merged.sourceHandle !== undefined ? { sourceHandle: merged.sourceHandle } : {}),
        ...(merged.targetHandle !== undefined ? { targetHandle: merged.targetHandle } : {}),
      }, basis)
      nextDiagram = { ...merged, points: route.points, sourceHandle: route.sourceHandle, targetHandle: route.targetHandle }
    }

    const updatedEdges = system.edges.map(e =>
      e.id === edgeId
        // Pinning makes the edge router-controlled (side-pinned, path auto), so clear
        // manually_routed — otherwise updateVertexWithRouting would skip it and the pin
        // would not survive device moves.
        ? { ...e, diagram: nextDiagram, manually_routed: false }
        : e,
    )
    const updatedSystem: HydraulicSystem = { ...system, edges: updatedEdges }
    set({ system: updatedSystem, undoStack, redoStack: [], canUndo: true, canRedo: false, isDirty: true })
    debouncedSave(updatedSystem, get().calculationResults, get().resultsStale)
  },

  setEdgePins: (targets): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return
    const basis = getRouteBasis(system)

    // Last-wins dedupe per (edgeId, end): the no-op filter below compares
    // against the ORIGINAL system, so without dedupe a duplicate same-end pair
    // like [{pin top}, {unpin}] would drop the unpin as a "no-op" and let the
    // earlier pin apply — violating last-wins. Designed callers can't emit
    // duplicates, but this is a public store action.
    const byKey = new Map<string, { edgeId: string; end: 'source' | 'target'; side: PortSide | null }>()
    for (const t of targets) byKey.set(`${t.edgeId}:${t.end}`, t)

    // Pre-filter missing edges and no-ops BEFORE pushing history so a fully
    // filtered batch leaves no empty undo entry (and doesn't wipe redo).
    const survivors = [...byKey.values()].filter(t => {
      const edge = system.edges.find(e => e.id === t.edgeId)
      if (!edge) return false
      const current = (t.end === 'source' ? edge.diagram.sourcePin : edge.diagram.targetPin) ?? null
      return current !== t.side
    })
    if (survivors.length === 0) return

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)

    // Apply against an EVOLVING edges array: later targets must see earlier
    // targets' pins (same-edge composition) AND their routed handles —
    // collectUsedSides reads sourceHandle/targetHandle, so writing the route
    // handles back into the evolving array is load-bearing for sibling
    // taken-side visibility.
    let edges = system.edges
    for (const t of survivors) {
      const edge = edges.find(e => e.id === t.edgeId)
      if (!edge) continue
      const merged: EdgeDiagramProperties = { ...edge.diagram }
      if (t.side === null) {
        if (t.end === 'source') delete merged.sourcePin
        else delete merged.targetPin
      } else {
        if (t.end === 'source') merged.sourcePin = t.side
        else merged.targetPin = t.side
      }

      let nextDiagram = merged
      const src = system.vertices.find(v => v.id === edge.connection.source_id)
      const tgt = system.vertices.find(v => v.id === edge.connection.target_id)
      // Reroute only when both endpoints exist (orphaned edge: store pin only).
      if (src && tgt) {
        const srcBox = vertexToRouteBox(src.diagram.position, src.type)
        const tgtBox = vertexToRouteBox(tgt.diagram.position, tgt.type)
        // exactOptionalPropertyTypes — conditional spreads, same idiom as setEdgePin.
        const route = routeEdge(srcBox, tgtBox, {
          ...(merged.sourcePin !== undefined ? { forcedSrcSide: merged.sourcePin } : {}),
          ...(merged.targetPin !== undefined ? { forcedTgtSide: merged.targetPin } : {}),
          ...(merged.waypoints !== undefined ? { waypoints: merged.waypoints } : {}),
          takenSrcSides: collectUsedSides(edges, edge.connection.source_id, edge.id),
          takenTgtSides: collectUsedSides(edges, edge.connection.target_id, edge.id),
        }, {
          // THIS iteration's merged diagram — the loop evolves `edges`, so an outer
          // or hoisted binding would preserve the wrong edge's chain. Same iso
          // semantics as setEdgePin: record the pin, leave the geometry alone.
          points: merged.points,
          ...(merged.sourceHandle !== undefined ? { sourceHandle: merged.sourceHandle } : {}),
          ...(merged.targetHandle !== undefined ? { targetHandle: merged.targetHandle } : {}),
        }, basis)
        nextDiagram = { ...merged, points: route.points, sourceHandle: route.sourceHandle, targetHandle: route.targetHandle }
      }

      // Pinning makes the edge router-controlled — clear manually_routed so the
      // pin survives device moves (same rationale as setEdgePin).
      edges = edges.map(e => e.id === t.edgeId ? { ...e, diagram: nextDiagram, manually_routed: false } : e)
    }

    const updatedSystem: HydraulicSystem = { ...system, edges }
    set({ system: updatedSystem, undoStack, redoStack: [], canUndo: true, canRedo: false, isDirty: true })
    debouncedSave(updatedSystem, get().calculationResults, get().resultsStale)
  },

  setEdgeWaypoints: (edgeId, waypoints): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return
    const basis = getRouteBasis(system)
    const edge = system.edges.find(e => e.id === edgeId)
    if (!edge) return

    if (waypoints !== null) {
      // Defensive validation (the drag math's bridge normalization guarantees
      // orthogonal output for legitimate gestures — this is the backstop, not
      // an expected path). Whole-action rejection, no silent fallback.
      const finite = waypoints.length > 0 && waypoints.every(p => Number.isFinite(p.x) && Number.isFinite(p.y))
      const EPS = 0.01
      const orthogonal = waypoints.every((p, i) => {
        if (i === 0) return true
        const q = waypoints[i - 1]!
        return Math.abs(p.x - q.x) < EPS || Math.abs(p.y - q.y) < EPS
      })
      if (!finite || !orthogonal) {
        logger.warn('setEdgeWaypoints: rejected malformed chain', { edgeId, waypoints })
        return
      }
    }

    // No-op skip before history (no empty undo entries, no redo wipe).
    const current = edge.diagram.waypoints
    if (waypoints === null && current === undefined) return
    if (waypoints !== null && current !== undefined && JSON.stringify(current) === JSON.stringify(waypoints)) return

    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)

    const merged: EdgeDiagramProperties = { ...edge.diagram }
    if (waypoints === null) delete merged.waypoints
    else merged.waypoints = waypoints

    let nextDiagram = merged
    const src = system.vertices.find(v => v.id === edge.connection.source_id)
    const tgt = system.vertices.find(v => v.id === edge.connection.target_id)
    // Reroute only when both endpoints exist (orphaned edge: store intent only).
    if (src && tgt) {
      const route = routeEdge(
        vertexToRouteBox(src.diagram.position, src.type),
        vertexToRouteBox(tgt.diagram.position, tgt.type),
        {
          ...(merged.sourcePin !== undefined ? { forcedSrcSide: merged.sourcePin } : {}),
          ...(merged.targetPin !== undefined ? { forcedTgtSide: merged.targetPin } : {}),
          ...(merged.waypoints !== undefined ? { waypoints: merged.waypoints } : {}),
          takenSrcSides: collectUsedSides(system.edges, edge.connection.source_id, edge.id),
          takenTgtSides: collectUsedSides(system.edges, edge.connection.target_id, edge.id),
        },
        // `undefined` is deliberate, and is the one site that always routes: an
        // explicit waypoint edit is the USER authoring geometry, not the router
        // overwriting it. Preserving the old chain here would persist the edit to
        // `waypoints` while rendering the previous `points` — the edit would vanish
        // and the pair would be permanently inconsistent (see common.ts: points is
        // the DERIVED polyline, waypoints the intent). Moot in practice — the
        // validator above rejects non-orthogonal chains, so an iso edge never
        // reaches here — but the intent should not be left to inference.
        undefined,
        basis,
      )
      nextDiagram = { ...merged, points: route.points, sourceHandle: route.sourceHandle, targetHandle: route.targetHandle }
    }

    const updatedEdges = system.edges.map(e =>
      // Waypointed edges are router-controlled — clear manually_routed so the
      // chain survives device moves (same rationale as setEdgePin).
      e.id === edgeId ? { ...e, diagram: nextDiagram, manually_routed: false } : e,
    )
    const updatedSystem: HydraulicSystem = { ...system, edges: updatedEdges }
    set({ system: updatedSystem, undoStack, redoStack: [], canUndo: true, canRedo: false, isDirty: true })
    debouncedSave(updatedSystem, get().calculationResults, get().resultsStale)
  },

  // Bulk "Keep all pipe routes": persist every eligible pipe's current bends
  // into diagram.waypoints so the router threads them verbatim across device
  // moves (instead of the fold-prone auto L/Z router). One undo entry.
  lockAllPipeRoutes: (): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system) return
    const basis = getRouteBasis(system)

    // Pipes only — the fold complaint (B33) is about pipes; keep intent narrow.
    const LOCKABLE_TYPES = new Set(['pipe', 'compressible_pipe'])
    let locked = 0
    let skipped = 0

    const updatedEdges = system.edges.map(e => {
      if (!LOCKABLE_TYPES.has(e.type)) return e
      const diagram = e.diagram
      // Already-locked (waypointed) or manually-routed edges are left untouched.
      if (diagram.waypoints !== undefined || (e as Partial<{ manually_routed: boolean }>).manually_routed) return e

      const src = system.vertices.find(v => v.id === e.connection.source_id)
      const tgt = system.vertices.find(v => v.id === e.connection.target_id)
      const lockable = deriveLockableWaypoints(diagram.points, src?.diagram.position, tgt?.diagram.position)
      if (!lockable) { skipped++; return e } // straight run / unlockable — doesn't fold

      // Reroute through the new chain (mirrors updateVertexWithRouting's per-edge
      // recompute; reads collectUsedSides from the PRE-update edges, deterministic).
      let nextDiagram: EdgeDiagramProperties = { ...diagram, waypoints: lockable }
      if (src && tgt) {
        const route = routeEdge(
          vertexToRouteBox(src.diagram.position, src.type),
          vertexToRouteBox(tgt.diagram.position, tgt.type),
          {
            ...(diagram.sourcePin !== undefined ? { forcedSrcSide: diagram.sourcePin } : {}),
            ...(diagram.targetPin !== undefined ? { forcedTgtSide: diagram.targetPin } : {}),
            waypoints: lockable,
            takenSrcSides: collectUsedSides(system.edges, e.connection.source_id, e.id),
            takenTgtSides: collectUsedSides(system.edges, e.connection.target_id, e.id),
          },
          {
            // Unreachable for iso today — deriveLockableWaypoints rejects any
            // non-orthogonal interior, so an iso edge already exited at `skipped++`
            // above. Dispatched through the facade anyway so the invariant holds:
            // EVERY router call decides ortho-vs-leave-alone in one place, and a
            // future change to deriveLockableWaypoints cannot silently start
            // rerouting iso drawings.
            points: diagram.points,
            ...(diagram.sourceHandle !== undefined ? { sourceHandle: diagram.sourceHandle } : {}),
            ...(diagram.targetHandle !== undefined ? { targetHandle: diagram.targetHandle } : {}),
          },
          basis,
        )
        nextDiagram = { ...nextDiagram, points: route.points, sourceHandle: route.sourceHandle, targetHandle: route.targetHandle }
      }
      locked++
      return { ...e, diagram: nextDiagram }
    })

    if (locked === 0) {
      toast(skipped > 0
        ? `No routes to keep · ${skipped} straight run${skipped === 1 ? '' : 's'} skipped`
        : 'No pipe routes to keep')
      return
    }

    // Waypoints are diagram-only (solver ignores them) — like setEdgeWaypoints and
    // updateVertexWithRouting, do NOT clearCalcResults / mark resultsStale, so the
    // V/Q/dP overlay survives and no needless recalc is forced.
    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const updatedSystem: HydraulicSystem = { ...system, edges: updatedEdges }
    set({ system: updatedSystem, undoStack, redoStack: [], canUndo: true, canRedo: false, isDirty: true })
    debouncedSave(updatedSystem, get().calculationResults, get().resultsStale)
    toast.success(`${locked} route${locked === 1 ? '' : 's'} kept${skipped > 0 ? ` · ${skipped} straight run${skipped === 1 ? '' : 's'} skipped` : ''}`)
  },

  // -- Undo / Redo ----------------------------------------------

  undo: (): void => {
    if (guardEdit()) return
    const { system, undoStack } = get()
    if (!system || undoStack.length === 0) return

    const prev = undoStack[undoStack.length - 1]!
    const current: HistoryEntry = prev.kind === 'full'
      ? captureFull(system)
      : { kind: 'graph' as const, vertices: structuredClone(system.vertices), edges: structuredClone(system.edges),
          scenarios: system.scenarios !== undefined ? structuredClone(system.scenarios) : undefined,
          diagram_elements: structuredClone(system.diagram_elements ?? []) }
    const newUndoStack = undoStack.slice(0, -1)
    const newRedoStack = [...get().redoStack, current]

    let updatedSystem = applyEntry(system, prev)

    updatedSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)

    const stale = staleAfterEdit({ updatedSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults })
    set({
      system: updatedSystem,
      undoStack: newUndoStack,
      redoStack: newRedoStack,
      canUndo: newUndoStack.length > 0,
      canRedo: true,
      isDirty: true,
      selectedIds: [],
      ...stale,
      resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale),
      criticalPath: stale.resultsStale ? null : get().criticalPath,
    })
    debouncedSave(updatedSystem, stale.calculationResults, stale.resultsStale)
  },

  redo: (): void => {
    if (guardEdit()) return
    const { system, redoStack } = get()
    if (!system || redoStack.length === 0) return

    const next = redoStack[redoStack.length - 1]!
    const current: HistoryEntry = next.kind === 'full'
      ? captureFull(system)
      : { kind: 'graph' as const, vertices: structuredClone(system.vertices), edges: structuredClone(system.edges),
          scenarios: system.scenarios !== undefined ? structuredClone(system.scenarios) : undefined,
          diagram_elements: structuredClone(system.diagram_elements ?? []) }
    const newRedoStack = redoStack.slice(0, -1)
    const newUndoStack = [...get().undoStack, current]

    let updatedSystem = applyEntry(system, next)

    updatedSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)

    const stale = staleAfterEdit({ updatedSystem, scenarioResults: get().scenarioResults, scenarioStatus: get().scenarioStatus, currentResults: get().calculationResults })
    set({
      system: updatedSystem,
      undoStack: newUndoStack,
      redoStack: newRedoStack,
      canUndo: true,
      canRedo: newRedoStack.length > 0,
      isDirty: true,
      selectedIds: [],
      ...stale,
      resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, stale.resultsStale),
      criticalPath: stale.resultsStale ? null : get().criticalPath,
    })
    debouncedSave(updatedSystem, stale.calculationResults, stale.resultsStale)
  },

  // -- Calculation -----------------------------------------------

  calculate: async (token?: string): Promise<CalcEventResult> => {
    // `startedAt`/`pipeCount`/`vertexCount` are captured HERE, before any
    // `await`, and carried through to every return below — never re-read
    // from the store after the solve, because the apply-guard further down
    // means the store may by then hold a different Model's state entirely.
    const startedAt = Date.now()
    const { system } = get()
    // No Model loaded: nothing to calculate, no solve ran. `{modelLoaded: false}`
    // is not a failed calculation and must never be reported as one (see
    // CalcEventResult) — the caller (EditorPage's handleCalculate) must not emit
    // `Model Calculated` for this.
    if (!system) return { modelLoaded: false }
    const pipeCount = system.edges?.length ?? 0
    const vertexCount = system.vertices?.length ?? 0
    const resolved = deriveResolved(system)!
    const activeId = system.scenarios?.active_id ?? 'base'
    const isBaseActive = activeId === 'base'

    const { calculate: runCalc, validate: validateSystem } = await import('../../../services/calc-service')
    const { validateScenariosClosure } = await import('@shared/hydraulic')
    const closureErrors = validateScenariosClosure(system as unknown as Parameters<typeof validateScenariosClosure>[0])
    const errors = [...closureErrors, ...validateSystem(resolved)]
    if (errors.length > 0) {
      const invalid = { status: 'invalid' as const, edgeResults: [], vertexPressures: {}, errors }
      const scenarioStatus = { ...get().scenarioStatus, [activeId]: 'stale' as const }
      const scenarioResults = { ...get().scenarioResults }; delete scenarioResults[activeId]
      set({ calculationResults: invalid, scenarioResults, scenarioStatus,
        analysisResults: null, elementHealthMap: {}, healthSummary: null, resultsStale: false, isCalculating: false })
      return { modelLoaded: true, outcome: 'invalid', durationMs: Date.now() - startedAt, pipeCount, vertexCount }
    }

    // Apply-guard (spec §3.4): capture a cheap reference-identity token for
    // the live system BEFORE the (possibly long, streamed) solve starts. The
    // store never mutates `system` in place — every edit action replaces it
    // wholesale — so `get().system !== capturedSystem` after the await is a
    // correct, O(1) "did the user edit during this solve?" check. Mirrors
    // runSave's `savedSystem` precedent (see debouncedSave/runSave above).
    const capturedSystem = system
    // Model-identity gate (final-review fix): `capturedSystem` alone catches
    // a mid-solve EDIT of the same model, but not a mid-solve MODEL SWITCH —
    // loading a different model also replaces `system` by reference, so
    // `get().system !== capturedSystem` is true either way. Capture the
    // model id too so the two cases can be told apart below.
    const capturedModelId = get().modelId

    // Streamed-calc UI (Task 4, spec §3.4): a local ticker drives the
    // elapsed-seconds display independent of frame arrival, and a watchdog
    // (reset via onFrame on 'started'/heartbeat) flags a stalled-looking
    // connection. Degrades to a static, non-ticking "Calculating…" if Task 0
    // ever records a buffering intermediary (CALC_STREAM_BUFFERING_DEGRADED).
    //
    // Gated to the SAME size-gate calc-service uses internally (spec §1.1:
    // "everything else stays on the untouched sync path") — sub-threshold
    // calls never stream, so calc-service never invokes onProgress for them,
    // and a ticker/streamProgress here would be a needless "Calculating…
    // 0s" flash on every sub-second auto-recalc-on-edit once a component
    // renders off streamProgress. A no-op stand-in keeps the onFrame()/
    // stop() call sites below unconditional.
    const isHeavy = (resolved.vertices?.length ?? 0) + (resolved.edges?.length ?? 0) > STREAM_THRESHOLD
    const abortController = new AbortController()
    calcAbortController = abortController
    const ticker = isHeavy
      ? createStreamTicker({
          degraded: CALC_STREAM_BUFFERING_DEGRADED,
          heartbeatMs: HEARTBEAT_MS,
          onUpdate: (progress): void => set({ streamProgress: progress }),
        })
      : { onFrame: (): void => {}, stop: (): void => {} }

    // Reported to the caller regardless of the apply-guard outcome below —
    // it reflects what THIS solve computed, which is correct analytics even
    // when the guard declines to commit it to the store (the model changed
    // mid-solve, not the calculation itself). Assigned on every path through
    // the try/catch below before the final `return` reads it.
    let outcome: CalculatedOutcome

    set({ isCalculating: true })
    try {
      const response = await runCalc(resolved, token, {
        signal: abortController.signal,
        onProgress: (): void => ticker.onFrame(),
      })
      const calcOutput = response.calculationOutput ?? null
      outcome = calcOutput?.status ?? 'failed'

      let materialized = resolved
      if (hasUsableResults(calcOutput)) {
        const { applyCalcResults } = await import('@shared/hydraulic')
        materialized = applyCalcResults(materialized, calcOutput)
      }
      const autoSizeSuggestions = calcOutput?.status === 'success' ? computeAutoSizeSuggestions(materialized, calcOutput) : []
      let criticalPath: CriticalPathResult | null = null
      if (hasUsableResults(calcOutput)) {
        const { findCriticalPath } = await import('@shared/hydraulic')
        criticalPath = findCriticalPath(materialized, calcOutput)
      }
      const elementHealthMap = computeElementHealthMap(response.results ?? {})
      const totalElements = (materialized.vertices?.length ?? 0) + (materialized.edges?.length ?? 0)
      const healthSummary = computeHealthSummary(response.results ?? {}, elementHealthMap, totalElements)

      // Apply-guard: only write system/calculationResults/analysisResults/
      // autoSizeSuggestions/criticalPath/elementHealthMap/healthSummary/
      // dismissedSuggestionIds/scenarioResults[activeId] — the WHOLE
      // derived-overlay block — if the live system is still the exact one
      // we captured before the solve started. An edit landing mid-solve
      // means these fields would reference edges/vertices the user has
      // since changed; write NONE of them, stash the result for history,
      // and leave the scenario 'stale' so the UI knows to recalculate.
      const sameModel = get().modelId === capturedModelId
      const liveSystemUnchanged = sameModel && get().system === capturedSystem
      const scenarioResults = { ...get().scenarioResults }
      const scenarioStatus = { ...get().scenarioStatus }

      if (!sameModel) {
        // Model switched mid-solve — this result belongs to a model no
        // longer loaded. Abandon it silently: writing scenarioStatus/
        // staleCalcHistory/scenarioResults here would stomp the NEWLY
        // loaded model's state (e.g. `activeId` is the OLD model's active
        // scenario id, so it can collide with the new model's 'base').
        // Only clear our own transient flags if we still own the calc
        // lifecycle — mirrors the finally-block guard below; if the new
        // model's load already reset them, don't touch them again.
        if (calcAbortController === abortController) {
          set({ isCalculating: false, streamProgress: null })
        }
      } else if (!liveSystemUnchanged) {
        scenarioStatus[activeId] = 'stale'
        set({
          isCalculating: false,
          streamProgress: null,
          scenarioStatus,
          // Capped (MAX_STALE_CALC_HISTORY): each entry carries a full
          // AnalysisResponse — ~5MB for exactly the heavy solves this
          // feature targets — so an unbounded array across several
          // edits-during-heavy-solves in one session would leak memory.
          // Keep only the most recent few for debugging.
          staleCalcHistory: [
            ...get().staleCalcHistory,
            { supersededAt: Date.now(), output: calcOutput, response },
          ].slice(-MAX_STALE_CALC_HISTORY),
        })
      } else {
        if (hasUsableResults(calcOutput)) {
          scenarioResults[activeId] = { output: calcOutput, materializedSystem: resolved }
          scenarioStatus[activeId] = 'fresh'
        } else {
          delete scenarioResults[activeId]
          scenarioStatus[activeId] = 'stale'
        }

        // The Base-active install gate. Skipping it for a partial result would
        // leave the PREVIOUS run's persisted `calc` fields active, so the model
        // would display stale numbers alongside a Partial badge.
        const nextSystem = isBaseActive && hasUsableResults(calcOutput) ? materialized : get().system!

        set({
          system: nextSystem, scenarioResults, scenarioStatus,
          calculationResults: calcOutput, analysisResults: response, resultsStale: false, isCalculating: false,
          convergenceDoctorNeeded: calcOutput?.status === 'failed' || calcOutput?.status === 'invalid',
          autoSizeSuggestions, dismissedSuggestionIds: [], criticalPath, elementHealthMap, healthSummary,
          streamProgress: null,
        })
      }
    } catch (err) {
      outcome = 'failed'
      logger.error('Calculation failed', err instanceof Error ? err : undefined, { modelId: get().modelId, projectId: get().projectId })
      if (get().modelId !== capturedModelId) {
        // Model switched mid-solve; this failure belongs to a model no
        // longer loaded — don't clobber the new model's calc state. Only
        // reset our own transient flags if we still own the calc lifecycle.
        if (calcAbortController === abortController) {
          set({ isCalculating: false, streamProgress: null })
        }
      } else {
        const scenarioResults = { ...get().scenarioResults }; delete scenarioResults[activeId]
        const scenarioStatus = { ...get().scenarioStatus, [activeId]: 'stale' as const }
        set({ scenarioResults, scenarioStatus,
          calculationResults: { status: 'failed', edgeResults: [], vertexPressures: {},
            errors: [err instanceof Error ? err.message : 'Calculation failed unexpectedly'] },
          analysisResults: null, elementHealthMap: {}, healthSummary: null, resultsStale: false,
          isCalculating: false, convergenceDoctorNeeded: true, streamProgress: null })
      }
    } finally {
      ticker.stop()
      if (calcAbortController === abortController) calcAbortController = null
    }
    if (get().system) debouncedSave(get().system!, get().calculationResults, get().resultsStale)
    return { modelLoaded: true, outcome, durationMs: Date.now() - startedAt, pipeCount, vertexCount }
  },

  calculateAllScenarios: async (token?: string): Promise<void> => {
    const { system } = get()
    if (!system?.scenarios) return

    const { validateScenariosClosure, resolveScenario } = await import('@shared/hydraulic')
    const closureErrors = validateScenariosClosure(system as unknown as Parameters<typeof validateScenariosClosure>[0])
    if (closureErrors.length > 0) {
      set({ scenarioCalcProgress: { total: 0, done: 0, failed: ['Scenario tree is invalid — fix it before calculating.'] } })
      return
    }
    const { calculate: runCalc, validate: validateSystem } = await import('../../../services/calc-service')

    const activeId = system.scenarios.active_id
    // Resolve an auth token if the caller didn't pass one. The Compare view's
    // "Calculate all" button invokes this with no token, and runCalc()/calculate()
    // POST to an authenticated endpoint — without a token every scenario silently
    // fails to compute and the UI looks like nothing happened. Mirror the same
    // getAuthToken provider the manual Calculate / auto-calc paths use.
    const fetchToken = get().getAuthToken
    const authToken = token ?? (fetchToken ? (await fetchToken()) ?? undefined : undefined)
    // Compute every scenario that isn't already fresh (fresh = results are current).
    // ⚠️ 'fresh' means "status current", NOT "a cached result exists". Base is seeded 'fresh'
    // from the persisted document on load with an EMPTY `scenarioResults`, so testing status
    // alone left `todo` empty and made "Calculate all" silently do nothing after a reload.
    const todo = system.scenarios.nodes.filter(
      (n) => (get().scenarioStatus[n.id] ?? 'never') !== 'fresh' || !get().scenarioResults[n.id])
    const total = todo.length
    if (total === 0) { set({ scenarioCalcProgress: null }); return }

    set({ isCalculatingAll: true, scenarioCalcProgress: { total, done: 0, failed: [], failureDetails: [] } })
    const failed: string[] = []
    // ⚠ Parallel to `failed`, carrying WHY. The engine's message is often the only actionable
    // thing the user gets (it can name the offending device and the fix); dropping it leaves a
    // bare scenario name and no route to a diagnosis.
    const failureDetails: string[] = []
    let done = 0

    // try/finally so an unexpected throw (e.g. in the tail projection) can NEVER strand
    // isCalculatingAll=true and permanently disable the button (review H1).
    try {
      for (const node of todo) {
        if (node.id === activeId) {
          // Active scenario: delegate to calculate() so its criticalPath/health/auto-size stay correct.
          await get().calculate(authToken)
          if ((get().scenarioStatus[node.id] ?? 'never') !== 'fresh') failed.push(node.name)
        } else {
          // Non-active node (this is also the path for Base when Base isn't the active scenario:
          // resolveScenario(system,'base') returns the same system ref, which runCalc reads without
          // mutating — correct, no clone needed).
          try {
            const resolved = resolveScenario(get().system!, node.id)
            const errs = validateSystem(resolved)
            if (errs.length > 0) throw new Error(errs[0])
            const response = await runCalc(resolved, authToken)
            const out = response.calculationOutput ?? null
            const scenarioResults = { ...get().scenarioResults }
            const scenarioStatus = { ...get().scenarioStatus }
            if (hasUsableResults(out)) {
              scenarioResults[node.id] = { output: out, materializedSystem: resolved }
              scenarioStatus[node.id] = 'fresh'
            } else {
              delete scenarioResults[node.id]
              scenarioStatus[node.id] = 'stale'
              failed.push(node.name)
              // ⚠ The reason travels in `response.results` (analyzer annotations), NOT on
              // calculationOutput: HydraulicCalculationAnalyzer catches an engine throw and
              // re-emits it as a CALCULATION_FAILED annotation carrying the verbatim message.
              // This is the same source ResultsPanel renders for the ACTIVE scenario — the
              // non-active path simply never read it.
              const reason = Object.values(response.results ?? {})
                .flatMap((r) => r.annotations ?? [])
                .find((a) => a.severity === 'error')?.message
              failureDetails.push(`${node.name}: ${reason ?? 'calculation did not succeed'}`)
            }
            set({ scenarioResults, scenarioStatus })
          } catch (err) {
            logger.error('Scenario calc failed', err instanceof Error ? err : undefined, { scenarioId: node.id })
            const scenarioResults = { ...get().scenarioResults }; delete scenarioResults[node.id]
            set({ scenarioResults, scenarioStatus: { ...get().scenarioStatus, [node.id]: 'stale' as const } })
            failed.push(node.name)
            failureDetails.push(`${node.name}: ${err instanceof Error ? err.message : 'calculation failed'}`)
          }
        }
        done += 1
        set({ scenarioCalcProgress: { total, done, failed: [...failed], failureDetails: [...failureDetails] } })
      }

      // Re-project the active scenario's plain fields (idempotent if it was just computed via calculate()).
      const liveScenarios: Scenarios | undefined = get().system?.scenarios
      const projection = projectActiveScenario({
        scenarios: liveScenarios ?? system.scenarios,
        scenarioResults: get().scenarioResults,
        scenarioStatus: get().scenarioStatus,
        current: get().calculationResults,
      })
      set({ ...projection, scenarioCalcProgress: failed.length > 0 ? { total, done, failed } : null })
    } finally {
      set({ isCalculatingAll: false })
    }
  },

  fullAnalyze: async (token?: string): Promise<CalcEventResult> => {
    // `startedAt`/`pipeCount`/`vertexCount` are captured HERE, before any `await`,
    // and carried to every return below — never re-read from the store, because the
    // apply-guard means the store may by then hold a different Model entirely. Same
    // reasoning as `calculate()`; see CalcEventResult.
    const startedAt = Date.now()
    const { system } = get()
    // No Model loaded: nothing analyzed, no solve ran. `{modelLoaded: false}` is not
    // a failed analysis and must never be reported as one.
    if (!system) return { modelLoaded: false }
    const pipeCount = system.edges?.length ?? 0
    const vertexCount = system.vertices?.length ?? 0

    // ── Apply-guard (PIPE-125) ────────────────────────────────────────────────
    // Captured BEFORE the dynamic import below, not after: the FIRST runtime load
    // of calc-service is a network-fetched Vite chunk, and a Model switch during it
    // would otherwise let this invocation's validation-failure write land on the new
    // Model. (Once the chunk is evaluated the continuation is a microtask and no
    // user event can interleave — but the cold-load window is real.)
    //
    // BOTH halves are required:
    //  - `capturedSystem` catches a mid-analyze EDIT. Every edit action replaces
    //    `system` wholesale — the store never mutates it in place — so reference
    //    inequality is a correct O(1) "did the user edit during this analyze?".
    //  - `capturedModelId` catches a Model SWITCH. It is NOT sufficient alone: an
    //    imported Model and a blank unsaved Model both carry `modelId: null`, so two
    //    genuinely different Models compare equal on it.
    const capturedSystem = system
    const capturedModelId = get().modelId
    const capturedProjectId = get().projectId
    const stillOurs = (): boolean =>
      get().modelId === capturedModelId && get().system === capturedSystem

    const { analyzeSystem, validate: validateSystem } = await import('../../../services/calc-service')

    const errors = validateSystem(system)
    if (errors.length > 0) {
      // Model-specific fields are guarded; the flag write below is deliberately NOT.
      if (stillOurs()) {
        set({
          calculationResults: { status: 'invalid', edgeResults: [], vertexPressures: {}, errors },
          analysisResults: null,
          elementHealthMap: {},
          healthSummary: null,
          resultsStale: false,
          designReviewJobId: null,
          isDesignReviewPending: false,
          isAiDisclosurePending: false,
        })
      }
      // ⚠ Unconditional, exactly as it shipped. Flag OWNERSHIP is out of scope here
      // (#803, #804 — the `isSolveInFlight` work). Making this conditional would
      // introduce a STRANDING bug that does not exist today: an older invocation
      // that no longer owns the flag would decline to clear it, and `isAnalyzing`
      // stuck true disables the Analyze button for the rest of the session. Compare
      // `calculateAllScenarios`'s try/finally and its "can NEVER strand" note.
      set({ isAnalyzing: false })
      return { modelLoaded: true, outcome: 'invalid', durationMs: Date.now() - startedAt, pipeCount, vertexCount }
    }

    set({ isAnalyzing: true, isCalculating: true })
    // Design-review lifecycle fields are Model-specific — guarded. The two flags
    // above are not (see the note above).
    if (stillOurs()) {
      set({ designReviewJobId: null, isDesignReviewPending: false, isAiDisclosurePending: false })
    }

    // Assigned on every path below before the final `return` reads it.
    let outcome: CalculatedOutcome
    // Did THIS invocation actually commit Model-specific state? Set only at the two
    // commit sites below, never in advance. The save at the bottom is gated on it and
    // must never re-test the reference instead: `applyCalcResults` returns a NEW
    // system object which the commit installs, so a fresh
    // `get().system === capturedSystem` there would reject this invocation's own
    // legitimate save. It also has to mean *committed*, not *was eligible* — a caught
    // failure that writes a failed result on the same Model must persist that result,
    // exactly as the unconditional tail did before this guard existed.
    let committed = false

    try {
      const choice = get().aiDesignReviewChoice
      const response = await analyzeSystem(system, token, { runDesignReview: choice === 'always' })
      const calcOutput = response.calculationOutput ?? null
      // Read off the response, never off the store — this is what THIS invocation
      // computed, which stays correct analytics even when the guard declines to
      // commit it (the Model changed mid-analyze, not the analysis).
      outcome = calcOutput?.status ?? 'failed'

      // Cheap early bail so we don't do post-processing we already know is dead.
      // ⚠ NOT the authoritative decision — the `await import`s below each YIELD, and
      // another Model can be installed in that window (`openModel` commits inside an
      // async continuation, after its own awaits). An identity check taken before an
      // await is stale by the time the write lands; the real check is at the commit.
      if (stillOurs()) {
        // Derive from `capturedSystem`, not `get().system`, so the derivation can
        // never silently pick up a Model that arrived mid-flight — the commit below
        // decides whether any of it is applied.
        let updatedSystem = capturedSystem
        if (hasUsableResults(calcOutput)) {
          const { applyCalcResults } = await import('@shared/hydraulic')
          updatedSystem = applyCalcResults(updatedSystem, calcOutput)
        }

        const autoSizeSuggestions = calcOutput?.status === 'success'
          ? computeAutoSizeSuggestions(updatedSystem, calcOutput)
          : []

        let criticalPath: CriticalPathResult | null = null
        if (hasUsableResults(calcOutput)) {
          const { findCriticalPath } = await import('@shared/hydraulic')
          criticalPath = findCriticalPath(updatedSystem, calcOutput)
        }

        const elementHealthMap = computeElementHealthMap(response.results ?? {})
        const totalElements = (updatedSystem.vertices?.length ?? 0) + (updatedSystem.edges?.length ?? 0)
        const healthSummary = computeHealthSummary(response.results ?? {}, elementHealthMap, totalElements)

        // THE authoritative check. It must sit HERE, adjacent to the `set()`, with no
        // yielding boundary in between — that adjacency is the whole guarantee, so do
        // not hoist it above the derivation or introduce an `await` between the two.
        if (stillOurs()) {
          set({
            system: updatedSystem,
            calculationResults: calcOutput,
            analysisResults: response,
            resultsStale: false,
            autoSizeSuggestions,
            dismissedSuggestionIds: [],
            criticalPath,
            elementHealthMap,
            healthSummary,
            designReviewJobId: response.designReviewJobId ?? null,
            isDesignReviewPending: !!response.designReviewJobId,
            isAiDisclosurePending: choice === null,
          })
          committed = true
        }
      }
      // Declined → silent discard. Deliberately nothing is written: there is no UI
      // channel for "discarded Analyze" (the status pill and status bar read only
      // result-existence + `resultsStale`, the toast fires off a newly committed
      // `analyzedAt`), and unlike `calculate()` there is no scenario status to mark
      // stale or `staleCalcHistory` to stash into. Manufacturing an invalid/failed
      // state here would make the live Model lie about numbers it still holds.
      set({ isCalculating: false, isAnalyzing: false })
    } catch (err) {
      outcome = 'failed'
      // CAPTURED ids, not live ones — a late failure must not be attributed to
      // whichever Model happens to be open by the time it lands.
      logger.error(
        'Analysis failed',
        err instanceof Error ? err : undefined,
        { modelId: capturedModelId, projectId: capturedProjectId, vertexCount, edgeCount: pipeCount },
      )
      // Same adjacency rule as the success commit — check, then write, no await between.
      if (stillOurs()) {
        set({
          calculationResults: {
            status: 'failed',
            edgeResults: [],
            vertexPressures: {},
            errors: [err instanceof Error ? err.message : 'Analysis failed unexpectedly'],
          },
          analysisResults: null,
          elementHealthMap: {},
          healthSummary: null,
          resultsStale: false,
          designReviewJobId: null,
          isDesignReviewPending: false,
          isAiDisclosurePending: false,
        })
        // A failed result IS Model state — `runSave` maps it to the persisted
        // dashboard calcStatus. Not saving it here would silently drop a failure the
        // unconditional tail used to persist.
        committed = true
      }
      set({ isCalculating: false, isAnalyzing: false })
    }

    if (committed && get().system) debouncedSave(get().system!, get().calculationResults, get().resultsStale)
    return { modelLoaded: true, outcome, durationMs: Date.now() - startedAt, pipeCount, vertexCount }
  },

  mergeDesignReview: (jobId, result): void => {
    const state = get()
    // Staleness guard: a newer analyze superseded this job, or analysis was cleared.
    if (state.designReviewJobId !== jobId) return
    if (state.analysisResults == null) return

    const mergedResults = {
      ...state.analysisResults.results,
      'design-review': result,
    }
    const mergedAnalysis = { ...state.analysisResults, results: mergedResults }

    const elementHealthMap = computeElementHealthMap(mergedResults)
    const sys = state.system
    const totalElements = (sys?.vertices?.length ?? 0) + (sys?.edges?.length ?? 0)
    const healthSummary = computeHealthSummary(mergedResults, elementHealthMap, totalElements)

    set({
      analysisResults: mergedAnalysis,
      elementHealthMap,
      healthSummary,
      isDesignReviewPending: false,
    })
  },

  setAiDesignReviewChoice: (choice): void => {
    writeAiDesignReviewChoice(choice)
    set({ aiDesignReviewChoice: choice })
  },

  resolveAiDisclosure: async (decision): Promise<void> => {
    if (decision === 'always') {
      get().setAiDesignReviewChoice('always')
      set({ isAiDisclosurePending: false })
      const token = await get().getAuthToken?.()
      await get().fullAnalyze(token ?? undefined)   // re-run so the AI review actually runs
    } else if (decision === 'never') {
      get().setAiDesignReviewChoice('never')
      set({ isAiDisclosurePending: false })
    } else {
      set({ isAiDisclosurePending: false })          // 'not-now' — ask again next Analyze
    }
  },

  toggleAutoCalc: (): void => {
    set({ isAutoCalcEnabled: !get().isAutoCalcEnabled })
  },

  startAutoCalc: (): (() => void) => {
    // One subscription drives auto-recalc instead of threading a calculate()
    // call through every one of the ~18 mutation actions. It fires only when:
    //   - auto-calc is on and we're not already solving,
    //   - results already exist (RE-SOLVE ONLY — never auto-fires the very
    //     first solve; that stays on the explicit Calculate button), and
    //   - an edit both changed the model AND invalidated results.
    // Pure device moves keep resultsStale=false (see updateVertexWithRouting),
    // so dragging never recalculates. The debounce resets on each edit so the
    // solve runs once the user pauses. The token comes from the same auth
    // provider the manual Calculate button uses (getAuthToken).
    return useModelStore.subscribe((state, prev): void => {
      if (!state.isAutoCalcEnabled || state.isCalculating) return
      // ⚠️ No `!state.calculationResults` guard. It was a stand-in for "results already
      // exist" and reads a wrong `null` after a page reload, which is why auto-calc never
      // fired on a reloaded model (PIPE-66). `becameStale` below is the real test and is
      // strictly stronger: `markScenariosStale` only promotes a NON-'never' status, so a
      // scenario can only become stale if it had results to invalidate.
      const activeId = state.system?.scenarios?.active_id ?? 'base'
      const becameStale = state.scenarioStatus[activeId] === 'stale' && prev.scenarioStatus[activeId] !== 'stale'
      if (!becameStale) return

      scheduleAutoCalc()
    })
  },

  setResultsDisplayMode: (mode): void => {
    const state = get()

    if (mode === 'critical_path' && state.criticalPath) {
      // Extract PathResult subset for pressure profile
      const pathResult: PathResult = {
        vertexIds: state.criticalPath.vertexIds,
        edgeIds: state.criticalPath.edgeIds,
        totalDistance: state.criticalPath.totalDistance,
      }
      set({
        resultsDisplayMode: mode,
        pathTraceResult: pathResult,
        pathTraceSource: 'critical_path',
      })
    } else if (mode !== 'critical_path' && state.pathTraceSource === 'critical_path') {
      // Leaving critical_path mode — clear auto-set path trace
      set({
        resultsDisplayMode: mode,
        pathTraceResult: null,
        pathTraceSource: null,
      })
    } else {
      set({ resultsDisplayMode: mode })
    }
  },

  toggleSizingDialog: (): void => set((s): { showSizingDialog: boolean; } => ({ showSizingDialog: !s.showSizingDialog })),

  // -- Auto-sizing -----------------------------------------------

  autoSizeAllPipes: async (token?: string): Promise<{ pipesSized: number; iterations: number; compromises: number; unresolvable: number; status: "max_iterations" | "converged" | "oscillation" | "calc_failed"; }> => {
    // Read-only session: nothing to size, nothing pending — mirror the "no
    // system loaded" early-return below (status union has no dedicated
    // "skipped" member, so 'converged' is the closest honest fit: no changes
    // were made, none are outstanding).
    if (guardEdit()) return { pipesSized: 0, iterations: 0, compromises: 0, unresolvable: 0, status: 'converged' as const }
    const { system } = get()
    if (!system) return { pipesSized: 0, iterations: 0, compromises: 0, unresolvable: 0, status: 'converged' as const }

    // Default fallback matches VelocityAnalyzer's VELOCITY_HIGH threshold (3.0 m/s)
    const DEFAULT_MAX_VELOCITY_MS = 3.0

    const constraints = system.design_constraints
    const maxVelocityMs = constraints?.max_velocity
      ? readSI(constraints.max_velocity, 'velocity')
      : DEFAULT_MAX_VELOCITY_MS
    const minVelocityMs = 0 // future: constraints.min_velocity

    set({ isAutoSizing: true })

    try {
      // Step 1: Ensure we have fresh calc results
      if (!get().calculationResults || get().resultsStale) {
        await get().calculate(token)
        if (get().calculationResults?.status !== 'success') {
          set({ isAutoSizing: false })
          return { pipesSized: 0, iterations: 0, compromises: 0, unresolvable: 0, status: 'calc_failed' as const }
        }
      }

      // Snapshot for single undo entry — capture AFTER initial calc so undo restores a valid state
      const currentSystem = get().system!
      const undoSnapshot = {
        vertices: structuredClone(currentSystem.vertices),
        edges: structuredClone(currentSystem.edges),
        scenarios: currentSystem.scenarios !== undefined ? structuredClone(currentSystem.scenarios) : undefined,
        diagram_elements: structuredClone(currentSystem.diagram_elements ?? []),
      }

      const sizedPipeIds = new Set<string>()
      const compromisePipeIds = new Set<string>()
      const unresolvablePipeIds = new Set<string>()
      let previousChangeKey: string | null = null
      let finalStatus: 'converged' | 'max_iterations' | 'oscillation' | 'calc_failed' = 'max_iterations'
      let iteration = 0

      // Scenario accumulator threaded across iterations: under a non-Base scenario
      // each per-iteration resize becomes a coupled `pipe_spec` override here rather
      // than a Base edge mutation. Under Base, routeFieldEdit always returns kind:'base'
      // so this is never reassigned and edges are mutated exactly as before.
      let scenarios = currentSystem.scenarios

      for (iteration = 1; iteration <= 5; iteration++) {
        const currentSystem = get().system!
        const calcResults = get().calculationResults!
        const changes: { edgeId: string; sizeId: string; isCompromise: boolean }[] = []
        unresolvablePipeIds.clear()

        // Check each pipe
        for (const edge of currentSystem.edges) {
          if (edge.type !== 'pipe' && edge.type !== 'compressible_pipe') continue

          // edge is narrowed to PipeEdge | CompressiblePipeEdge by the type guard;
          // both expose pipe_specification_id / pipe_size_id directly.
          const pipeEdge = edge
          const edgeResult = calcResults.edgeResults.find((r): boolean => r.id === edge.id)
          if (!edgeResult) continue

          const density = resolveDensityForEdge(currentSystem, pipeEdge, WATER_DENSITY_KGM3)

          const spec = currentSystem.pipe_specifications.find(
            (ps): boolean => ps.id === pipeEdge.pipe_specification_id,
          )
          if (!spec) continue

          const result = suggestPipeSize(
            edgeResult.flow,
            density,
            spec,
            maxVelocityMs,
            minVelocityMs,
          )
          if (!result) continue
          if (result.sizeId === pipeEdge.pipe_size_id) {
            // Suggester returned the current size — either the pipe is fine, or
            // no available size can fix it. Distinguish using the actual velocity.
            if (result.velocity > maxVelocityMs || (minVelocityMs > 0 && result.velocity < minVelocityMs)) {
              unresolvablePipeIds.add(edge.id)
            }
            continue
          }

          changes.push({
            edgeId: edge.id,
            sizeId: result.sizeId,
            isCompromise: result.isCompromise,
          })
        }

        // Convergence: no changes needed
        if (changes.length === 0) {
          finalStatus = 'converged'
          break
        }

        // Oscillation detection: same change set as previous iteration
        const changeKey = changes
          .map((c): string => `${c.edgeId}:${c.sizeId}`)
          .sort()
          .join('|')
        if (changeKey === previousChangeKey) {
          finalStatus = 'oscillation'
          break
        }
        previousChangeKey = changeKey

        // Apply all changes (without individual undo entries). Route each resize
        // as a coupled `pipe_spec` field: under a non-Base scenario it accumulates
        // into the `scenarios` override tree; under Base it mutates the working
        // edges array via applyEdgeField (identical to the prior inline write).
        let updatedEdges = currentSystem.edges
        for (const change of changes) {
          const edge = currentSystem.edges.find((e): boolean => e.id === change.edgeId)
          if (!edge) continue
          const specId = (edge as unknown as { pipe_specification_id?: string }).pipe_specification_id
          if (specId === undefined) continue
          const fieldValue = { pipe_specification_id: specId, pipe_size_id: change.sizeId }
          const route = routeFieldEdit({ ...currentSystem, scenarios }, 'edge', change.edgeId, 'pipe_spec', fieldValue)
          if (route.kind === 'override') {
            scenarios = route.scenarios
          } else {
            updatedEdges = updatedEdges.map((e): Edge =>
              e.id === change.edgeId ? applyEdgeField(e, 'pipe_spec', fieldValue) : e,
            )
          }
        }

        set({
          system: { ...currentSystem, edges: updatedEdges, scenarios },
          isDirty: true,
        })

        changes.forEach(c => sizedPipeIds.add(c.edgeId))
        changes.forEach(c => {
          if (c.isCompromise) compromisePipeIds.add(c.edgeId)
          else compromisePipeIds.delete(c.edgeId)
        })

        // Re-run solver
        await get().calculate(token)
        if (get().calculationResults?.status !== 'success') {
          finalStatus = 'calc_failed'
          break
        }
      }

      // Push the pre-auto-size snapshot as a single undo entry
      const currentUndoStack = get().undoStack
      const undoEntry: HistoryEntry = {
        kind: 'graph',
        vertices: undoSnapshot.vertices,
        edges: undoSnapshot.edges,
        scenarios: undoSnapshot.scenarios,
        diagram_elements: undoSnapshot.diagram_elements,
      }
      const newUndoStack = [...currentUndoStack, undoEntry]
      if (newUndoStack.length > MAX_UNDO_DEPTH) newUndoStack.shift()

      set({
        undoStack: newUndoStack,
        redoStack: [],
        canUndo: true,
        canRedo: false,
        isAutoSizing: false,
      })

      // Trigger save after auto-sizing (matches existing mutation pattern)
      // NOTE: debouncedSave is a module-scope function, NOT a store property — call directly
      debouncedSave(get().system!, get().calculationResults, get().resultsStale)

      return {
        pipesSized: sizedPipeIds.size,
        iterations: iteration,
        compromises: compromisePipeIds.size,
        unresolvable: unresolvablePipeIds.size,
        status: finalStatus,
      }
    } catch {
      set({ isAutoSizing: false })
      return { pipesSized: 0, iterations: 0, compromises: 0, unresolvable: 0, status: 'calc_failed' as const }
    }
  },

  acceptAutoSizeSuggestion: (edgeId: string): void => {
    const { system, autoSizeSuggestions } = get()
    if (!system) return

    const suggestion = autoSizeSuggestions.find((s): boolean => s.edgeId === edgeId)
    if (!suggestion) return

    // Route the size write as a coupled `pipe_spec` field through the leaf
    // action: under a non-Base scenario it becomes an override; under Base it
    // mutates the edge directly. updateEdgeProperty owns history, routing,
    // staleness, calc-clearing, and save — so we no longer hand-roll those here.
    const edge = system.edges.find((e): boolean => e.id === edgeId)
    if (!edge) return
    const specId = (edge as unknown as { pipe_specification_id?: string }).pipe_specification_id
    if (specId === undefined) return

    get().updateEdgeProperty(edgeId, 'pipe_spec', {
      pipe_specification_id: specId,
      pipe_size_id: suggestion.suggestedSizeId,
    })

    // Extra behavior updateEdgeProperty does NOT cover: drop the accepted
    // suggestion from the active list (kept as a follow-up set so it survives
    // the leaf action's own set()).
    set({
      autoSizeSuggestions: get().autoSizeSuggestions.filter((s): boolean => s.edgeId !== edgeId),
    })
  },

  dismissAutoSizeSuggestion: (edgeId: string): void => {
    set((s): { dismissedSuggestionIds: Array<string>; } => ({
      dismissedSuggestionIds: [...s.dismissedSuggestionIds, edgeId],
    }))
  },

  // -- New System -----------------------------------------------

  createBlankSystem: (title, fluid, pipeSpec, basis = 'orthogonal'): void => {
    if (saveTimeout) clearTimeout(saveTimeout)
    // Navigate-away (spec §3.4): cancel any in-flight streamed calc.
    calcAbortController?.abort()
    calcAbortController = null
    const { getAuthToken } = get()
    const system = {
      schema_version: 2 as const,
      calculation_settings: {
        max_iterations: 100,
        linear_switch_point: 0.001,
        laminar_cutoff_Reynolds: 2300,
      },
      fluid_zones: [{
        id: 'fz-1',
        name: fluid.name,
        fluid_name: fluid.name,
        temperature: { value: convertValue(fluid.temperature, 'degF', 'K'), unit: 'K' },
        density: { value: convertValue(fluid.density, 'lb/ft3', 'kg/m3'), unit: 'kg/m3' },
        viscosity: { value: convertValue(fluid.viscosity, 'cP', 'Pa·s'), unit: 'Pa·s' },
        vapor_pressure: { value: convertValue(fluid.vaporPressure, 'psia', 'Pa'), unit: 'Pa' },
      }],
      pipe_specifications: [{
        id: 'ps-1',
        name: `${pipeSpec.material} ${pipeSpec.schedule}`,
        material: pipeSpec.material,
        metric: false,
        roughness: { value: convertValue(pipeSpec.roughness, 'in', 'm'), unit: 'm' },
        schedule: pipeSpec.schedule,
        standard: 'ASME B36.10M',
        standard_version: '2015',
        sizes: [{
          id: 'size-default',
          name: pipeSpec.nominalSize,
          inner_diameter: { value: convertValue(pipeSpec.innerDiameter, 'in', 'm'), unit: 'm' },
        }],
      }],
      vertices: [],
      edges: [],
      // Basis chosen at creation; an isometric model also gets its lattice here,
      // or it silently draws a square grid over an isometric document.
      document_header: newDocumentHeader(title, basis, new Date().toISOString()),
    }

    set({
      // structuredClone returns a structurally-equivalent shape but TS can't
      // verify the union members survive the clone; narrow through `unknown`.
      system: ensureScenarios(structuredClone(system) as unknown as HydraulicSystem),
      selectedIds: [],
      undoStack: [],
      redoStack: [],
      canUndo: false,
      canRedo: false,
      isDirty: false,
      lastSavedAt: null,
      projectId: null,
      modelId: null,
    modelName: null,
    capabilities: null,
    importJobId: null,
    sourceType: null,
      serverVersion: null,
      headSeq: null,
      getAuthToken, // preserve auth provider across resets
      interactionMode: 'select',
      pendingLibrarySnapshot: null,
      schematicSnapshot: null,
      viewMode: '2d',
      addVertexType: null,
      addPipeSource: null,
      calculationResults: null,
      isCalculating: false,
      isAutoCalcEnabled: false,
      resultsStale: false,
      streamProgress: null,
      staleCalcHistory: [],
      scenarioResults: {},
      scenarioStatus: {},
      isCalculatingAll: false,
      scenarioCalcProgress: null,
      clipboard: null,
      pasteCounter: 0,
    })
    // NOTE: No debouncedSave — model not on server yet.
    // The ProjectSelectorDialog flow will POST to create the model.
  },

  // -- FlowAssist actions ----------------------------------------

  addUserMessage: (content): void =>
    set((state): { flowAssist: { messages: Array<ChatMessage>; isStreaming: boolean; sessionTokens: { input: number; output: number; }; sessionId: string | null; pendingMessage?: string; }; } => ({
      flowAssist: {
        ...state.flowAssist,
        messages: [
          ...state.flowAssist.messages,
          { role: 'user' as const, content, timestamp: Date.now() },
        ],
      },
    })),

  appendAssistantChunk: (content): void =>
    set((state): { flowAssist: { messages: Array<ChatMessage>; isStreaming: boolean; sessionTokens: { input: number; output: number; }; sessionId: string | null; pendingMessage?: string; }; } => {
      const msgs = [...state.flowAssist.messages]
      const last = msgs[msgs.length - 1]
      if (last?.role === 'assistant') {
        msgs[msgs.length - 1] = { ...last, content: last.content + content }
      } else {
        msgs.push({ role: 'assistant', content, timestamp: Date.now() })
      }
      return { flowAssist: { ...state.flowAssist, messages: msgs } }
    }),

  finalizeAssistantMessage: (elementRefs): void =>
    set((state): ModelStore | { flowAssist: { messages: Array<ChatMessage>; isStreaming: boolean; sessionTokens: { input: number; output: number; }; sessionId: string | null; pendingMessage?: string; }; } => {
      if (!elementRefs || elementRefs.length === 0) return state
      const msgs = [...state.flowAssist.messages]
      const last = msgs[msgs.length - 1]
      if (last?.role === 'assistant') {
        msgs[msgs.length - 1] = { ...last, elementRefs }
      }
      return { flowAssist: { ...state.flowAssist, messages: msgs } }
    }),

  setFlowAssistStreaming: (isStreaming): void =>
    set((state): { flowAssist: { isStreaming: boolean; messages: ChatMessage[]; sessionTokens: { input: number; output: number; }; sessionId: string | null; pendingMessage?: string; }; } => ({
      flowAssist: { ...state.flowAssist, isStreaming },
    })),

  clearFlowAssistChat: (): void =>
    set((): { flowAssist: { messages: Array<never>; isStreaming: false; sessionTokens: { input: number; output: number; }; sessionId: null; }; } => ({
      flowAssist: {
        messages: [],
        isStreaming: false,
        sessionTokens: { input: 0, output: 0 },
        sessionId: null,
      },
    })),

  setFlowAssistSessionId: (sessionId): void =>
    set((state): { flowAssist: { sessionId: string | null; messages: ChatMessage[]; isStreaming: boolean; sessionTokens: { input: number; output: number; }; pendingMessage?: string; }; } => ({
      flowAssist: { ...state.flowAssist, sessionId },
    })),

  addFlowAssistTokens: (input, output): void =>
    set((state): { flowAssist: { sessionTokens: { input: number; output: number; }; messages: ChatMessage[]; isStreaming: boolean; sessionId: string | null; pendingMessage?: string; }; } => ({
      flowAssist: {
        ...state.flowAssist,
        sessionTokens: {
          input: state.flowAssist.sessionTokens.input + input,
          output: state.flowAssist.sessionTokens.output + output,
        },
      },
    })),


  // -- FlowAssist Palette (Phase 2) --------------------------------

  toggleFlowAssistPalette: (): void =>
    set((state): { showFlowAssistPalette: boolean; } => ({ showFlowAssistPalette: !state.showFlowAssistPalette })),

  setShowFindDevice: (open: boolean): void => set({ showFindDevice: open }),

  // -- FlowAssist Smart Suggest (Phase 3) --------------------------------

  trackAcceptedSuggestion: (deviceId, fieldKey, suggestedValue): void =>
    set((state): { pendingSuggestions: { [x: string]: { suggestedValue: unknown; timestamp: number; } | { suggestedValue: unknown; timestamp: number; }; }; } => ({
      pendingSuggestions: {
        ...state.pendingSuggestions,
        [`${deviceId}_${fieldKey}`]: { suggestedValue, timestamp: Date.now() },
      },
    })),

  clearPendingSuggestions: (): void =>
    set((): { pendingSuggestions: {}; } => ({ pendingSuggestions: {} })),

  executeProposedAction: (action): void => {
    const { payload } = action as { payload: { storeAction: string; args: unknown[] } }
    if (!payload || !payload.storeAction) return

    const ALLOWED_STORE_ACTIONS = new Set([
      'updateVertexProperty', 'updateEdgeProperty',
      'bulkUpdateVertexProperty', 'bulkUpdateEdgeProperty',
      'addVertex', 'addEdge',
      'updateDesignConstraints', 'updateFluidZone',
      'removeVertex', 'removeEdge',
      'relayoutToGrid', 'setEdgePins',
    ])

    if (!ALLOWED_STORE_ACTIONS.has(payload.storeAction)) {
      logger.warn('FlowAssist blocked unrecognized store action', { storeAction: payload.storeAction })
      return
    }

    const store = useModelStore.getState()

    // Helper: resolve dot-notation paths into a top-level key + merged nested object.
    // The store's updateVertexProperty/updateEdgeProperty uses { ...v, [key]: value },
    // which creates a literal key like 'elevation.value' instead of traversing the
    // nested object. This helper resolves that by splitting the path and merging.
    const UNSAFE_PATH_SEGMENTS = new Set(['__proto__', 'constructor', 'prototype'])

    const resolveProperty = (
      entityId: string,
      path: string,
      newValue: unknown,
      entities: Array<{ id: string } & Record<string, unknown>>,
    ): [string, unknown] => {
      if (path.split('.').some(s => UNSAFE_PATH_SEGMENTS.has(s))) {
        logger.warn('FlowAssist blocked unsafe property path', { path })
        return [path, undefined]
      }
      // diagram/layout state is owned by pin_pipe_ports / relayoutToGrid /
      // canvas editing; raw property writes would bypass PortSide validation
      // and re-routing. Mirrors the backend isBlockedLayoutPath guard.
      if (path === 'diagram' || path.startsWith('diagram.')) {
        logger.warn('FlowAssist blocked diagram property path', { path })
        return [path, undefined]
      }
      if (!path.includes('.')) return [path, newValue]

      const parts = path.split('.')
      const topKey = parts[0]!
      const entity = entities.find(e => e.id === entityId)
      const currentTopValue = entity ? (entity as Record<string, unknown>)[topKey] : {}

      // Build the merged nested object
      let result = { ...(currentTopValue as Record<string, unknown> ?? {}) }
      let cursor: Record<string, unknown> = result
      for (let i = 1; i < parts.length - 1; i++) {
        const key = parts[i]!
        const nested = { ...(cursor[key] as Record<string, unknown> ?? {}) }
        cursor[key] = nested
        cursor = nested
      }
      cursor[parts[parts.length - 1]!] = newValue

      return [topKey, result]
    }

    // Vertex / Edge are discriminated unions; resolveProperty just needs `id`
    // plus open-ended field access for the dot-notation path resolver. Narrow
    // through `unknown` once at the call sites rather than `any`.
    type ResolvableArray = Array<{ id: string } & Record<string, unknown>>
    const verticesForResolve = store.system!.vertices as unknown as ResolvableArray
    const edgesForResolve = store.system!.edges as unknown as ResolvableArray

    switch (payload.storeAction) {
      case 'updateVertexProperty': {
        const [id, path, value] = payload.args as [string, string, unknown]
        const [key, resolved] = resolveProperty(id, path, value, verticesForResolve)
        if (resolved !== undefined) {
          store.updateVertexProperty(id, key, resolved)
        } else {
          // Blocked path (already warned in resolveProperty) OR a payload-builder
          // key mismatch sending undefined (BUG-002 class) — never write undefined.
          logger.warn('FlowAssist property update skipped: undefined resolved value', { id, path })
        }
        break
      }
      case 'updateEdgeProperty': {
        const [id, path, value] = payload.args as [string, string, unknown]
        const [key, resolved] = resolveProperty(id, path, value, edgesForResolve)
        if (resolved !== undefined) {
          store.updateEdgeProperty(id, key, resolved)
        } else {
          // Blocked path (already warned in resolveProperty) OR a payload-builder
          // key mismatch sending undefined (BUG-002 class) — never write undefined.
          logger.warn('FlowAssist property update skipped: undefined resolved value', { id, path })
        }
        break
      }
      case 'bulkUpdateVertexProperty': {
        const [ids, path, value] = payload.args as [string[], string, unknown]
        for (const id of ids) {
          const [key, resolved] = resolveProperty(id, path, value, verticesForResolve)
          if (resolved !== undefined) {
            store.updateVertexProperty(id, key, resolved)
          } else {
            // Blocked path (already warned in resolveProperty) OR a payload-builder
            // key mismatch sending undefined (BUG-002 class) — never write undefined.
            logger.warn('FlowAssist property update skipped: undefined resolved value', { id, path })
          }
        }
        break
      }
      case 'bulkUpdateEdgeProperty': {
        const [ids, path, value] = payload.args as [string[], string, unknown]
        for (const id of ids) {
          const [key, resolved] = resolveProperty(id, path, value, edgesForResolve)
          if (resolved !== undefined) {
            store.updateEdgeProperty(id, key, resolved)
          } else {
            // Blocked path (already warned in resolveProperty) OR a payload-builder
            // key mismatch sending undefined (BUG-002 class) — never write undefined.
            logger.warn('FlowAssist property update skipped: undefined resolved value', { id, path })
          }
        }
        break
      }
      case 'addVertex': {
        const [params] = payload.args as [{ type: string; name: string; x: number; y: number }]
        const id = nextVertexId(store.system!.vertices)
        // Snap to the canvas's 20-unit grid so AI-placed devices align with
        // hand-placed ones. Mirrors the click-to-drop snap in SchematicCanvas.
        const snapped = snapCenterToGrid({ x: params.x, y: params.y }, params.type)
        const vertex = createVertexAtPosition(id, params.type, snapped)
        vertex.name = params.name
        store.addVertex(vertex)
        break
      }
      case 'addEdge': {
        const [params] = payload.args as [{ source_id: string; target_id: string; name?: string }]
        const edgeId = nextEdgeId(store.system!.edges)
        const edge: Edge = {
          id: edgeId,
          type: 'pipe',
          name: params.name ?? edgeId,
          connection: {
            source_id: params.source_id,
            target_id: params.target_id,
          },
          pipe_specification_id: store.system!.pipe_specifications[0]?.id ?? '',
          pipe_size_id: store.system!.pipe_specifications[0]?.sizes[0]?.id ?? '',
          fluid_zone_id: store.system!.fluid_zones[0]?.id ?? '',
          length: { value: 10, unit: 'm' },
          fittings: [],
          is_closed: false,
          is_oneway: false,
          diagram: { points: [] },
        } as Edge
        store.addEdge(edge)
        break
      }
      case 'updateDesignConstraints': {
        const [constraints] = payload.args as [Record<string, unknown>]
        store.updateDesignConstraints(constraints)
        break
      }
      case 'updateFluidZone': {
        const [zoneId, updates] = payload.args as [string, Record<string, unknown>]
        // ⚠️ THROWN, not logged. `updateFluidZone` returns void and declines a
        // zone whose units cannot be converted to SI — which is right, but
        // invisible from here, so FlowAssist reported "Applied" over a write that
        // never happened. `useFlowAssist.ts:282` already wraps this call and
        // renders "Failed to apply: <message>", so raising is all that is needed.
        //
        // Checked HERE rather than by enumerating units in the tool schema
        // (write-tools.ts): a list of accepted units in the schema would be a
        // second source of truth that has to agree with the converters forever,
        // and two lists that must agree is the bug this whole change removes.
        const zoneBefore = get().system?.fluid_zones.find((z): boolean => z.id === zoneId)
        if (zoneBefore) {
          const probe = normalizeSystemFluidZonesToSI({ fluid_zones: [{ ...zoneBefore, ...updates }] })
          if (probe.errors.length > 0) {
            throw new Error(`Cannot update fluid zone ${zoneId}: ${probe.errors.join('; ')}`)
          }
        }
        store.updateFluidZone(zoneId, updates)
        break
      }
      case 'removeVertex': {
        const [deviceId] = payload.args as [string]
        store.removeVertex(deviceId)
        break
      }
      case 'removeEdge': {
        const [pipeId] = payload.args as [string]
        store.removeEdge(pipeId)
        break
      }
      case 'relayoutToGrid': {
        // Mirrors the Cmd/Ctrl+Shift+G keyboard shortcut: snap each target
        // vertex's center to the 20-unit grid and re-route every connected edge
        // under the type-aware orthogonal router — as ONE batched mutation.
        // (Empty ids = the whole model; relayoutToGrid handles that itself.)
        const [ids] = payload.args as [string[]]
        store.relayoutToGrid(Array.isArray(ids) ? ids : [])
        break
      }
      case 'setEdgePins': {
        const [targets] = payload.args as [Array<{ edgeId: string; end: 'source' | 'target'; side: PortSide | null }>]
        // Runtime shape validation: this is an AI-writable surface and a
        // malformed side must never be persisted into the document. Reject the
        // WHOLE action (no partial apply, no silent fallback).
        const VALID_SIDES = new Set(['top', 'right', 'bottom', 'left'])
        const ok = Array.isArray(targets) && targets.every(t =>
          typeof t?.edgeId === 'string' &&
          (t.end === 'source' || t.end === 'target') &&
          (t.side === null || VALID_SIDES.has(t.side as string)))
        if (!ok) {
          logger.warn('FlowAssist setEdgePins: malformed targets, ignoring', { targets })
          break
        }
        store.setEdgePins(targets)
        break
      }
      default:
        logger.warn('FlowAssist unknown store action', { storeAction: payload.storeAction })
    }
  },

  // -- Settings dialog ------------------------------------------

  openSettings: (tab): void => {
    set({ settingsOpen: true, settingsTab: tab ?? 'general' })
  },
  closeSettings: (): void => {
    set({ settingsOpen: false })
  },

  // -- Save-to-Library dialog -----------------------------------

  openSaveToLibrary: (vertexId): void => {
    if (vertexId && !get().selectedIds.includes(vertexId)) {
      set({ selectedIds: [vertexId] })
    }
    set({ saveToLibraryOpen: true })
  },
  closeSaveToLibrary: (): void => {
    set({ saveToLibraryOpen: false })
  },
  openSymbolPicker: (vertexId): void => {
    set({ symbolPickerVertexId: vertexId })
  },
  closeSymbolPicker: (): void => {
    set({ symbolPickerVertexId: null })
  },

  // -- Element focus --------------------------------------------

  selectAndFocusElement: (elementId: string, opts?: { zoom?: boolean }): void => {
    set({ selectedIds: [elementId] })
    window.dispatchEvent(
      new CustomEvent('pan-to-element', { detail: { elementId, zoom: opts?.zoom ?? false } }),
    )
  },

  selectAndFocusGroup: (groupId: string): void => {
    const group = get().system?.groups?.find((g) => g.id === groupId)
    if (!group) return
    set({ selectedIds: [...group.member_ids] })
    window.dispatchEvent(
      new CustomEvent('fit-to-group', { detail: { memberIds: group.member_ids } }),
    )
  },

  setHoveredId: (id: string | null): void => {
    // Only allow hover during select mode — prevents re-render cascades
    // during add-pipe/add-device/delete modes where rapid state changes occur
    if (id !== null && get().interactionMode !== 'select') return
    set({ hoveredId: id })
  },

  bumpLibraryRefresh: (): void => {
    set((s) => ({ libraryRefreshToken: s.libraryRefreshToken + 1 }))
  },

  // -- Scenario management --------------------------------------

  setActiveScenario: (scenarioId): void => {
    const { system } = get()
    if (!system?.scenarios) return
    if (system.scenarios.active_id === scenarioId) return // no-op: same reference preserved
    if (!system.scenarios.nodes.some((n) => n.id === scenarioId)) {
      throw new Error(`setActiveScenario: scenario '${scenarioId}' does not exist`)
    }
    const nextScenarios: Scenarios = { ...system.scenarios, active_id: scenarioId }
    const updatedSystem: HydraulicSystem = {
      ...system,
      scenarios: nextScenarios,
    }
    const projection = projectActiveScenario({
      scenarios: nextScenarios,
      scenarioResults: get().scenarioResults,
      scenarioStatus: get().scenarioStatus,
      current: get().calculationResults,
    })
    set({ system: updatedSystem, isDirty: true, ...projection, criticalPath: null })
    debouncedSave(updatedSystem, get().calculationResults, get().resultsStale)
    // #3: auto-recalc the now-active scenario when it is stale and auto-calc is
    // on. Fresh => projection already shows cached results; never => first solve
    // stays manual (consistent with startAutoCalc).
    if (get().isAutoCalcEnabled && get().scenarioStatus[scenarioId] === 'stale') {
      scheduleAutoCalc()
    }
  },

  branchScenarioAction: (parentId, name): string => {
    if (guardEdit()) return get().system?.scenarios?.active_id ?? 'base'
    const { system } = get()
    if (!system?.scenarios) throw new Error('branchScenarioAction: system has no scenario tree')
    const newId = nextScenarioId()
    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const scenarios = branchScenario(system.scenarios, parentId, name, newId)
    const updatedSystem = { ...system, scenarios }
    set({ system: updatedSystem, undoStack, redoStack: [], canUndo: true, canRedo: false, isDirty: true })
    debouncedSave(updatedSystem, get().calculationResults, get().resultsStale)
    return newId
  },

  renameScenarioAction: (scenarioId, name): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system?.scenarios) return
    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const scenarios = { ...system.scenarios, nodes: system.scenarios.nodes.map(
      (n) => (n.id === scenarioId ? { ...n, name } : n)) }
    const updatedSystem = { ...system, scenarios }
    set({ system: updatedSystem, undoStack, redoStack: [], canUndo: true, canRedo: false, isDirty: true })
    debouncedSave(updatedSystem, get().calculationResults, get().resultsStale)
  },

  resetOverrideAction: (scenarioId, _target, id, field): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system?.scenarios) return
    // No-op guard (mirror clearScenarioOverrides): if this node doesn't pin (id,field), do nothing —
    // don't push history / flip isDirty / schedule a save for a removeOverride that changes nothing.
    const node = system.scenarios.nodes.find((n) => n.id === scenarioId)
    if (!node || !node.overrides.some((o) => o.id === id && o.field === field)) return
    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const scenarios = removeOverride(system.scenarios, scenarioId, id, field)
    let updatedSystem: HydraulicSystem = { ...system, scenarios }
    updatedSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)
    // Mark the EDITED node's subtree stale (scenarioId), NOT active_id — mirrors clearScenarioOverrides (review C1).
    const { status: scenarioStatus, invalidatedIds } = markScenariosStale(scenarios, get().scenarioStatus, scenarioId)
    const scenarioResults = { ...get().scenarioResults }
    for (const invId of invalidatedIds) delete scenarioResults[invId]
    const projection = projectActiveScenario({ scenarios, scenarioResults, scenarioStatus, current: get().calculationResults })
    set({ system: updatedSystem, undoStack, redoStack: [], canUndo: true, canRedo: false, isDirty: true,
      scenarioResults, scenarioStatus, ...projection,
      resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, projection.resultsStale),
      criticalPath: projection.resultsStale ? null : get().criticalPath })
    debouncedSave(updatedSystem, projection.calculationResults, projection.resultsStale)
  },

  deleteScenarioAction: (scenarioId): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system?.scenarios) return
    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const scenarios = deleteScenarioWithBake(system.scenarios, scenarioId) // throws on root
    const updatedSystem = { ...system, scenarios }
    set({ system: updatedSystem, undoStack, redoStack: [], canUndo: true, canRedo: false, isDirty: true })
    debouncedSave(updatedSystem, get().calculationResults, get().resultsStale)
  },


  clearScenarioOverrides: (scenarioId): void => {
    if (guardEdit()) return
    const { system } = get()
    if (!system?.scenarios) return
    const node = system.scenarios.nodes.find((n) => n.id === scenarioId)
    if (!node || node.overrides.length === 0) return
    const undoStack = pushHistory(get().undoStack, system.vertices, system.edges, system.scenarios, system.diagram_elements)
    const scenarios = { ...system.scenarios, nodes: system.scenarios.nodes.map(
      (n) => (n.id === scenarioId ? { ...n, overrides: [] } : n)) }
    let updatedSystem: HydraulicSystem = { ...system, scenarios }
    updatedSystem = clearCalcResultsIfSolverInputsChanged(system, updatedSystem)
    // CRITICAL (review C1): mark the EDITED node's subtree stale (scenarioId), NOT the active scenario.
    // `staleAfterEdit` keys staleness off `scenarios.active_id`, so it is WRONG here (the manager's
    // reset button fires on any row, often not the active one). Use `markScenariosStale` with the
    // explicit edited id, then project the plain fields from whatever is currently active.
    const { status: scenarioStatus, invalidatedIds } = markScenariosStale(scenarios, get().scenarioStatus, scenarioId)
    const scenarioResults = { ...get().scenarioResults }
    for (const id of invalidatedIds) delete scenarioResults[id]
    const projection = projectActiveScenario({ scenarios, scenarioResults, scenarioStatus, current: get().calculationResults })
    set({ system: updatedSystem, undoStack, redoStack: [], canUndo: true, canRedo: false, isDirty: true,
      scenarioResults, scenarioStatus, ...projection,
      resultsDisplayMode: nextDisplayMode(get().resultsDisplayMode, projection.resultsStale),
      criticalPath: projection.resultsStale ? null : get().criticalPath })
    debouncedSave(updatedSystem, projection.calculationResults, projection.resultsStale)
  },
}))
